import { computed, inject, Injectable, Injector, NgZone, runInInjectionContext, Signal, signal, WritableSignal } from '@angular/core';
import { IUser } from '../_models/user.model';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Global } from '../_constants/global.variables';
import { BehaviorSubject, ReplaySubject, catchError, debounceTime, distinctUntilChanged, filter, map, Observable, of, startWith, Subject, Subscription, shareReplay, throwError } from 'rxjs';
import { SignalRCoreService } from './signalr-core.service';
import { ISite } from '../_models/site.model';
import { IAsset } from '../_models/asset.model';
import { IObservation } from '../_models/observation.model';
import _, { result } from 'lodash';
import { UtilityService } from './utility.service';
import { ITag } from '../_models/tag.model';
import { ITagNamePrefixSubject } from '../_models/tag-name-prefix-subject.model';
import { IStandardObservation } from '../_models/standardObservation.model';
import { ISQLServerSubject } from '../_models/sql-server-subject.model';
import { IncomingDataService } from './incoming-data.service';
import { IAssetState } from '../_models/asset-state.model';
import { TagCacheService } from './tag-cache.service';
import { ISystem } from "../_models/system.model";
import { ICamera } from "../_models/camera.model";
import { or } from 'node_modules/@progress/kendo-angular-grid/utils';
import { ModalController } from '@ionic/angular/standalone';
import { ITagHistorical } from "../_models/tag-historical.model";


export function isUnitTestRunner(): boolean {
  return typeof window !== 'undefined' && Boolean((window as any).__karma__ || (window as any).jasmine);
}

export function isTestHost(): boolean {
  const h = window.location.hostname.toLowerCase();
  const href = window.location.href.toLowerCase();
  return (
    h.includes('localhost') ||
    h.includes('127.0.0.1') ||
    href.includes('test')
  );
}

function createEmptyDataCache(): any {
    return {
        assets: [],
        assetsObject: {},
        gates: [],
        organizations: [],
        organizationsObject: {},
        sites: [],
        sitesObject: {},
        systems: [],
        systemsObject: {},
        cameras: [],
        jbtStandardObservations: [],
        observations: [],
        tags: [],
        tagsObject: {},
        emailTagUsers: [],
        userIssues: [],
    };
}

//create an interface for the Asset object using the Tag interface below
export interface Asset {
  id: number;
  name: string;
  description: string;
  customer: string;
  imageUrl: string;
  assetType: string;
  commodity: string;
  status: string;
  issues: number;
  route: number;
  mileage: number;
  tags: WritableSignal<Tag[]>;
  maintenanceInstances: any[];
}

export interface Tag {
  stdId: number;
  name: string;
  // dateAsSignal: WritableSignal<Date | string>;
  // valueAsSignal: WritableSignal<number | string>;
  date: Date | string;
  value: number | string;
  valueWhenActive: string;
  severity: string;
  recentlyUpdated: boolean;
}


@Injectable({
    providedIn: "root",
})
export class DataService {
    public savedSearches: any = [
        {
            name: "Active Assets",
            queryParams: {
                listBy: "asset",
                active: "true",
            },
            icon: "car",
        },
        {
            name: "Active Gates",
            queryParams: {
                listBy: "gate",
                active: "true",
            },
            icon: "car",
        },
        {
            name: "PBB Critical Alarms and Alarms",
            queryParams: {
                listBy: "asset",
                active: "true",
                alarm: "true",
                cAlarm: "true",
                atId: "3",
                atgId: "1",
            },
            icon: "car",
        },
    ];
    public readyForUse: boolean = false;
    private readonly sparklineCacheTtlMs = 2 * 60 * 1000;
    private readonly sparklineCacheMax = 200;
    private readonly sparklineCache = new Map<string, { expires: number; obs$: Observable<any> }>();
    private readonly pbbHistoricalCacheMax = 25;
    private readonly pbbHistoricalCache = new Map<string, { loadedAtMs: number; rows: Array<{ TagId?: number; TagValues?: string }> }>();
    private modalCtrl = inject(ModalController);
    //psuedo coding for new preferrred insidhgts.
    //1 or 0 match value color of number
    //need rule for when value matches value when active
    //need a rule for when value doesn't match value when active

    //  preferredInsightTags: Array<TagInsight> = [
    //       {
    //            identifier: "JBTStandardObservationId",
    //            id: 4504,
    //            assetTypeIds: [86294],
    //            display: {
    //                 allignment: "center",
    //                 size: "12", //using grid system until I figure out a more user friendly solution
    //            },

    //            rules: [
    //                 {
    //                      operator: "=",
    //                      value: "valueWhenActive",
    //                      display: {
    //                           type: "text",
    //                           color: "green",
    //                      },
    //                 },
    //                 {
    //                      operator: "!=",
    //                      value: "valueWhenActive",
    //                      display: {
    //                           type: "text",
    //                           color: "grey",
    //                      },
    //                 },
    //            ],
    //       },
    //       {
    //            identifier: "JBTStandardObservationId",
    //            id: 12324,
    //            assetTypeIds: [86302, 1, 3, 2],
    //            display: {
    //                 allignment: "center",
    //                 size: "12", //using grid system until I figure out a more user friendly solution
    //            },

    //            rules: [
    //                 {
    //                      operator: "=",
    //                      value: "valueWhenActive",
    //                      display: {
    //                           type: "text",
    //                           color: "green",
    //                      },
    //                 },
    //                 {
    //                      operator: "!=",
    //                      value: "valueWhenActive",
    //                      display: {
    //                           type: "text",
    //                           color: "grey",
    //                      },
    //                 },
    //            ],
    //       },
    //  ];

    public potdUrl: string = "";
    imageKeyURL: string = "";
    genericDataUrl: string = "";
    multiDataAPIUrl: string = "";
    downloadFileUrl: string = "";
    requestUserAccountURL: string = "";
    requestToResetPasswordURL: string = "";
    changePasswordUrl: string = "";
    cacheImageKeyFilesUrl: string = "";
    fileUploadUrl: string = "";
    fileUploadListUrl: string = "";
    questionControlDataUrl: string = "";
    oktaSearchUrl: string = "";

    public operationMetadata: any;
    public HighestTagChangeDateInMilliseconds: number = 0;
    public apiUrl: string = "";
    public loginUrl: string = "";
    public fullCache$: BehaviorSubject<any> = new BehaviorSubject<any>(null);
    public fullDataCacheExists$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public dataCacheAssetsExist$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public dataSource$: BehaviorSubject<string> = new BehaviorSubject<string>("prod");
    private updatedAssets$: Subscription = new Subscription();
    public updatedTagsViaSignalR$: Subject<any> = new Subject<any>();

    cache: any = createEmptyDataCache();
    ready: boolean | undefined;
    private multiDataDeferralList: Array<any> = [];

    Statistics: any;

    public updatedObservationsList: Array<IObservation> = [];

    serviceName: string = "data.service: ";

    public gsetacticalComponents: any = [
    { path: 'tactical-gsetop-five-alarms', name: 'Top Five Alarms' },
    { path: 'tactical-gse-out-of-service', name: 'Out Of Service' },
    { path: 'tactical-gse-fleet-usage', name: 'Fleet Usage' },
    { path: 'tactical-gse-available-assets', name: 'Fleet Availablity' },
        { path: "tactical-levels", name: "Fleet Levels" },
    { path: 'gse-assets-in-use', name: 'Assets In Use' },

    ];

    public gateTacticalComponents: any = [
        { path: "pca-not-meeting-performance", name: "PCA Not Meeting Performance", assetType: "PCA" },
        { path: "gpu-out-of-compliance", name: "GPU Out Of Compliance", assetType: "GPU" },
        { path: "pbb-out-of-compliance", name: "PBB Out Of Compliance", assetType: "PBB" },
        { path: "tactical-gstop-five-alarms", name: "Top Five Alarms", assetType: "any" },
        { path: "tactical-gs-out-of-service", name: "Out Of Service", assetType: "any" },
    { path: 'tactical-gs-equipment-not-used', name: 'Equipment Not Used', assetType: "any" },
    { path: 'tactical-gs-comms-loss', name: 'Comms Loss', assetType: "any" },
    { path: 'tactical-intelligent-charging-system', name: 'Inteligent Charging System', assetType: "ICS" },
        { path: "pbb-docking", name: "PBB Docking", assetType: "PBB" },
        { path: "equipment", name: "Equipment Cycle Counts and Runtime", assetType: "any" },
    { path: 'gs-assets-in-use', name: 'Assets In Use', assetType: "any" },
    { path: 'gs-alarm-status', name: 'Alarm Status', assetType: "any" },
    { path: 'gs-available-assets', name: 'Available Assets', assetType: "any" },

    ];

    public assetComponents: any = [
        { path: "historical-events", name: "Historical Events" },
        { path: "historical-faults", name: "Historical Faults" },
        { path: "historical-alerts", name: "Historical Alerts" },
    ];

    private gsePeakReportJBTStandardObservationIds: Array<any> = [19131, 19134, 19133, 19132, 19161, 19125, 55021, 54910, 55231, 55229, 1968, 54118, 19127, 54911, 54285, 54288, 54286, 54287, 56302, 56303, 56326, 56327, 56328, 56363, 56364, 56365, 3895, 4504, 54271, 19264, 55259, 55192, 55185, 19141, 19229, 19230, 54180];
    public activeSubjects: Array<ITagNamePrefixSubject> = [];
    public sqlListenerSubjects: Array<ISQLServerSubject> = [];

    public totalTimeToLoadTags: number = 0;
    public timeInMSWeJoinedSignalRGroups: number = 0;
    public totalTimeInMSFromJoinGroupsToTagsBeingReceived: number = 0;

    private fullDataCacheSubscription: any;
    private lastIngestTagCount: number = 0;
    private lastFlushCount: number = 0;
    private lastUpdatedTagsCount: number = 0;

    public weatherCurrentConditionsUpdated$ = new Subject<any>();

    // ---- Cache readiness (one source of truth) ----
    private hasEmittedReady = false;

    /** Emits true when any boot path marks the cache as ready */
  public cacheReady$: Observable<boolean> = this.fullDataCacheExists$.pipe(
    startWith(Global.FullDataCacheExists === true),
    distinctUntilChanged()
  );

    /** Call this when your cache is usable (even minimally) */
    private markCacheReady(reason: string) {
        if (this.hasEmittedReady) return;
        this.hasEmittedReady = true;
        this.readyForUse = true;
        Global.FullDataCacheExists = true;
    console.log('[DataService] markCacheReady:', reason);
    try { this.fullDataCacheExists$.next(true); } catch { }
    }

    /** Reset all cached in-memory data, observables, subjects, and readiness state */
    public resetDataServiceState(): void {
        console.log(this.serviceName + "resetDataServiceState invoked.");
        this.cache = createEmptyDataCache();
        this.cachedObservables = {};
        this.hasEmittedReady = false;
        this.ready = false;
        this.readyForUse = false;
        this.HighestTagChangeDateInMilliseconds = 0;
        this.updatedObservationsList = [];
        this.activeSubjects = [];
        this.sqlListenerSubjects = [];
        this.multiDataDeferralList = [];
        try { this.fullCache$.next(null); } catch { }
        try { this.fullDataCacheExists$.next(false); } catch { }
        try { this.dataCacheAssetsExist$.next(false); } catch { }
    }

    constructor(
        private http: HttpClient,
        private signalRCore: SignalRCoreService,
        private utilityService: UtilityService,
        private zone: NgZone,
        private incomingDataService: IncomingDataService,
        private injector: Injector,
        private tagCacheService: TagCacheService
    ) {

        if (document.URL.indexOf("localhost") > 0 || document.URL.indexOf("test") > 0) {
            //console.log("checking Global: %O", Global);
            if (Global.Data.dataServerIsLocal == true) {
                //console.log("Global.Data.dataServerIsLocal = " + Global.Data.dataServerIsLocal);
                //-- this is to run the webAPI locally.  The setting for Global.Data.dataServerUrl and Global.Data.dataServerIsLocal has already been defined in the Global variables.
                Global.SignalR.CoreUrl = Global.Test.signalRCoreServerUrl;
                Global.Data.footerDataSource = "Test";
                Global.Application.Environment.Name = "test";
                Global.Application.Environment.Database = "test";
            } else {
                var useProductionDataOnTest = localStorage.getItem("useProductionDataOnTest") == "false" ? false : true;
                //console.log("useProductionDataOnTest = " + useProductionDataOnTest);

                if (useProductionDataOnTest) {
                    //console.log("using Production data on test.");
                    Global.Data.dataServerUrl = Global.Prod.dataServerUrl;
                    Global.SignalR.CoreUrl = Global.Prod.signalRCoreServerUrl;
                    Global.Data.reportsServerUrl = Global.Prod.reportsServerUrl;
                    Global.Data.footerDataSource = "Prod";
                    Global.Application.Environment.Name = "test";
                    Global.Application.Environment.Database = "prod";
                } else {
                    //console.log("using Test data on test.");
                    //-- Had to explicitly set the dataServerUrl here since the relative addressing wasn't working. --Kirk T. Sherer, November 20, 2023.
                    Global.Data.dataServerUrl = Global.Test.dataServerUrl;
                    Global.SignalR.CoreUrl = Global.Test.signalRCoreServerUrl;
                    Global.Data.reportsServerUrl = Global.Test.reportsServerUrl;
                    Global.Data.footerDataSource = "Test";
                    Global.Application.Environment.Name = "test";
                    Global.Application.Environment.Database = "test";
                }
            }
        } else {
            console.log("Just normal production data setup.");
            //-- if we didn't come in here with Test in the URL or localhost, set the database to Prod.
            Global.Data.dataServerUrl = Global.Prod.dataServerUrl;
            Global.SignalR.CoreUrl = Global.Prod.signalRCoreServerUrl;
            Global.Data.reportsServerUrl = Global.Prod.reportsServerUrl;
        }

        // console.log("Global after check: %O", Global);

    this.signalRCore.hubConnectionState$?.subscribe((hubConnectionState:string) => {
            if (!isUnitTestRunner()) {
                console.log(this.serviceName + "hubConnectionState: " + hubConnectionState + ", Global.SignalR.Status: " + Global.SignalR.Status);
            }
            if (hubConnectionState == "Connected" && Global.SignalR.Status == "Disconnected") {
            Global.SignalR.Status = "Connected";   //-- setting back to 'Connected' so that we don't continually hit this piece of code again after reconnection.
                var startTimer = performance.now();

                this.getTagsFromAPIAgain();
            }
        });

        if (!isUnitTestRunner()) {
            window.addEventListener("focus", () => {
                Global.User.DebugMode && console.log("<--- Browser window got focus --->");
                this.getTagsFromAPIAgain();
                this.utilityService?.showToastMessageShared?.({
                    type: "connected",
                    message: "iOPS refocused. Tags retrieved.",
                    title: "Browser"
                });
            });
        }

        this.dataSource$.next(Global.Application.Environment.Database);
        let localDataServerUrl = Global.Data.dataServerUrl;

        var urlHostname = window.location.hostname;
        const replaceWithAzHost = (rawUrl: string): string => {
            if (!rawUrl) {
                return rawUrl;
            }
            try {
                const parsedUrl = new URL(rawUrl);
                const hostname = parsedUrl.hostname.toLowerCase();
        if (
          hostname.endsWith('iopspro.com') &&
          hostname !== 'az.iopspro.com' &&
          !hostname.endsWith('.az.iopspro.com')
        ) {
          parsedUrl.hostname = parsedUrl.hostname.replace(/iopspro\.com$/i, 'az.iopspro.com');
                }
                return parsedUrl.toString();
            } catch {
                return rawUrl;
            }
        };
        if (urlHostname.indexOf("az.") > -1) {
            Global.Data.dataServerUrl = replaceWithAzHost(Global.Data.dataServerUrl);
            Global.SignalR.CoreUrl = replaceWithAzHost(Global.SignalR.CoreUrl);
            Global.Data.reportsServerUrl = replaceWithAzHost(Global.Data.reportsServerUrl);
            localDataServerUrl = Global.Data.dataServerUrl;
        }
        if (!isUnitTestRunner()) {
            console.log('urlHostname = ' + urlHostname);
            console.log("dataServerUrl: " + localDataServerUrl);
            console.log("signalRUrl: " + Global.SignalR.CoreUrl);
            console.log("reportsServerUrl: " + Global.Data.reportsServerUrl);
        }

        if (!Global.FullDataCacheExists) {
            this.fullDataCacheSubscription = this.fullDataCacheExists$.subscribe((data: any) => {
                if (data === true) {
                    Global.FullDataCacheExists = true;
                    this.fullDataCacheSubscription && this.fullDataCacheSubscription.unsubscribe();
                }
            });
        }

        this.updatedAssets$ = this.tagCacheService.assetStatePatches$?.subscribe((patches) => {
            for (const [assetId, patch] of patches) {
                const asset = this.cache.assetsObject[assetId];
                if (!asset) continue;

                this.formatAssetCurrentStatusFromTagRecords(asset, patch);
            }
        }) ?? new Subscription();

        setInterval(() => {
            this.harvestIncomingData();
        }, 100);

        setInterval(() => {
            this.ProcessRecentlyUpdatedTags();
        }, 500);

        setInterval(() => {
            var tagsProcessed = this.tagCacheService.ingestTagCounter - this.lastIngestTagCount;
            var tagsUpdated = this.tagCacheService.tagsUpdatedCounter - this.lastUpdatedTagsCount;

            if (this.lastFlushCount > 0 && !isUnitTestRunner()) {
                var difference = this.tagCacheService.flushCounter - this.lastFlushCount;
                console.log("tagCache: lastFlushCount (30 second check): " + difference + ", tagsProcessed: " + tagsProcessed + ", tagsUpdated: " + tagsUpdated);
            }
            this.lastIngestTagCount = this.tagCacheService.ingestTagCounter;
            this.lastUpdatedTagsCount = this.tagCacheService.tagsUpdatedCounter;
            this.lastFlushCount = this.tagCacheService.flushCounter;
        }, 30000);

        setInterval(() => {
            if (Global.FullDataCacheExists) {
                this.buildAssetStates();
            }
        }, 3000);

        // DebugMode is set from user settings during authentication; do not force-enable it here.

        if (Global.Data.dataServerIsLocal) {
            localDataServerUrl.replace("/iOPS", "");
        }

        this.loginUrl = localDataServerUrl + "/LoginValidation?$format=application/json;odata.metadata=none";
        this.potdUrl = localDataServerUrl + "/POTD?$format=application/json;odata.metadata=none";
        this.imageKeyURL = localDataServerUrl + "/ImageDownload";
        this.genericDataUrl = localDataServerUrl + "/GenericDataObject";
        this.multiDataAPIUrl = localDataServerUrl + "/MultiDataCondensedHTMLEncoded";
        this.downloadFileUrl = localDataServerUrl + "/SpreadsheetDownloadForCompositeStatement";
        this.requestUserAccountURL = localDataServerUrl + "/RequestUserAccount";
        this.requestToResetPasswordURL = localDataServerUrl + "/RequestToResetPassword";
        this.changePasswordUrl = localDataServerUrl + "/ChangePassword";
        this.fileUploadUrl = localDataServerUrl + "/upload";
        this.fileUploadListUrl = localDataServerUrl + "/ByImageKeyList";
        this.questionControlDataUrl = localDataServerUrl + "/Data";
        this.oktaSearchUrl = localDataServerUrl + "/OktaSearch";

        var service = this;

        this.fullCache$ = new BehaviorSubject(service.cache);
        this.fullCache$.subscribe({
            next: (cache) => {
                if (cache) {
                    // for(var propertyName in service.cache){
                    //   Global.User.DebugMode && console.log("Property = " + propertyName);
                    //   Global.User.DebugMode && console.log("Property Value = %O", service.cache[propertyName]);
                    // }

                    if (!Global.User.isAdmin) {
                        //-- current user isn't an administrator, so only give them their own list of issues. --Kirk T. Sherer, August 8, 2023.
                        service.cache.userIssues = (service.cache.userIssues ?? [])
                            .filter((issue: any) => issue.CreatorUserId == Global.User.currentUser.Id);
                    }

                    service.cache.ready = true;
                    service.ready = true;
                    Global.User.DebugMode && console.log("this.cache (fullCache$) = %O", { ...service.cache });

                    var actualPermittedSites: any[] = [];
                    var permittedSites: any;
                    //-- NOTE: Global.User.isAdmin = Global.User.currentUser.IsSystemAdministrator.
                    //-- If this is no longer correct, then it should be reset in the authentication component and the login component. --Kirk T. Sherer, September 3, 2020.
                    permittedSites = service.cache.sites;
                    permittedSites.forEach((site: any) => {
                        if (site.Name != null && site.Id != null) {
                            actualPermittedSites.push(site);
                        }
                    });
                    Global.User.PermittedSites = actualPermittedSites;
                    // var image = this.GetImage("assets\\img\\default-avatar.png");
                    // console.log("GetImage value = " + image);
                    // Global.User.DebugMode && console.log("Global.User.PermittedSites = %O", Global.User.PermittedSites);
                    // Global.User.DebugMode && console.log("Global.User = %O", Global.User);
                }
            },
            error: (e) => {
                Global.User.DebugMode && console.error(`fullCache$ error: ${e}`);
            },
            complete: () => {
                console.info("full cache complete");
            },
        });
    }

    applicationLoadingMessageObject: { message: any; shouldExist: any } | undefined;
    currentUserJSONObject: any;
    httpHeaders: any;

    multiDataTimeout: any;

    cachedObservables: any = {};

    preferredStdIds = [6, 7, 5, 2];
    customerTypes = [
        {
            name: "mcneilus",
            value: "mcneilus",
            logoUrl: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQDNGBAfKOLax3M-RwZ58kVzgRFrHbsqFJA-w&s",
            preferredStdIds: [6, 7, 5, 2],
            searchChips: [
                {
                    type: "stdId",
                    value: 7,
                    name: "Driving",
                },
                {
                    type: "stdId",
                    value: 6,
                    name: "Moving With Parking Brake On",
                },
                {
                    type: "stdId",
                    value: 5,
                    name: "Check Engine",
                },
                {
                    type: "assetState",
                    value: "inAlarm",
                    name: "Alarm",
                    greaterThanIndex: 1,
                },
            ],
            categories: [
                {
                    icon: "alert-outline",
                    name: "Check Engine",
                    queryParameter: "checkEngine",
                    value: "true",
                },
                {
                    icon: "alert-circle-outline",
                    name: "In Alarm",
                    queryParameter: "inAlarm",
                    value: "true",
                },
                {
                    icon: "car-outline",
                    name: "Driving",
                    queryParameter: "driving",
                    value: "true",
                },

                {
                    icon: "stop-circle-outline",
                    name: "Parking Brake",
                    queryParameter: "parkingBrake",
                    value: "true",
                },
            ],
            favorites: [
                {
                    type: "asset",
                    id: 1,
                    url: "/asset-summary",
                    detailedRoute: "overview",
                },
            ],
        },
        {
            name: "pierce",
            value: "pierce",
            logoUrl: "https://upload.wikimedia.org/wikipedia/commons/5/50/Pierce_Manufacturing_logo.png",
            preferredStdIds: [6, 7, 5, 50],
            searchChips: [
                {
                    type: "stdId",
                    value: 7,
                    name: "Driving",
                },
                {
                    type: "stdId",
                    value: 6,
                    name: "Moving With Parking Brake On",
                },
                {
                    type: "stdId",
                    value: 50,
                    name: "Emergency Mode",
                },
                {
                    type: "assetState",
                    value: "inAlarm",
                    name: "Alarm",
                    greaterThanIndex: 1,
                },
            ],
            categories: [
                {
                    icon: "sunny-outline",
                    name: "Emergency Mode",
                    queryParameter: "emergencyMode",
                    value: "true",
                },
                {
                    icon: "alert-circle-outline",
                    name: "In Alarm",
                    queryParameter: "inAlarm",
                    value: "true",
                },
                {
                    icon: "car-outline",
                    name: "Driving",
                    queryParameter: "driving",
                    value: "true",
                },

                {
                    icon: "stop-circle-outline",
                    name: "Parking Brake",
                    queryParameter: "parkingBrake",
                    value: "true",
                },
            ],
            favorites: [
                {
                    type: "asset",
                    id: 21,
                    url: "asset-summary",
                    detailedRoute: "overview",
                },
            ],
        },
    ];

    public selectedCustomerType = this.customerTypes[0];

    public assetList: Asset[] = [
        {
            maintenanceInstances: [
                {
                    date: new Date("December 17, 2024 03:24:00"),
                    partsOrdered: new Date("January 17, 2025 03:24:00"),
                    orderStatus: "Waiting to Ship",
                },
                {
                    date: new Date("December 17, 2024 03:24:00"),
                    partsOrdered: new Date("January 17, 2025 03:24:00"),
                    orderStatus: "Waiting to Ship",
                },
                {
                    date: new Date("December 17, 2024 03:24:00"),
                    partsOrdered: new Date("January 17, 2025 03:24:00"),
                    orderStatus: "Waiting to Ship",
                },
                {
                    date: new Date("December 17, 2024 03:24:00"),
                    partsOrdered: new Date("January 17, 2025 03:24:00"),
                    orderStatus: "Waiting to Ship",
                },
                {
                    date: new Date("December 17, 2024 03:24:00"),
                    partsOrdered: new Date("January 17, 2025 03:24:00"),
                    orderStatus: "Waiting to Ship",
                },
                {
                    date: new Date("December 17, 2024 03:24:00"),
                    partsOrdered: new Date("January 17, 2025 03:24:00"),
                    orderStatus: "Waiting to Ship",
                },
                {
                    date: new Date("December 17, 2024 03:24:00"),
                    partsOrdered: new Date("January 17, 2025 03:24:00"),
                    orderStatus: "Waiting to Ship",
                },
                {
                    date: new Date("December 17, 2024 03:24:00"),
                    partsOrdered: new Date("January 17, 2025 03:24:00"),
                    orderStatus: "Waiting to Ship",
                },
                {
                    date: new Date("December 17, 2024 03:24:00"),
                    partsOrdered: new Date("January 17, 2025 03:24:00"),
                    orderStatus: "Waiting to Ship",
                },
            ],
            id: 1,
            name: "Truck# 1234",
            description: "Asset 1 Description",
            customer: "mcneilus",
            imageUrl: "https://img.freepik.com/premium-photo/semi-truck-delivery-white-background-meticulous-detailing-distinctive-noses_899449-161723.jpg?w=300",
            assetType: "Sideload",
            commodity: "Recycle",
            status: "active",
            issues: 3,
            route: 431,
            mileage: Math.floor(Math.random() * 1000),
            tags: signal([
                {
                    stdId: 100,
                    name: "latitude",
                    // dateAsSignal: signal(new Date()),
                    date: new Date(),
                    recentlyUpdated: false,
                    // valueAsSignal: signal(46.69804),
                    value: 46.69804,
                    valueWhenActive: "1",
                    severity: "informational",
                },
                {
                    stdId: 101,
                    name: "longitude",
                    // dateAsSignal: signal(new Date()),
                    date: new Date(),
                    recentlyUpdated: false,
                    // valueAsSignal: signal(-87.63329),
                    value: -87.63329,
                    valueWhenActive: "1",
                    severity: "informational",
                },
                {
                    stdId: 1,
                    name: "Do Not Move Truck",
                    // dateAsSignal: signal(new Date()),
                    date: new Date(),
                    recentlyUpdated: false,
                    // valueAsSignal: signal('0'),
                    value: "0",
                    valueWhenActive: "1",
                    severity: "informational",
                },
                {
                    stdId: 2,
                    name: "Battery Voltage Low",
                    // dateAsSignal: signal(new Date()),
                    date: new Date(),
                    recentlyUpdated: false,
                    // valueAsSignal: signal('0'),
                    value: "0",
                    valueWhenActive: "1",
                    severity: "warning",
                },

                {
                    stdId: 3,
                    name: "Stop Engine",
                    // dateAsSignal: signal(new Date()),
                    date: new Date(),
                    recentlyUpdated: false,
                    // valueAsSignal: signal('0'),
                    value: "0",
                    valueWhenActive: "1",
                    severity: "alarm",
                },
                {
                    stdId: 4,
                    name: "Steps Extended",
                    // dateAsSignal: signal(new Date()),
                    date: new Date(),
                    recentlyUpdated: false,
                    // valueAsSignal: signal('0'),
                    value: "0",
                    valueWhenActive: "1",
                    severity: "alarm",
                },
                {
                    stdId: 7,
                    name: "Driving",
                    // dateAsSignal: signal(new Date()),
                    date: new Date(),
                    recentlyUpdated: false,
                    // valueAsSignal: signal('1'),
                    value: "1",
                    valueWhenActive: "1",
                    severity: "informational",
                },
                {
                    stdId: 5,
                    name: "Check Engine",
                    // dateAsSignal: signal(new Date()),
                    date: new Date(),
                    recentlyUpdated: false,
                    // valueAsSignal: signal('0'),
                    value: "0",
                    valueWhenActive: "1",
                    severity: "informational",
                },
                {
                    stdId: 6,
                    name: "Moving With Parking Brake On",
                    // dateAsSignal: signal(new Date()),
                    date: new Date(),
                    recentlyUpdated: false,
                    // valueAsSignal: signal('0'),
                    value: "0",
                    valueWhenActive: "1",
                    severity: "critical",
                },
            ]),
        },
    ];

    severityLevelStructure: any = [
        {
            customer: "mcneilus",
            structure: [
                {
                    name: "Informational",
                    value: "informational",
                    index: 1,
                    presentation: "checkmark-circle-outline",
                    color: "green",
                },
                {
                    name: "Warning",
                    value: "warning",
                    index: 2,
                    presentation: "close-circle-outline",
                    color: "rgb(222, 167, 0)",
                },
                {
                    name: "Alarm",
                    value: "alarm",
                    index: 3,
                    presentation: "close-circle-outline",
                    color: "red",
                },
                {
                    name: "Critical",
                    value: "critical",
                    index: 4,
                    presentation: "close-circle-outline",
                    color: "violet",
                },
            ],
        },
        {
            customer: "pierce",
            structure: [
                {
                    name: "White",
                    value: "white",
                    index: 1,
                    presentation: "checkmark-circle-outline",
                    color: "green",
                },
                {
                    name: "Amber",
                    value: "amber",
                    index: 2,
                    presentation: "close-circle-outline",
                    color: "rgb(222, 167, 0)",
                },
                {
                    name: "Red",
                    value: "red",
                    index: 3,
                    presentation: "close-circle-outline",
                    color: "red",
                },
            ],
        },
    ];

    getTagsFromAPIAgain() {
        //-- this will get the tags again for the selected organization on the current user's record.
        var selectedOrganizationId = Global?.User?.currentUser?.SelectedOrganizationId ?? null;

        if (selectedOrganizationId) {
            var startTimer = performance.now();
            this.GetTagsFromWebAPI(selectedOrganizationId)
                .then((response: any) => {
                    const endTimer = performance.now();
                    console.log(`${this.serviceName} Time to get tags string from webAPI: ${endTimer - startTimer}ms...`);

                    return this.FormatTagsFromWebAPI(response);
                })
                .catch((err: any) => {
                    console.warn(`${this.serviceName} Tags API failed; continuing without pre-fetched tags. Error:`, err);
                    // IMPORTANT: resolve to null so the next .then still runs
                    return null;
                });
        }
    }

    generateLastHourDataForAllAssets() {
        //
        this.assetList.forEach((asset: any) => {
            asset.tags().forEach((tag: any) => {
                if (tag.name !== "latitude" && tag.name !== "longitude") {
                    let list = [];
                    //create 10 random historical observations with a value of "0" or "1"
                    for (let i = 0; i < 10; i++) {
                        let historicalObservation = Math.floor(Math.random() * 2);
                        list.push(historicalObservation);
                    }
                    tag.historicalObservations = signal(list);
                }
            });
        });
    }

    getAssets(params?: any) {
        console.log("dataService.getAssets called with params: ", params);
        // {search: '1234', active: true, severity: 'critical', description: 'Asset 1 Description', fleet: 'Oshkosh'}
        let reducedAssetList: any = [];

        if (params.id) {
            reducedAssetList = this.assetList.filter((a) => {
                return a.id == params.id;
            });
        } else {
            reducedAssetList = this.assetList.filter((a) => {
                return a.customer === this.selectedCustomerType.name;
            });

            reducedAssetList = reducedAssetList.filter((a: any) => {
                if (params.search) {
                    return a.name.toLowerCase().includes(params.search.toLowerCase());
                } else {
                    return a;
                }
            });

            reducedAssetList.forEach((a: any) => {
                a.highestAlarm = this.returnHighestSeverityLevelConfigurationForCurrentStateOfAsset(a);
            });
            if (params.inAlarm === "true") {
                reducedAssetList = reducedAssetList.filter((a: any) => {
                    if (a.highestAlarm && a.highestAlarm.severityStructure.index > 1) {
                        return a;
                    }
                });
            }
            if (params.driving === "true") {
                //find driving asset on truck and see if it's active
                reducedAssetList = reducedAssetList.filter((a: any) => {
                    let drivingTag = a.tags().find((t: any) => {
                        return t.stdId === 7;
                    });
                    return drivingTag.value === drivingTag.valueWhenActive;
                });
            }

            if (params.emergencyMode === "true") {
                //find driving asset on truck and see if it's active
                reducedAssetList = reducedAssetList.filter((a: any) => {
                    let emergencyModeTag = a.tags().find((t: any) => {
                        return t.stdId === 50;
                    });
                    return emergencyModeTag.value === emergencyModeTag.valueWhenActive;
                });
            }

            if (params.checkEngine === "true") {
                //find driving asset on truck and see if it's active
                reducedAssetList = reducedAssetList.filter((a: any) => {
                    let checkEngineTag = a.tags().find((t: any) => {
                        return t.stdId === 5;
                    });
                    return checkEngineTag.value === checkEngineTag.valueWhenActive;
                });
            }

            if (params.parkingBrake === "true") {
                //find driving asset on truck and see if it's active
                reducedAssetList = reducedAssetList.filter((a: any) => {
                    let parkingBrakeTag = a.tags().find((t: any) => {
                        return t.stdId === 6;
                    });
                    return parkingBrakeTag.value === parkingBrakeTag.valueWhenActive;
                });
            }
        }

        reducedAssetList.forEach((a: any) => {
            a.preferredTags = a.tags().filter((t: any) => {
                return this.selectedCustomerType.preferredStdIds.includes(t.stdId);
            });
        });
        return reducedAssetList;
    }

    getAlarmSeverityLevelsForCustomer() {
        let structure = this.severityLevelStructure.find((sls: any) => {
            return sls.customer === this.selectedCustomerType.name && sls;
        });
        let onlyAlarmSeverityLevels: any = [];
        structure.structure.forEach((s: any) => {
            if (s.index > 1) {
                onlyAlarmSeverityLevels.push(s.value);
            }
        });

        return onlyAlarmSeverityLevels;
    }

    evaluateAssetsHighestSeverityLevel(passedAsset: any): any {
        let asset = this.assetList.find((a) => {
            return passedAsset.id === a.id;
        });

        let severityStructure = this.severityLevelStructure.find((svs: any) => {
            return svs.customer === asset?.customer;
        });

        let activeAlerts: any = asset?.tags().filter((t) => {
            // return t.value === t.valueWhenActive
            return;
        });

        activeAlerts?.forEach((alert: any) => {
            let severity = severityStructure?.structure.find((s: any) => {
                return s.value === alert.severity;
            });
            if (severity) {
                alert.severityIndex = severity.index;
            }
        });

        if (activeAlerts && activeAlerts.length > 0) {
            // sort the active alerts by the severity of the found severityLevelStructure
            let sortedActiveAlerts: any = activeAlerts.sort((a: { severityIndex: number }, b: { severityIndex: number }) => {
                //sort by field severityIndex
                return b.severityIndex - a.severityIndex;
            });
            if (sortedActiveAlerts && sortedActiveAlerts.length > 0) {
                return sortedActiveAlerts[0];
            } else {
                return [];
            }
        } else {
            return [];
        }
    }

    returnHighestSeverityLevelConfigurationForCurrentStateOfAsset(asset: any) {
        //TODO insted of returning hard coded informational, return the lowest severity level for the organization
        let returnedObject: any = {
            severityLevel: "",
            severityStructure: {},
        };
        let highestAlarm = this.evaluateAssetsHighestSeverityLevel(asset);
        let severityStructureAsset = this.severityLevelStructure.find((svs: any) => {
            return svs.customer === asset.customer;
        });

        let severityStructure = severityStructureAsset?.structure.find((s: any) => {
            return s.value === highestAlarm.severity;
        });

        if (highestAlarm && severityStructure) {
            returnedObject.severityStructure = severityStructure;
            returnedObject.severityLevel = highestAlarm;
            returnedObject.activeTagsWithSameSeverity = asset.tags().filter((t: any) => {
                return t.severity === highestAlarm.severity && t.value === t.valueWhenActive;
            });
        } else {
            let lowestSeverity = severityStructureAsset?.structure.sort((a: any, b: any) => {
                return a.index - b.index;
            })[0];
            returnedObject.severityLevel = lowestSeverity.name;
            returnedObject.severityStructure = lowestSeverity;
        }

        return returnedObject;
    }

    saveCustomerType(customerTypeName: string) {
        let customerType = this.customerTypes.find((ct) => {
            return ct.name === customerTypeName;
        });

        if (customerType) {
            this.selectedCustomerType = customerType;
        }
    }

    getAssetById(id: string) {
        return this.assetList.find((a) => {
            return a.id === parseInt(id);
        });
    }

    serviceDataMutator(assetIndex?: any) {
        //write interval executing every 2 seconds
        let interval = setInterval(() => {
            //random number generator for the value of the tags

            let lengthOfAssetList = this.assetList.length;
            //random asset picker using lengthOfAssetList
            let randomAsset;
            if (assetIndex) {
                randomAsset = this.assetList[assetIndex];
            } else {
                let randomAssetPicker = Math.floor(Math.random() * lengthOfAssetList);
                randomAsset = this.assetList[randomAssetPicker];
            }
            //random tag picker using length of tags
            let lengthOfTags = randomAsset.tags().length;
            let randomTagPicker = Math.floor(Math.random() * lengthOfTags);
            let randomTag: any = randomAsset.tags()[randomTagPicker];
            //random value generator
            let randomValue = Math.floor(Math.random() * 2);
            console.log(randomTag);
            if (randomTag.name !== "latitude" && randomTag.name !== "longitude") {
                this.assetList[assetIndex].tags.update((tags: any) => {
                    return tags.map((t: any) => {
                        if (t.stdId === randomTag.stdId) {
                            t.recentlyUpdated = true;
                            t.date = new Date();
                            t.value = randomValue.toString();
                            // t.valueAsSignal.set(randomValue.toString())
                            // t.dateAsSignal.set(new Date());
                            let timeout = setTimeout(() => {
                                t.recentlyUpdated = false;
                                clearTimeout(timeout);
                            }, 3000);
                            return t;
                        } else {
                            return t;
                        }
                    });
                });
                // randomTag.value.set(randomValue.toString())
                // // randomTag.value = randomValue.toString();
                // randomTag.date = new Date();
                console.log(randomAsset.name + " " + randomTag.name + " value is now " + randomTag.value);
                // console.log(randomTag.historicalObservations())
                // randomTag.historicalObservations.update((values: any) => [...values, randomValue]);

                // console.log(randomTag.historicalObservations())
            }
        }, 2000);
    }

    ApplicationLoadingMessage(message: any, shouldExist: any) {
        this.applicationLoadingMessageObject = {
            message: message,
            shouldExist: shouldExist,
        };
    }

    SQLMultiAction(requestJSON: any, accessToken?: string) {
        // if (!this.currentUserJSONObject) {
        //   var user =
        //     localStorage?.getItem('currentUser') != null
        //       ? localStorage?.getItem('currentUser')
        //       : '';
        //   this.currentUserJSONObject = user != null ? JSON.parse(user) : '';
        // }

    const rawToken = Global.User.currentUser?.AuthenticationToken ?? accessToken ?? '';
    const bearerToken = rawToken?.startsWith('Bearer ') ? rawToken : `Bearer ${rawToken}`;

        this.httpHeaders = new HttpHeaders({
            "Content-Type": "text/plain",
            Authorization: bearerToken,
        });

        let httpOptions = {
            headers: this.httpHeaders,
            observe: "response" as "response",
            responseType: "text" as "text",
        };

        // var testJSON: any = [
        //    {
        //        label: "AMMR",
        //        sqlStatement: "API.Asset_MaintenanceModeReasons",
        //    },
        //    {
        //        label: "AOOSR",
        //        sqlStatement: "API.Asset_OutOfServiceReasons"
        //    },
        //    {
        //        label: "Countries",
        //        sqlStatement: "API.GetListOfCountries"
        //    },
        //    {
        //        label: "StateAbbreviations",
        //        sqlStatement: "API.GetStateAbbreviations"
        //    }
        // ];

        // requestJSON = testJSON;
        var compositeStatement = requestJSON
            .select((item: any) => {
                return item.label + "^" + item.sqlStatement;
            })
            .toArray()
            .join("~");

        console.log("multiDataAPIUrl = " + this.multiDataAPIUrl + ", compositeStatement = " + compositeStatement);

        const http = this.http
            .post(this.multiDataAPIUrl, compositeStatement, httpOptions)
            .toPromise()
            .then((data: any) => {
                var returnArray = data.body
                    .split("~~!!")
                    .select((q: string) => {
                        return q.split("%*");
                    })
                    .skip(1)
                    .where((q: any) => {
                        return q.length > 2;
                    })
                    .select((q: any) => {
                        return {
                            label: q[0],
                            sqlStatement: q[1],
                            sqlMS: q[2],
                            data: q[4] == "" ? [] : q[4] == undefined ? [] : q[4].startsWith("ERROR") ? q[4] : this.ExpandCondensedTableData(q[3], q[4]),
                        };
                    })
                    .toArray();

                return returnArray;
            })
      .catch((error:any) => {
                const status = error?.status;
                const statusText = error?.statusText;
                const body = error?.error || error?.message || error?.xhr?.responseText;
                const dump = this.safeStringify(error);
                console.error("Error executing MultiDataCondensed: " + (error?.message || error), { status, statusText, body, dump });
                throw error;
            });

        return http;
    }

    performMultiDataBatch() {
        console.log(this.serviceName + "performMultiDataBatch invoked. multiDataDeferralList = %O", this.multiDataDeferralList);
        //Build a multidata json object to submit
        var queryCounter: number = 1;

        //var testJSON = [
        //          {
        //              label: "AMMR",
        //		sqlStatement: "API.Asset_MaintenanceModeReasons",
        //          },
        //          {
        //              label: "AOOSR",
        //              sqlStatement: "API.Asset_OutOfServiceReasons"
        //          },
        //          {
        //              label: "Countries",
        //              sqlStatement: "API.GetListOfCountries"
        //          },
        //          {
        //              label: "StateAbbreviations",
        //              sqlStatement: "API.GetStateAbbreviations"
        //          }
        //];
        if (this.multiDataDeferralList && this.multiDataDeferralList.length > 0) {
            t0 = performance.now();
            var jsonGroupedBySubmission = this.multiDataDeferralList
                .select((item: { sqlStatement: any; promiseResolve: any; promiseObject: any }) => {
                    return {
                        label: "Q" + queryCounter++,
                        sqlStatement: item.sqlStatement,
                        promiseResolve: item.promiseResolve,
                        promiseObject: item.promiseObject,
                    };
                })
                .groupBy((item: { sqlStatement: any }) => {
                    return item.sqlStatement;
                })
                .select((item: { key: any; toArray: () => any }) => {
                    var output = {
                        label: "Q" + queryCounter++,
                        sqlStatement: item.key,
                        group: item.toArray(),
                    };
                    if (output.group.length > 1) {
                        // console.log(
                        // 	"data-service: aggregated multidata found. output = %O",
                        // 	output
                        // );
                    }
                    return output;
                })
                .toArray();

            this.multiDataDeferralList = [];
            var t0 = performance.now();

            if (
                jsonGroupedBySubmission.any((item: { group: string | any[] }) => {
                    return item.group.length > 1;
                })
            ) {
                // console.log(
                // 	"data-service: aggregated multidata jsonGroupedBySubmission found. jsonGroupedBySubmission = %O",
                // 	jsonGroupedBySubmission
                // );
            }

      this.SQLMultiAction(jsonGroupedBySubmission).then(
                    (multiReturn: {
                        body: any;
                        select: (
                            arg0: (q: any) => {
                                label: any;
                                sqlStatement: any;
                                sqlMS: any;
                                data: any;
                                group: any;
                            }
                        ) => string | any[];
                    }) => {
                        var returnObject: any = multiReturn.body;
                        //console.log(performance.now() + " SQLMultiAction Submission = %O", jsonGroupedBySubmission);

                        var returnArray = multiReturn
                            .select((q: any) => {
                                var submissionObject = jsonGroupedBySubmission.first((s: any) => {
                                    return s.label == q.label;
                                });
                                return {
                                    label: q.label,
                                    sqlStatement: q.sqlStatement,
                                    sqlMS: q.sqlMS,
                                    data: q.data,
                                    group: submissionObject.group,
                                };
                            })
                            .toArray();
                        // Global.User.DebugMode &&
                        // 	console.log(
                        // 		performance.now() +
                        // 			" " +
                        // 			(performance.now() - t0) +
                        // 			"ms SQL MultiData Processed Complex Object: %O",
                        // 		returnArray
                        // 	);

                        returnArray.forEach((dataReturn: any) => {
                            if (!Array.isArray(dataReturn.data) && dataReturn.data.startsWith("ERROR")) {
                                console.error("Error in SQL MultiData statement: %O", dataReturn);
                                if (dataReturn.data.contains("Expired")) {
                                    this.SQLActionAsPromise(dataReturn.data.sqlStatement); //-- running the statement again since we timed out on SQL Server.
                                }
                            } else {
                                dataReturn.group.forEach((sqlQuery: { promiseResolve: (arg0: any) => void }) => {
                                    sqlQuery.promiseResolve(dataReturn.data);
                                });
                            }
                        });

                        console.log(this.serviceName + "SQLActionAsPromise (deferred to multiDataDeferralList) --> total time: " + (performance.now() - t0) + "ms = %O", returnArray);
                    }
      ).catch((error: any) => {
                    console.error(this.serviceName + "performMultiDataBatch failed", error);
                    jsonGroupedBySubmission.forEach((submission: any) => {
                        submission.group.forEach((sqlQuery: { promiseResolve: (arg0: any) => void }) => {
                            sqlQuery.promiseResolve([]);
                        });
                    });
                });
        }
    }

    ExpandCondensedTableData(cols: string, data: any) {
        if (cols == undefined || data == undefined) {
            return; //-- nothing to parse here.
        }

        var colNumber = 0;

        try {
            //console.log("ExpandCondensedTableData: cols = " + cols + ", data = " + data);

            var colData = cols.split(",").map((colSet: any) => {
                if (colSet) {
                    var colArray = colSet.split("_");

                    return {
                        type: colArray[0],
                        name: colArray[1],
                        position: colNumber++,
                    };
                } else {
                    return null;
                }
            });

            var expandedData = data.split("`&").map((row: any) => {
                var rowDataArray = row.split("~!");
                var rowObject: any = {};
                colData.forEach(function (col) {
                    switch (col?.type) {
                        case "S":
                            rowObject[col.name] = rowDataArray[col.position] == "" ? null : rowDataArray[col.position];
                            break;
                        case "N":
                            rowObject[col.name] = rowDataArray[col.position] == "" ? null : +rowDataArray[col.position];
                            break;
                        case "D":
                            rowObject[col.name] = rowDataArray[col.position] == "" || rowDataArray[col.position] == 0 ? null : new Date(+rowDataArray[col.position]);
                            rowObject[col.name + "MS"] = rowDataArray[col.position] == "" || rowDataArray[col.position] == 0 ? null : +rowDataArray[col.position];
                            break;
                        case "B":
                            rowObject[col.name] = rowDataArray[col.position] == "True" || +rowDataArray[col.position] == 1 ? true : false;
                            break;
                    }
                });
                return rowObject;
            });
            return expandedData;
        } catch (error) {
            console.error(`Error in Expand Condensed Table Data: ${error}`);
        }
    }

    SQLActionAsPromise(sqlStatement: string) {
        //console.log(this.serviceName + "SQLActionAsPromise invoked. Run immediately? " + (immediate ?? false) + ", sql: " + sqlStatement);
        //Global.User.DebugMode && console.log("Global.dataServerIsLocal = " + Global.dataServerIsLocal);
        if (!this.currentUserJSONObject) {
            const currentUserStr = localStorage.getItem("currentUser");
            this.currentUserJSONObject = <IUser>(currentUserStr ? JSON.parse(currentUserStr) : null);
        }

    if (
      sqlStatement.substring(0, 4) != "API." &&
      sqlStatement.substring(0, 5) != "URPT." &&
      sqlStatement.substring(0, 9) != "Security." &&
      sqlStatement.substring(0, 10) != "Reporting." &&
      sqlStatement.substring(0, 4) != "dbo."
    ) {
            //-- Default unqualified procedure names to API. Explicit schema prefixes such as API., URPT., Security., Reporting., and dbo. are preserved as-is. --Kirk T. Sherer, February 6, 2020 / clarified for current behavior.
            sqlStatement = "API." + sqlStatement;
        }

        const bypassDeferredBatch = sqlStatement.substring(0, 4) == "dbo.";

        //console.log(performance.now() + " SQLActionAsPromise Invoked - " + sqlStatement);
        if (Global.User.isLoggedIn && !bypassDeferredBatch) {
            //Will hold the resolve function reference
            var promiseResolve;

            var listElement = {
                sqlStatement: sqlStatement,
                promiseResolve: undefined, //Set to undefined initially
                promiseObject: new Promise(function (resolve) {
                    promiseResolve = resolve; //Save the resolution function in the local variable promiseResolve
                }),
            };

            clearTimeout(this.multiDataTimeout);
            this.multiDataTimeout = setTimeout(() => {
                this.performMultiDataBatch();
            }, 500);

            //Set the promiseResolve property of the list element to the saved function reference so we can run it later.
            listElement.promiseResolve = promiseResolve;

            //Set the promiseResolve property of the list element to the saved function reference so we can run it later.
            listElement.promiseResolve = promiseResolve;

            //console.log(this.serviceName + performance.now() + " SQLActionAsPromise listElement: %O", listElement);

            //Add it to the list.
            if (this.multiDataDeferralList == undefined) {
                this.multiDataDeferralList = [];
            }

            this.multiDataDeferralList.push(listElement);
            //console.log(this.serviceName + performance.now() + " SQLActionAsPromise multiDataDeferralList: %O", this.multiDataDeferralList);

            //Return the unresolved (pending) promise back to the caller. We will resolve it later.
            return listElement.promiseObject;
        }

        var methodType = "post";

        //Global.User.DebugMode && console.log("SQLActionAsPromise: sqlStatement = " + sqlStatement + ", type = " + type);

        this.httpHeaders = new HttpHeaders({
            "Content-Type": "text/plain",
            Authorization: this.currentUserJSONObject?.AuthenticationToken,
        });
        this.httpHeaders.Authorization = this.currentUserJSONObject?.AuthenticationToken;

        let httpOptions = {
            headers: this.httpHeaders,
            observe: "response" as "response",
            responseType: "text" as "text",
        };

        //Global.User.DebugMode && console.log("sqlStatement before editing for URL: " + sqlStatement);

        // if (sqlStatement.indexOf(" ") > 0 && methodType == "get") {
        // 	sqlStatement = sqlStatement.split(" ").join("+");
        // 	//Global.User.DebugMode && console.log("changing SQL statement spaces into URL-encoded '+' symbols...");
        // }

        // if (sqlStatement.indexOf("'") > 0 && methodType == "get") {
        // 	sqlStatement = sqlStatement.split("'").join("%27");
        // 	//Global.User.DebugMode && console.log("changing SQL statement single quotes into URL-encoded %27 symbols...");
        // }

        // if (sqlStatement.indexOf(",") > 0 && methodType == "get") {
        // 	sqlStatement = sqlStatement.split(",").join("%2C");
        // 	//Global.User.DebugMode && console.log("changing SQL statement commas into URL-encoded %2C symbols...");
        // }

        //Global.User.DebugMode && console.log("sqlStatement after editing for URL: " + sqlStatement);

        var sqlFullUrl = this.multiDataAPIUrl;

        if (methodType == "get") {
            sqlFullUrl = this.multiDataAPIUrl + "?storedProcedureName=" + sqlStatement;
        }

        //Global.User.DebugMode && console.log("sqlFullUrl = " + sqlFullUrl);

        //Global.User.DebugMode && console.log("methodType = " + methodType);
        if (methodType == "get") {
            const http = this.http
                .get(sqlFullUrl, httpOptions)
                .toPromise()
                .then((response: any) => {
                    console.log("get DataCondensed = %O", response);
                    var returnArray = response.body.split("\r\n").skip(1).toArray();

                    return returnArray[0] == undefined || returnArray[1] == undefined ? [] : this.ExpandCondensedTableData(returnArray[0], returnArray[1]);
                });
            return http;
        } else {
            var t0 = performance.now();
            const http = this.http
                .post(this.multiDataAPIUrl, sqlStatement, httpOptions)
                .toPromise()
                .then((response: any) => {
                    console.log("post DataCondensed = %O", response);
                    var returnArray = response.body
                        .split("~~!!")
                        .select((q: string) => {
                            return q.split("%*");
                        })
                        .skip(1)
                        .where((q: any) => {
                            return q.length > 2;
                        })
                        .select((q: any) => {
                            return {
                                label: q[0],
                                sqlStatement: q[1],
                                sqlMS: q[2],
                                data: q[4] == "" ? [] : q[4] == undefined ? [] : q[4].startsWith("ERROR") ? q[4] : this.ExpandCondensedTableData(q[3], q[4]),
                            };
                        })
                        .toArray();

                    // var returnArray = response.body.split("\r\n").skip(1).toArray();
                    console.log(this.serviceName + "SQLActionAsPromise (immediate) --> total time: " + (performance.now() - t0) + "ms = %O", returnArray);

                    return returnArray.firstOrDefault()?.data;
                });
            return http;
        }
    }

    //-- SQL Action function ---
    SQLAction(sqlStatement: string, type?: string) {
        //Global.User.DebugMode && console.log("Global.dataServerIsLocal = " + Global.dataServerIsLocal);
        var methodType = type ?? "post";

        if (!this.currentUserJSONObject) {
            var user = localStorage?.getItem("currentUser") != null ? localStorage?.getItem("currentUser") : "";
            this.currentUserJSONObject = user != null ? JSON.parse(user) : "";
        }

        this.httpHeaders = new HttpHeaders({
            "Content-Type": "text/plain",
            Authorization: this.currentUserJSONObject.AuthenticationToken && Global.User.currentUser?.AuthenticationToken,
        });

        if (this.cachedObservables[sqlStatement]) {
            return this.cachedObservables[sqlStatement];
        } else {
            //--returns an observable.  Usage is to call this function with a .subscribe function. --Kirk T. Sherer, February 11, 2020.

            let httpOptions = {
                headers: this.httpHeaders,
                observe: "response" as "response",
                responseType: "text" as "text",
            };

            //Global.User.DebugMode && console.log("sqlStatement before editing for URL: " + sqlStatement);

            if (sqlStatement.indexOf(" ") > 0 && methodType == "get") {
                sqlStatement = sqlStatement.split(" ").join("+");
                //console.log("data.service: changing SQL statement spaces into URL-encoded '+' symbols...");
            }

            if (sqlStatement.indexOf("'") > 0 && methodType == "get") {
                sqlStatement = sqlStatement.split("'").join("%27");
                //console.log("data.service: changing SQL statement single quotes into URL-encoded %27 symbols...");
            }

            if (sqlStatement.indexOf(",") > 0 && methodType == "get") {
                sqlStatement = sqlStatement.split(",").join("%2C");
                //console.log("data.service: changing SQL statement commas into URL-encoded %2C symbols...");
            }

      if (
        sqlStatement.substring(0, 4) != "API." &&
        sqlStatement.substring(0, 5) != "URPT." &&
        sqlStatement.substring(0, 10) != "Reporting." &&
        sqlStatement.substring(0, 4) != "dbo."
      ) {
                //-- Default unqualified procedure names to API. Explicit schema prefixes such as API., URPT., Reporting., and dbo. are preserved as-is. --Kirk T. Sherer, February 6, 2020 / clarified for current behavior.
                sqlStatement = "API." + sqlStatement;
            }

            //Global.User.DebugMode && console.log("data.service: sqlStatement after editing for URL: " + sqlStatement);

            var sqlFullUrl = this.apiUrl;

            if (methodType == "get") {
                sqlFullUrl = this.apiUrl + "?storedProcedureName=" + sqlStatement;
            }

            //Global.User.DebugMode && console.log("data.service: sqlFullUrl = " + sqlFullUrl);
            //Global.User.DebugMode && console.log("data.service: methodType = " + methodType);

            if (methodType == "get") {
                const http$ = this.http.get(sqlFullUrl, httpOptions).pipe(
                    map((data: any) => data.body), //--don't need all the other stuff with the HTTPResponse, so just send back the body that will contain the data from the stored procedure call. --Kirk T. Sherer, January 6, 2020.
                    // tap(data => {
                    //   Global.User.DebugMode && console.log("this.SQLAction data = %O", {...data});
                    // }),
                    catchError((err) => of([]))
                );
                this.cachedObservables[sqlStatement] = http$;
                return http$;
            } else {
                const http$ = this.http.post(this.apiUrl, sqlStatement, httpOptions).pipe(
                    map((data: any) => data.body), //--don't need all the other stuff with the HTTPResponse, so just send back the body that will contain the data from the stored procedure call. --Kirk T. Sherer, January 6, 2020.
                    // tap(data => {
                    //   Global.User.DebugMode && console.log("this.SQLAction data = %O", {...data});
                    // }),
                    catchError((err) => of([]))
                );
                this.cachedObservables[sqlStatement] = http$;
                return http$;
            }

            // const behaviorSubject$ = new BehaviorSubject(null);

            // http$.subscribe({
            //   next: x => behaviorSubject$.next(x),
            //   error: x => behaviorSubject$.error(x),
            //   complete: () => behaviorSubject$.complete()
            // });

            // var newCachedObservable = new ICachedObservable;
            // newCachedObservable.queryText = sqlStatement;
            // newCachedObservable.observable = behaviorSubject$;
            // this.cachedObservables.push(newCachedObservable);
        }
    }

    updateDebugMode(debugMode: boolean, updateFromSettings?: boolean) {
        Global.User.DebugMode = debugMode;

        if (updateFromSettings) {
            this.SQLActionAsPromise("API.UserSettings_UpdateRecordByIdAndFieldName " + Global.User.currentUser.Id + ", 'DebugMode', '" + (Global.User.DebugMode == true ? 1 : 0) + "'").then((data: any) => {
                Global.User.DebugMode && console.log("DebugMode updated in user settings. user record: %O", data);
            });
        }
    }

    ReturnToLastVisitedRoute(returnToLastVisitedRoute: boolean, updateFromSettings?: boolean) {
        if (updateFromSettings) {
            this.SQLActionAsPromise("API.UserSettings_UpdateRecordByIdAndFieldName " + Global.User.currentUser.Id + ", 'ReturnToLastVisitedRoute', '" + (returnToLastVisitedRoute == true ? 1 : 0) + "'").then((data: any) => {
                if (returnToLastVisitedRoute == true) {
                    Global.User.currentUser.ReturnToLastVisitedRoute = true;
                } else {
                    Global.User.currentUser.ReturnToLastVisitedRoute = false;
                }

                Global.User.DebugMode && console.log("ReturnToLastVisitedRoute updated in user settings. user record: %O", data);
            });
        }
    }

    //===========================================================================================================
    //+Attach a metadata object to the given input object.
    //This is used by view controllers to implement
    //color fade outs, removal after inactivity, etc. Since the dataService has to track the data, it will
    //reset these back to 10000 upon any data change anywhere.
    //10000 is used as an the update initial value so that we can get the resulution we need.
    //===========================================================================================================
    AttachBlankMetadataObject(obj: any) {
        obj.Metadata = {
            UpdateCountDowns: {
                OneSecond: 0,
                TenSecond: 0,
                ThirtySecond: 0,
                OneMinute: 0,
                FiveMinute: 0,
                FifteenMinute: 0,
                ThirtyMinute: 0,
                OneHour: 0,
            },
            Statistics: {
                ChangeCount: 0,
                MessageCount: 0,
                PreviousMessageCount: 0,
                MessagesPerSecond: 0,
                IsLastUpdateHistorical: false,
                KepwareToMainDatabaseTimeMS: 0,
                MainDatabaseToBrowserTimeMS: 0,
                KepwareToBrowserTimeMS: 0,
                KepwareSQLTimeDifferenceMSFromCentral: 0,
            },
            Status: {
                LastValueWasHistorical: false,
            },
        };
        return obj;
    }

    public DurationInMS(dateInMilliseconds: number): number {
        return dateInMilliseconds == null ? 0 : Date.now() - dateInMilliseconds;
    }

    public guid() {
        return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
            const r = (Math.random() * 16) | 0,
                v = c == "x" ? r : (r & 0x3) | 0x8;
            return v.toString(16);
        });
    }


    //***G
    //++SignalR Observation Update - push messages in real-time.
    //+This function is run whenever each signalR message arrives.
    //***G
    UpdateNewObservationFromSignalR(signalRDataRaw: any) {
        if (Global.User.isLoggedIn == false) {
            return;
        }

        // Split the incoming delimited string by carriage return / line feed
        var incomingSignalRData: any = [];

        incomingSignalRData = signalRDataRaw.object.split("\r\n");

        var newlyUpdatedObservations: Array<IObservation> = [];
        //console.log('UpdateNewObservationFromSignalR tagRecords: %O', tagRecords);

        incomingSignalRData.forEach((signalRMessage: string) => {

            Global.SignalR.countOfObservations != undefined ? Global.SignalR.countOfObservations++ : 1;
            let signalRData = this.GetJsonFromSignalR(signalRMessage);

            // if (signalRData.i == 11801341 ) {
            //      console.log('UpdateNewObservationFromSignalR invoked... signalRDataRaw: %O', signalRDataRaw);
            //      console.log('UpdateNewObservationFromSignalR incomingSignalRData: %O', incomingSignalRData);
            //      console.log('UpdateNewObservationFromSignalR signalRData: %O', signalRData);
            //      console.log('UpdateNewObservationFromSignalR signalRMessage: %O', signalRMessage);
            // }

            signalRData.SignalRReceivedMS = new Date().getTime();
            signalRData.SignalRLatencyMS = signalRData.SignalRReceivedMS - signalRData.d;

            if (signalRData.i) {
                //Find the Tag object in cache - it SHOULD already be there via a load from the database. -- Dylan Richard, June 20, 2022.
                // console.log("this.cache.tagsObject = %O", this.cache?.tagsObject);
                // console.log("this.cache.tagsObject[" + signalRData.i + "] = %O", this.cache?.tagsObject[signalRData.i]);

                var tagObsClone: IObservation = this.cache?.observationsObject[signalRData.i] && structuredClone(this.cache?.observationsObject[signalRData.i]);

                //Add a stub object there if for any reason we do not have the tag in the cache yet.
                //we cannot know the asset id, that will have to wait for a db load.
                /*-----------------------------------------------------------------------------------------------------------------------------------
                * NOTE: The following 'if' statement was put back in case the tag record DOES NOT exist in the data cache yet.
                * 		 This is always a possibility, especially in the case of graphing any tags from a data summary widget,
                * 		 because the user may have selected some tag records that are not yet in the data cache. --Kirk T. Sherer, September 20, 2022.
                -----------------------------------------------------------------------------------------------------------------------------------*/
                if (!tagObsClone) {
                    //console.error("UpdateNewObservationFromSignalR - received tag observation that is not in cache: %O", signalRData);
                    return;
                } else {
                    var currentTag: ITag = this.cache.tagsObject[signalRData.i];
                    // if (signalRData.i === 11801341) {
                    //      console.log(`SignalR Update --> tagObsClone TagId: ${tagObsClone.TagId}, tagsObject TagId: ${currentTag.Id}, signalRData TagId: ${signalRData.i}, signalRData = %O`, signalRData);
                    // }

                    //signalRData.i && console.log("signalRData.i: " + signalRData.i + ", signalRData.v = " + signalRData.v + ", tag.Value = " + tagClone?.Value + ", signalRData.d = " + signalRData.d + ", currentTag.Date = " + tagClone?.DateInMilliseconds);

                    //console.log('UpdateNewObservationFromSignalR updating existing tag record: %O', signalRData);

                    let lastChangeDateInMilliseconds: number = tagObsClone.LastChangeDateInMilliseconds;
                    let lastChangeValue: string = tagObsClone.LastChangeValue;

                    if (lastChangeDateInMilliseconds != signalRData.d && lastChangeValue != signalRData.v) {
                        try {

                            //------------------------------------------------------------------------------------------
                            //-- NOTE:  We are not updating the tags if both the value and the date/time have not changed.
                            //--        This is because we're not processing the retransmissions any longer in SQL Server, so
                            //--        there's no need to do anything with an observation that is identical in both value and
                            //--        date/time to the existing observation. --Kirk T. Sherer, March 24, 2026.
                            //------------------------------------------------------------------------------------------
                            let signalRCastAsNumber = Number(signalRData.v);
                            tagObsClone.LastChangeValue = !_.isNaN(signalRCastAsNumber) ? signalRCastAsNumber : signalRData.v;
                            tagObsClone.LastChangeDateInMilliseconds = signalRData.d; //--set the last change datetime in milliseconds to the new date/time since the value changed.

                            let pvCastAsNumber = Number(tagObsClone.Value);
                            let previousObservation: ITagHistorical = {
                                TagId: tagObsClone.TagId,
                                Value: tagObsClone.Value != null ? (!_.isNaN(pvCastAsNumber) ? pvCastAsNumber : tagObsClone.Value.toString()) : null,
                                DateInMilliseconds: +tagObsClone.DateInMilliseconds,
                            };

                            var observation =
                                currentTag.Historical &&
                                currentTag.Historical?.firstOrDefault((observation: any) => {
                                    return observation.DateInMilliseconds == tagObsClone.DateInMilliseconds && observation.Value == tagObsClone.Value;
                                });

                            if (!observation) {
                                if (currentTag.Historical && currentTag.Historical.length >= 500) {
                    var earliestRecord = currentTag.Historical.orderBy((item: any) => { return item.DateInMilliseconds }).firstOrDefault((item: any) => {
                                        return item;
                                    });

                                    currentTag.Historical = currentTag.Historical.where((observation: any) => {
                                        return observation.DateInMilliseconds !== earliestRecord?.DateInMilliseconds;
                                    }).toArray(); // Remove the tag from the signal array
                                }
                                currentTag.Historical?.unshift(previousObservation);
                            }

                            tagObsClone.DateInMilliseconds = signalRData.d; //-- this is the new observation date in milliseconds.
                            tagObsClone.JavascriptDate = new Date(signalRData.d);
                            tagObsClone.Value = !_.isNaN(signalRCastAsNumber) ? signalRCastAsNumber : signalRData.v;
                            tagObsClone.RecentlyUpdated = true;
                            tagObsClone.RecentlyUpdatedDate = new Date().getTime();
                            tagObsClone.RedisKeyName = signalRData.RedisKeyName;
                            currentTag.UpdateCount++;
                            tagObsClone.LocalUpdateDateUTCMS = Date.now();
                            tagObsClone.DateOfLastChangedValueInMS = tagObsClone.LastChangeValue && tagObsClone.LastChangeValue != signalRData.v ? tagObsClone.DateInMilliseconds : tagObsClone.DateOfLastChangedValueInMS;

                            //console.log('UpdateNewObservationFromSignalR  - pushing updated tag onto updatedObservationsList, newlyUpdatedObservations. Updated tag record: %O', tag);

                            this.updatedObservationsList.push(tagObsClone); //--updatedObservationsList is for the green color when observation is initially updated, and then removing the green color when it's been over 2 seconds since the change.
                            //------------------------------------------------------------------------------------------------------------------------------------------------------
                            // -- Update Angular Signal Tag Arrays ---
                            //------------------------------------------------------------------------------------------------------------------------------------------------------
                            newlyUpdatedObservations.push(tagObsClone);

                            //updatedObservation.TagId && console.log("tagObsClone.Id: " + tagObsClone.TagId + ", tagObsClone.Value = " + tagObsClone.Value + ", currentTag.LastObs.Value = " + currentTag.LastObs.Value + ", tagObsClone.Date = " + updatedObservation.DateInMilliseconds + ", currentTag.Date = " + currentTag.LastObs.DateInMilliseconds);
                            this.cache.observationsObject[tagObsClone.TagId] = tagObsClone;
                            currentTag.LastObs = tagObsClone;

                            // var duration = tag.DurationInMS();
                            // console.log("Duration: " + duration + ", SignalR update to existing tag = %O", tag);

                            this.signalRCore.countOfSignalRObservations$.next(Global.SignalR.countOfObservations != undefined ? Global.SignalR.countOfObservations : 1);
                            //console.log("Global.SignalR.countOfObservations = " + Global.SignalR.countOfObservations);

                            if (this.activeSubjects.length > 0) {
                                this.activeSubjects.forEach((activeSubject: ITagNamePrefixSubject) => {
                                    var tagHasAssetIdWeCareAbout = activeSubject.TagNamePrefix.firstOrDefault((tagNamePrefix: string) => {
                                        return tagNamePrefix == currentTag.Asset.TagNamePrefix;
                                    });
                                    if (tagHasAssetIdWeCareAbout) {
                                        //Global.User.DebugMode && console.log(this.serviceName + "sending updated tag we care about to active subscribers: %O", tag);
                                        activeSubject.Subject$.next(currentTag); //-- send the updated tag to the listening widgets with active subject subscriptions.  If there are none, then this won't do anything.  --Kirk T. Sherer, July 26, 2024.
                                    }
                                });
                            }

                            if (signalRData.SignalRLatencyMS > 30000 && Global.FullDataCacheExists) {
                                console.log("<--- SignalR Latency ---> " + signalRData.SignalRLatencyMS + "ms. TagName = " + currentTag.Name + "  TagId = " + currentTag.Id + ", RedisKeyName = " + tagObsClone.RedisKeyName + ", SignalRData = %O", signalRData);
                            }
              }
              catch (e) {
                            console.error("error with tag " + currentTag.Name + " previous observation. tag = %O", tagObsClone);
                        }
                    }
                }
            }
        });

        if (newlyUpdatedObservations.length > 0) {
            this.batchUpdateObservations(newlyUpdatedObservations); //-- batch update the tags in the cache so that we can update the UI with the new values. --Kirk T. Sherer, July 26, 2025.
        }
    }

    // Batch update function to update all relevant signal arrays
    async batchUpdateObservations(newObservations: IObservation[]) {
        //console.log("batchUpdateObservations invoked. newObservations = %O", newObservations);
        if (Global.FullDataCacheExists) {
            // de-dupe by TagId in case you got multiple updates for the same tag
            const updates = new Map<number, { obs: IObservation; tag: ITag }>();
            for (const obs of newObservations) {
                const tag = this.cache.tagsObject[obs.TagId];
                if (tag) updates.set(tag.Id, { obs, tag });
            }
            this.tagCacheService.queueTagUpdates([...updates.values()]);
        }
        return true;
    }

    processNextArrivalSignalRMessage(signalRDataRaw: any) {
        //Global.User.DebugMode && console.log(this.serviceName + "processNextArrivalSignalRMessage invoked. signalRDataRaw = %O", signalRDataRaw);
        this.Statistics.SignalR.MessageCount++;

        let signalRData = this.GetJsonFromSignalR(signalRDataRaw);
        signalRData.SignalRReceivedMS = new Date().getTime();
        signalRData.SignalRLatencyMS = signalRData.SignalRReceivedMS - signalRData.d;

        if (signalRData.SystemId) {
            //Find the Systems object in cache
            var system = this.cache.systemsObject[signalRData.SystemId];

            if (system) {
                //-- if the user doesn't have access to the system record we have in the message, there is no need to update the Next Arrival.  Hence the reason there is no 'else' statement for this 'if' condition.

                system.NextArrival =
                    signalRData.NextArrival != null
                        ? {
                              UTC: signalRData.NextArrival != null ? this.utilityService.convertMillisecondsToDateWithTimezoneOffset(+signalRData.NextArrival, 0) : null,
                              Local: signalRData.NextArrival != null ? new Date(signalRData.NextArrival) : null,
                              Site: signalRData.NextArrival != null ? this.utilityService.convertMillisecondsToDateWithTimezoneOffset(+signalRData.NextArrival, system.Site.UTCTimeOffset) : null,
                          }
                        : null;
            }
        }
    }

    GetJsonFromSignalR(signalRData: any) {
        var returnObject: any = {};

        var signalRString = signalRData?.object != undefined ? signalRData.object : signalRData;

        //Global.User.DebugMode && console.log("signalRString = " + signalRString);
        var stringList: any = signalRString.split("~");
        //Global.User.DebugMode && console.log("stringList = %O", stringList);
        stringList.forEach((item: string) => {
            //Global.User.DebugMode && console.log("item = " + item);
            var splitField: any = item.split("!");
            //Global.User.DebugMode && console.log("splitField = %O", splitField);
            returnObject[splitField[0]] = isNaN(+splitField[1]) ? splitField[1] : +splitField[1];
            //Global.User.DebugMode && console.log("returnObject[" + splitField[0] + "] = " + returnObject[splitField[0]]);
        });

        //Global.User.DebugMode && console.log("data-service: GetJsonFromSignalR returnObject = %O", returnObject);

        return returnObject;
    }

    GetBrokenOutFieldsFromStringTagData(data: any) {
        try {
            let returnedData = data.map((tstring: any) => {
                return this.ParseDStringIntoDiscreetFields(tstring);
            });

            return returnedData;
        } catch (e) {
            return this.ParseDStringIntoDiscreetFields(data); //-- data didn't have a .map as a function, so just pass data to the same function. --Kirk T. Sherer, May 9, 2023.
        }
    }

    ParseDStringIntoDiscreetFields(data: any) {
        try {
            if (data?.d == undefined && data?.D == undefined && data == undefined) {
                //console.log("ParseDStringIntoDiscreetFields invoked. Data is undefined. data = %O", data);
                return undefined;
            } else {
                var tarray = data.d !== undefined ? data.d?.split("~") : data.D !== undefined ? data.D?.split("~") : data != undefined ? data?.split("~") : undefined;
                //console.log("TagId: " + tarray[0] + ", tarray = %O", tarray);
                if (tarray) {
                    let parsedObservation = {
                        Id: +tarray[0],
                        Name: tarray[1],
                        SiteId: +tarray[2],
                        DateInMilliseconds: tarray[4] == null || tarray[4] == "" ? null : +tarray[4],
                        AssetId: +tarray[5],
                        LastObservationId: tarray[6] == null || tarray[6] == "" ? null : +tarray[6],
                        JBTStandardObservationId: +tarray[7],
                        LastObservationTextValue: tarray[8] == null || tarray[8] == "" || tarray[8] == "null" || tarray[8] == "NaN" ? null : tarray[8],
                        LastObservationQuality: tarray[9] == null || tarray[9] == "" ? null : +tarray[9],
                        IsAlarm: +tarray[10] == 1,
                        IsWarning: +tarray[11] == 1,
                        ValueWhenActive: tarray[12] == null || tarray[12] == "" ? "1" : tarray[12],
                        GateName: tarray[13],
                        AssetName: tarray[14],
                        IsInformational: +tarray[15] == 1,
                        IsCritical: +tarray[16] == 1,
                        DataType: "DB",
                        Severity: tarray[18],
                        ValueTranslation: tarray[19] == null || tarray[19] == "" ? null : tarray[19],
                        IsAlert: +tarray[20] == 1,
                        EffectiveSeverityLevelId: tarray[21] == null || tarray[21] == "" ? 4 : +tarray[21],
                        RedisKeyName: tarray[22] == null || tarray[22] == "" ? null : tarray[22],
                        IsDiagnostic: +tarray[23] == 1,
                        LastChangeDateInMilliseconds: tarray[24] == null || tarray[24] == "" ? (tarray[4] == null || tarray[4] == "" ? null : +tarray[4]) : +tarray[24],
                        LastChangeValue: tarray[25] == null || tarray[25] == "" ? (tarray[8] == null || tarray[8] == "" || tarray[8] == "null" || tarray[8] == "NaN" ? null : tarray[8]) : tarray[25],
			            EffectiveAlarmCategoryName: tarray[26] == null || tarray[26] == "" ? null : tarray[26],
			            EffectiveAlarmCategoryId: tarray[27] == null || tarray[27] == "" ? 0 : +tarray[27]
                    };

                    return parsedObservation;
                } else {
                    return undefined;
                }
            }
        } catch (error) {
            console.error("Error with ParseDStringIntoDiscreetFields. data = %O", data);
            return undefined;
        }
    }

    BuildStandardCacheTagObjectFromDatabaseFields(entityFromDatabase: any) {
        var existingTagRecord = this.cache.tagsObject[entityFromDatabase.Id];
        //console.log("existingTagRecord = %O", existingTagRecord);

        var retryCount = 0;
        var successful = false;
        while (!successful && retryCount < 5) {
            try {
                //console.log("GetStandardCacheTagObjectFromDatabaseFields: entityFromDatabase: %O", entityFromDatabase);

                var service = this;
                // var site: any = this.cache.sitesObject[entityFromDatabase.SiteId];
                var asset: IAsset = this.cache.assetsObject[entityFromDatabase.AssetId];

                var standardObservation: IStandardObservation = entityFromDatabase.JBTStandardObservationId != null ? service.cache.jbtStandardObservationsObject[+entityFromDatabase.JBTStandardObservationId] : null;

                //Global.User.DebugMode && console.log("Pre-loaded observation = %O", entityFromDatabase);
                var plcUTCDate = !entityFromDatabase.DateInMilliseconds ? null : new Date(entityFromDatabase.DateInMilliseconds);
                var obsCreatedDate = !entityFromDatabase.LastObservationCreationDate ? null : new Date(entityFromDatabase.LastObservationCreationDate);

                if (entityFromDatabase.Name == undefined && entityFromDatabase.TagName == undefined) {
                    return undefined;
                } else {
                    var tagNameSplit = (entityFromDatabase.Name || entityFromDatabase.TagName).split("|");
                    var lastOne = tagNameSplit.last().toString();
                    var tagSimpleName = lastOne.replace(".PCA.", "").replace(".GPU.", "").replace(".PBB.", "").replace("Airport_", "").replace(".", "");

                    var favoritesArray = Global.User.currentUser?.UserFavorites;
                    let valueCastAsNumber = Number(entityFromDatabase.LastObservationTextValue);
                    let valueWhenActiveCastAsNumber = Number(entityFromDatabase.ValueWhenActive);

                    var currentDateTimeInMS = new Date().getTime();
                    var tagLastObservationRecord = new IObservation(
                        +entityFromDatabase.Id, //TagId: number;
                        !_.isNaN(valueCastAsNumber) ? valueCastAsNumber : entityFromDatabase.LastObservationTextValue, // Value: any,
                        +entityFromDatabase.DateInMilliseconds, // DateInMilliseconds: number,
                        new Date(entityFromDatabase.DateInMilliseconds), // JavascriptDate: Date,
                        entityFromDatabase.LastObservationQuality || 192, // Quality?: number,
                        existingTagRecord ? existingTagRecord.DateInMilliseconds : undefined, //--these two fields should not come from SQL Server. It's tracking whether or not we've ever received a SignalR update in the application. // PreviousObservationDateInMilliseconds?: number,
                        existingTagRecord ? existingTagRecord.Value : undefined, //--these two fields should not come from SQL Server. It's tracking whether or not we've ever received a SignalR update in the application. // PreviousObservationTextValue?: string,
                        entityFromDatabase.DateInMilliseconds, // DateOfLastChangedValueInMS: number,
                        Date.now(), // LocalUpdateDateUTCMS: number,
                        false, // RecentlyUpdated?: boolean,
                        0, // RecentlyUpdatedDate?: number,
                        entityFromDatabase.RedisKeyName // RedisKeyName?: string
                    );

                    // if (entityFromDatabase.EffectiveAlarmCategoryId != 0) {
                    //     console.log("BuildStandardCacheTagObjectFromDatabaseFields: entityFromDatabase.EffectiveAlarmCategoryId = " + entityFromDatabase.EffectiveAlarmCategoryId + ", entityFromDatabase.EffectiveAlarmCategoryName = " + entityFromDatabase.EffectiveAlarmCategoryName);
                    // }

                    var tagModelCacheRecord = new ITag(
                        asset, // Asset: any,
                        +entityFromDatabase.AssetId, // AssetId: number,
                        favoritesArray != null // Favorite: boolean = false,
                            ? favoritesArray.firstOrDefault((fav: any) => {
                                  return fav.TagId == entityFromDatabase.Id;
                              }) != null
                                ? true
                                : false
                            : false,
                        this.guid(), // Guid: string,
                        +entityFromDatabase.Id, // Id: number,
                        entityFromDatabase.IsAlarm, // IsAlarm: boolean = false,
                        entityFromDatabase.IsAlert, // IsAlert: boolean = false,
                        entityFromDatabase.IsCritical, // IsCritical: boolean = false,
                        entityFromDatabase.IsInformational, // IsInformational: boolean = false,
                        entityFromDatabase.IsWarning, // IsWarning: boolean = false,
                        entityFromDatabase.Name || entityFromDatabase.TagName, // Name: string,
                        asset?.TagNamePrefix || "", // TagNamePrefix: string,
                        entityFromDatabase.ValueTranslation, // ValueTranslation: string,
                        0, // UpdateCount: number,
                        tagLastObservationRecord, //-- Last Observation Date, Last Observation Text Value, Quality, RedisKeyName. (see above for last observation information).existingTagRecord != null ? existingTagRecord.UpdateCount++ : 0, // UpdateCount?: number,
                        +entityFromDatabase.EffectiveAlarmCategoryId, // EffectiveAlarmCategoryId: number,
					    entityFromDatabase.EffectiveAlarmCategoryName, // EffectiveAlarmCategoryName: string,
                        standardObservation, // JBTStandardObservation?: any,
                        +entityFromDatabase.JBTStandardObservationId, // JBTStandardObservationId?: number,
                        entityFromDatabase.EffectiveSeverityLevelId != null ? +entityFromDatabase.EffectiveSeverityLevelId : 4, // EffectiveSeverityLevelId?: number,
                        entityFromDatabase.Severity || "Informational", // Severity?: string,
                        standardObservation != null ? standardObservation.Name : tagSimpleName.replace(".", " ").replace("_", " "), // ShortTagName?: string,
                        entityFromDatabase.SimpleName || tagSimpleName, // SimpleName?: string,
                        entityFromDatabase.SiteId != null ? +entityFromDatabase.SiteId : service.cache.assetsObject[entityFromDatabase.AssetId].SiteId, // SiteId?: number,
                        entityFromDatabase.SiteId != null ? service.cache.sitesObject[entityFromDatabase.SiteId]?.Name : service.cache.assetsObject[entityFromDatabase.AssetId].Site?.Name, // SiteName?: string,
                        !_.isNaN(valueWhenActiveCastAsNumber) ? valueWhenActiveCastAsNumber : !_.isNil(entityFromDatabase.valueWhenActiveCastAsNumber) ? entityFromDatabase.valueWhenActiveCastAsNumber : 1, // ValueWhenActive?: any
                        entityFromDatabase.IsDiagnostic, // IsDiagnostic: boolean = false,
                        [] //-- Historical Observation Tag array.  This starts with nothing since we haven't received any tags yet for this new tag record.

                    );

                    if (entityFromDatabase.JBTStandardObservationId != null) {
                        //-- we are mapped to a JBT Standard Observation Id.  Find out if it's in the list of tags for the Standard Observation's object.  If not, then add it.  Otherwise, it's already been updated with the Tag data cache.
                        var stdObjTag = this.cache.jbtStandardObservationsObject[entityFromDatabase.JBTStandardObservationId]?.Tags?.firstOrDefault((tag: ITag) => {
                            return tag.Id == entityFromDatabase.TagId;
                        });
                        if (!stdObjTag) {
                            this.cache.jbtStandardObservationsObject[entityFromDatabase.JBTStandardObservationId]?.Tags?.push(tagModelCacheRecord);
                        }
                    }

                    if (!tagModelCacheRecord) {
                        console.log("tagModelCacheRecord is null", entityFromDatabase);
                    } else {
                        successful = true;
                        //console.log(tagModelCacheRecord);
                    }
                    //console.log("tagModelCacheRecord = %O", tagModelCacheRecord);
                    return tagModelCacheRecord;
                }
            } catch (e) {
                console.error("Error in creating tag object: %O", e);
                retryCount++;
                if (retryCount > 5) {
                    return null;
                }
            }
        }

        return null;
    }

    public harvestIncomingData() {
        if (Global.FullDataCacheExists == true) {
            var service = this;
            service.zone.runOutsideAngular(() => {
                while (service.incomingDataService.incomingDataArray.length > 0) {
                    //console.log("incoming Data Array: %O", service.incomingDataService.incomingSQLDataArray);
                    var signalRMessage = this.incomingDataService.incomingDataArray.pop();
                    //console.log("signalRMessage = %O", signalRMessage);
                    this.UpdateNewObservationFromSignalR(signalRMessage);
                }
            });
        }
    }

    public createSubjectAndSubscribe(configuration: any) {
        Global.User.DebugMode && console.log(this.serviceName + "<-- Creating and Subscribing to Active Subject -->");
        return new Promise((resolve, reject) => {
            this.activeSubjects.push({
                Id: configuration.Id,
                WidgetName: configuration.WidgetName,
                TagNamePrefix: configuration.TagNamePrefix,
                Subject$: new Subject<any>(),
            });
            resolve({
                Name: configuration.Id,
                WidgetName: configuration.WidgetName,
                TagNamePrefix: configuration.TagNamePrefix,
                Subject$: new Subject<any>(),
            });
        });
    }

    public createSqlListenerSubjectAndSubscribe(configuration: any) {
        Global.User.DebugMode && console.log(this.serviceName + "<-- Creating and Subscribing to SQL Listener Subject -->");
        return new Promise((resolve, reject) => {
            this.sqlListenerSubjects.push({
                Id: configuration.Id,
                cachedCollectionName: configuration.cachedCollectionName,
                sqlUpdateName: configuration.sqlUpdateName,
                Subject$: new Subject<any>(),
            });
            resolve({
                Name: configuration.Id,
                cachedCollectionName: configuration.cachedCollectionName,
                sqlUpdateName: configuration.sqlUpdateName,
                Subject$: new Subject<any>(),
            });
        });
    }

    public unsubscribeAndLeaveActiveSubjects(guid: string) {
        var activeSubject = this.activeSubjects.firstOrDefault((subject: ITagNamePrefixSubject) => {
            return subject.Id == guid;
        });
        activeSubject && activeSubject.Subject$.unsubscribe();

        this.activeSubjects = this.activeSubjects
            .where((subject: ITagNamePrefixSubject) => {
                return subject.Id != guid;
            })
            .toArray();
        Global.User.DebugMode && console.log(this.serviceName + "<-- Leaving Active Subject -->");
        Global.User.DebugMode && console.log(this.serviceName + "current active subjects: %O", this.activeSubjects);
    }

    public unsubscribeAndLeaveSqlListenerSubjects(guid: string) {
        var sqlListenerSubject = this.sqlListenerSubjects.firstOrDefault((subject: ISQLServerSubject) => {
            return subject.Id == guid;
        });
        sqlListenerSubject && sqlListenerSubject?.Subject$?.unsubscribe();

        this.sqlListenerSubjects = this.sqlListenerSubjects
            .where((subject: ISQLServerSubject) => {
                return subject.Id != guid;
            })
            .toArray();
        Global.User.DebugMode && console.log(this.serviceName + "<-- Leaving SQL Listener Subject -->");
        Global.User.DebugMode && console.log(this.serviceName + "current SQL Listener Subjects: %O", this.sqlListenerSubjects);
    }

    public returnCorrectActiveSubject(guid: string) {
        var activeSubject = this.activeSubjects.firstOrDefault((subject: ITagNamePrefixSubject) => {
            return subject.Id == guid;
        });
        return activeSubject;
    }

    public saveSearch(search: any) {
        this.savedSearches.push(search);
    }

    setThemeBackToDefaultSettings() {
        // Global.Theme = Global.DefaultThemeForApplication_AirportSiteUsers;
        // const body = document.getElementsByTagName("body")[0];
        // console.log("body.classList = %O", body.classList);
        // console.log(
        // 	"Global.Theme = " +
        // 		Global.Theme +
        // 		", Global.DefaultThemeForApplication = " +
        // 		Global.DefaultThemeForApplication
        // );
        // //-- PLEASE DO NOT COMMENT OUT THESE NEXT FIVE LINES. They are specifically related to insuring we're starting with the correct default theme for the site. --Kirk T. Sherer, August 24, 2022.
        // if (Global.Theme == "dark") {
        // 	body.classList.remove("white-content");
        // } else {
        // 	body.classList.add("white-content");
        // }
        // this.setTheme(Global.Theme);
        // console.log("Global = %O", Global);
    }

    setTheme(theme: string, updateFromSettings?: boolean) {
        // Global.User.DebugMode &&
        // 	console.log("toggleDarkLightTheme invoked... theme: " + theme);
        // //Global.User.DebugMode && console.log("darkTheme = " + darkTheme + ", this.darkTheme = " + this.darkTheme);
        // Global.Theme = theme; //-- we're sending either 'light' or 'dark' here. --Kirk T. Sherer, October 16, 2020.
        // Global.User.DebugMode && console.log("Global = %O", Global);
        // Global.User.DebugMode &&
        // 	console.log(
        // 		"sending out dataService.themeChanged$.next(" +
        // 			Global.Theme +
        // 			")..."
        // 	);
        // this.themeChanged$.next(Global.Theme);
        // this.setImagesByTheme(Global.Theme);
        // //-- next lines are necessary for Kendo UI to toggle dark and light theming correctly... --Kirk T. Sherer, September 6, 2020.
        // const body = document.getElementsByTagName("body")[0];
        // Global.User.DebugMode && console.log("body = %O", body);
        // if (Global.Theme == "dark") {
        // 	body.classList.remove("white-content");
        // 	this.darkTheme = true;
        // } else {
        // 	body.classList.add("white-content");
        // 	this.darkTheme = false;
        // }
        // Global.User.DebugMode &&
        // 	console.log("this.darkTheme = " + this.darkTheme);
        // if (updateFromSettings) {
        // 	this.SQLActionAsPromise(
        // 		"API.UserSettings_UpdateRecordByIdAndFieldName " +
        // 			Global.User.currentUser.Id +
        // 			", 'MobileOnlyDarkTheme', '" +
        // 			(Global.Theme == "dark" ? 1 : 0) +
        // 			"'"
        // 	).then((data: any) => {
        // 		Global.User.DebugMode &&
        // 			console.log(
        // 				"MobileOnlyDarkTheme updated in user settings. user record: %O",
        // 				data
        // 			);
        // 	});
        // }
        // this.companyLogo$.next(Global.CompanyLogoImage);
        // this.iopsLogo$.next(Global.iOPSLogoImage);
    }

    public initializeGlobalVariables(type: string, requestedUrl: string) {
        var service = this;
        var baseUrl = "";

        Global.Initial = {
            href: window.location.href, //-- need full href to determine if we can travel to this route later after the user has been authenticated.
            route: window.location.pathname == "/" ? "" : requestedUrl, //-- need the route so we can travel there after the user has been authenticated. --Kirk T. Sherer, November 17, 2023.
            baseUrl: window.location.origin,
        };

        if (type == "mobile") {
            if (document.URL.indexOf("localhost") > 0) {
                Global.Initial = {
                    href: "https://test.iopsmobile.com/", //-- need full href to determine if we can travel to this route later after the user has been authenticated.
                    route: window.location.pathname == "/" ? "" : requestedUrl, //-- need the route so we can travel there after the user has been authenticated. --Kirk T. Sherer, November 17, 2023.
                    baseUrl: "https://test.iopsmobile.com/",
                };
            }

            baseUrl = Global.Initial.baseUrl;

            if (baseUrl.indexOf("localhost") > 0) {
                Global.Initial = {
                    href: "https://test.iopsmobile.com/", //-- need full href to determine if we can travel to this route later after the user has been authenticated.
                    route: window.location.pathname == "/" ? "" : requestedUrl, //-- need the route so we can travel there after the user has been authenticated. --Kirk T. Sherer, November 17, 2023.
                    baseUrl: "https://test.iopsmobile.com/",
                };
            }

            baseUrl = Global.Initial.baseUrl;

            if (Global.iOPSLogoImage.indexOf("http") < 0) {
                //-- only set these images when we first come into the application.

                Global.LogosForApplicationTheme.Dark.iOPSLogo = baseUrl.replace("iopsmobile", "iopspro") + Global.LogosForApplicationTheme.Dark.iOPSLogo;
                Global.LogosForApplicationTheme.Dark.iOPSTinyLogo = baseUrl.replace("iopsmobile", "iopspro") + Global.LogosForApplicationTheme.Dark.iOPSTinyLogo;
                Global.LogosForApplicationTheme.Dark.CompanyTinyLogo = baseUrl.replace("iopsmobile", "iopspro") + Global.LogosForApplicationTheme.Dark.CompanyTinyLogo;
                Global.LogosForApplicationTheme.Dark.CompanyLogo = baseUrl.replace("iopsmobile", "iopspro") + Global.LogosForApplicationTheme.Dark.CompanyLogo;

                Global.LogosForApplicationTheme.Light.iOPSLogo = baseUrl.replace("iopsmobile", "iopspro") + Global.LogosForApplicationTheme.Light.iOPSLogo;
                Global.LogosForApplicationTheme.Light.iOPSTinyLogo = baseUrl.replace("iopsmobile", "iopspro") + Global.LogosForApplicationTheme.Light.iOPSTinyLogo;
                Global.LogosForApplicationTheme.Light.CompanyTinyLogo = baseUrl.replace("iopsmobile", "iopspro") + Global.LogosForApplicationTheme.Light.CompanyTinyLogo;
                Global.LogosForApplicationTheme.Light.CompanyLogo = baseUrl.replace("iopsmobile", "iopspro") + Global.LogosForApplicationTheme.Light.CompanyLogo;

                Global.iOPSLogoImage = baseUrl.replace("iopsmobile", "iopspro") + Global.iOPSLogoImage;
                Global.iOPSTinyLogoImage = baseUrl.replace("iopsmobile", "iopspro") + Global.iOPSTinyLogoImage;
                Global.CompanyTinyLogoImage = baseUrl.replace("iopsmobile", "iopspro") + Global.CompanyTinyLogoImage;
                Global.CompanyLogoImage = baseUrl.replace("iopsmobile", "iopspro") + Global.CompanyLogoImage;
                this.setThemeBackToDefaultSettings();

                Global.imagesUrl = baseUrl.replace("iopsmobile", "iopspro") + Global.imagesUrl;
                Global.isMobile = true;
                console.log("Global = %O", Global);
            }
        } else {
            if (document.URL.indexOf("localhost") > 0) {
                Global.Initial = {
                    href: "https://test.iopspro.com/", //-- need full href to determine if we can travel to this route later after the user has been authenticated.
                    route: window.location.pathname == "/" ? "" : window.location.pathname, //-- need the route so we can travel there after the user has been authenticated. --Kirk T. Sherer, November 17, 2023.
                    baseUrl: "https://test.iopspro.com/",
                };
            }

            if (Global.iOPSLogoImage.indexOf("http") < 0) {
                //-- only set these images when we first come into the application.
                Global.LogosForApplicationTheme.Dark.iOPSLogo = Global.Initial.baseUrl + Global.LogosForApplicationTheme.Dark.iOPSLogo;
                Global.LogosForApplicationTheme.Dark.iOPSTinyLogo = Global.Initial.baseUrl + Global.LogosForApplicationTheme.Dark.iOPSTinyLogo;
                Global.LogosForApplicationTheme.Dark.CompanyTinyLogo = Global.Initial.baseUrl + Global.LogosForApplicationTheme.Dark.CompanyTinyLogo;
                Global.LogosForApplicationTheme.Dark.CompanyLogo = Global.Initial.baseUrl + Global.LogosForApplicationTheme.Dark.CompanyLogo;

                Global.LogosForApplicationTheme.Light.iOPSLogo = Global.Initial.baseUrl + Global.LogosForApplicationTheme.Light.iOPSLogo;
                Global.LogosForApplicationTheme.Light.iOPSTinyLogo = Global.Initial.baseUrl + Global.LogosForApplicationTheme.Light.iOPSTinyLogo;
                Global.LogosForApplicationTheme.Light.CompanyTinyLogo = Global.Initial.baseUrl + Global.LogosForApplicationTheme.Light.CompanyTinyLogo;
                Global.LogosForApplicationTheme.Light.CompanyLogo = Global.Initial.baseUrl + Global.LogosForApplicationTheme.Light.CompanyLogo;

                Global.iOPSLogoImage = Global.Initial.baseUrl + Global.iOPSLogoImage;
                Global.iOPSTinyLogoImage = Global.Initial.baseUrl + Global.iOPSTinyLogoImage;
                Global.CompanyTinyLogoImage = Global.Initial.baseUrl + Global.CompanyTinyLogoImage;
                Global.CompanyLogoImage = Global.Initial.baseUrl + Global.CompanyLogoImage;
                this.setThemeBackToDefaultSettings();

                Global.imagesUrl = Global.Initial.baseUrl + Global.imagesUrl;
                Global.isMobile = false;
                console.log("Global = %O", Global);
            }
        }
    }

    getTacticalReport(name: string) {
        var tacticalReport = this.gateTacticalComponents.firstOrDefault((t: any) => {
            return t.path == name;
        });
        if (tacticalReport) {
            return tacticalReport;
        } else {
            return null;
        }
    }

    joinSignalRGroups() {
        //-- determine list of TagNamePrefixes based on the administrator's AdministratorOrganizationId or by the normal user's organization ID. --Kirk T. Sherer, May 16, 2025.
        console.log("data-service: joinSignalRGroups invoked...");
        var organizationId = Global.User.currentUser.IsSystemAdministrator ? Global.User.currentUser.AdministratorOrganizationId : Global.User.currentUser.Organization.Id;
        var listOfTagNamePrefixes = this.cache.assets
            .where((asset: any) => {
                return asset.OwnerOrganizationId == parseFloat(organizationId) || asset.OperatorOrganizationId == parseFloat(organizationId) || asset.MaintenanceOrganizationId == parseFloat(organizationId);
            })
            .select((asset: any) => {
                return asset.TagNamePrefix;
            })
            .distinct()
            .toArray()
            .join(",");
        console.log("data-service: current listOfTagNamePrefixes = %O", listOfTagNamePrefixes);

        if (Global.SignalR.ListOfTagNamePrefixes != undefined) {
            //-- must leave the current list of SignalR groups before joining the new ones from the newly selected organization. --Kirk T. Sherer, May 19, 2025.
            this.signalRCore.leaveCurrentTagNamePrefixGroups().then((result: boolean) => {
                console.log("data-service: leaveCurrentTagNamePrefixGroups result: %O", result);
                Global.SignalR.ListOfTagNamePrefixes = listOfTagNamePrefixes;
                this.timeInMSWeJoinedSignalRGroups = performance.now();
                console.log(this.serviceName + "SignalR joinGroups being instantiated...");
                this.signalRCore.joinGroups();
            });
        } else {
            //-- Don't have to un-join the SignalR groups since none are defined yet. --Kirk T. Sherer, May 19, 2025.
            Global.SignalR.ListOfTagNamePrefixes = listOfTagNamePrefixes;
            this.timeInMSWeJoinedSignalRGroups = performance.now();
            console.log(this.serviceName + "SignalR joinGroups being instantiated...");
            this.signalRCore.joinGroups();
        }
    }

    ProcessRecentlyUpdatedTags() {
        //console.log("this.updatedObservationsList = %O", this.updatedObservationsList);
        if (Global.FullDataCacheExists) {
            var newlyUpdatedObservations: Array<IObservation> = [];
            this.updatedObservationsList = this.updatedObservationsList
                .where((obs: IObservation) => {
                    var currentDateTimeInMS = Date.now();
                    if (obs.LocalUpdateDateUTCMS) {
                        var difference = currentDateTimeInMS - obs.LocalUpdateDateUTCMS;
                        //console.log("currentDateTimeInMS = " + currentDateTimeInMS + ", tag.LocalUpdateDateUTCMS = " + tag.LocalUpdateDateUTCMS + ", difference = " + difference);
                        var stale = currentDateTimeInMS - obs.LocalUpdateDateUTCMS > 2000;
                        if (stale) {
                            obs.RecentlyUpdated = false; //-- this updates the actual tagsObject.
                            newlyUpdatedObservations.push(obs); //-- this is the list of tags that have been updated in the cache.
                            return false; //-- this removes it from the updatedObservationsList.
                        }
                        return true; //-- this keeps it on the list.
                    } else {
                        obs.LocalUpdateDateUTCMS = Date.now();
                        return true; //-- keep it on the list for now since LocalUpdateDateUTCMS is null.
                    }
                })
                .toArray();

            if (newlyUpdatedObservations.length > 0) {
                this.batchUpdateObservations(newlyUpdatedObservations); //-- batch update the tags in the cache so that we can update the UI with the new values. --Kirk T. Sherer, July 26, 2025.
            }
        }
    }

    GetTagsFromWebAPI(orgId: number): Promise<any> {
        const headers = new HttpHeaders({
            "Content-Type": "text/plain",
            Authorization: Global.User.currentUser?.AuthenticationTokenFromUserRecord,
        });

        const httpOptions: {
            headers: HttpHeaders;
            observe: "response";
            responseType: "text";
        } = {
            headers,
            observe: "response",
            responseType: "text",
        };

        const url = `${Global.Data.dataServerUrl}/TagsByOrgIdCondensed?orgId=${orgId}`;

        return this.http
            .get(url, httpOptions)
            .toPromise()
            .then((response: any) => {
                if (response && response.body) {
                    //console.log("GetTagsFromWebAPI response.body: %O", response.body);
                    return response.body;
                }
                return response;
            });
    }

    async FormatTagsFromWebAPI(tagRecordBlob: string): Promise<boolean> {
        return new Promise<boolean>((resolve, reject) => {
            this.zone.runOutsideAngular(() => {
                console.log(this.serviceName + "FormatTagsFromWebAPI invoked.");
                const startTimer = performance.now();

                try {
                    if (typeof (tagRecordBlob as any).split !== "function") {
                        console.log(this.serviceName + "tagRecordBlob can't be split. tagRecordBlob = %O", tagRecordBlob);
                        return resolve(false);
                    }

                    const tagRecords = tagRecordBlob.split(/\r?\n/).filter((line) => line.trim().length > 0);
                    if (tagRecords.length === 0) {
                        console.log("No tag records found in the response. tagRecords = %O", tagRecords);
                        return resolve(false);
                    }

                    // Parse records
                    const parsedRecords = tagRecords
                        .select((msg: string) =>

                        this.BuildStandardCacheTagObjectFromDatabaseFields(
                            this.ParseDStringIntoDiscreetFields(msg)
                        )

                        )
                        .toArray();

                    let endTimer = performance.now();
                    console.log(this.serviceName + "Time to parse tags: " + (endTimer - startTimer) + "ms...");

                    // Place parsed records into signals and cache maps
                    const tagsStartTimer = performance.now();
                    const observationsToProcess: IObservation[] = [];
                    const tagMap = new Map<string, WritableSignal<ITag>>();
                    const obsMap = new Map<string, IObservation>();

                    this.cache.tags = [];

                    for (const tag of parsedRecords) {
                        if (!tag) continue;

                        tagMap.set(tag.Id.toString(), signal(tag));
                        this.cache.tagsObject[tag.Id] = tag;
                        this.cache.tags.push(tag);

                        if (tag.LastObs) {
                            obsMap.set(tag.Id.toString(), tag.LastObs);
                            this.cache.observationsObject[tag.Id] = tag.LastObs;
                            observationsToProcess.push(tag.LastObs);
                        }
                    }

                    this.tagCacheService.tagMap = tagMap;
                    this.tagCacheService.obsMap = obsMap;
                    this.tagCacheService.assetsObject = this.cache.assetsObject;

                    observationsToProcess.forEach((obs) => {
                        const updatedTag = this.cache.tagsObject[obs.TagId];
                        this.tagCacheService.ingestTag(obs, updatedTag);
                    });

                    this.tagCacheService.initializeIndexes = false;
                    // Important: many TagCacheService computeds are created before the full cache is loaded.
                    // Since `tagMap`/`obsMap` are replaced wholesale above (non-reactive), force a recompute so
                    // signals like getAlarmTagsForOrganization() "attach" to the newly-loaded tag signals.
                    this.tagCacheService.notifyTagMapReplaced('FormatTagsFromWebAPI initial load');

                    endTimer = performance.now();
                    console.log(this.serviceName + "Time to set up cache.tags array: " + (endTimer - tagsStartTimer) + "ms...");
                    console.log("✅ All records processed.");

                    this.totalTimeToLoadTags += endTimer - startTimer;
                    console.log(this.serviceName + "Total time to load and format tags: " + this.totalTimeToLoadTags + "ms...");

                    console.log(this.serviceName + "Now processing Asset States...");
                    const buildAssetStatesStartTimer = performance.now();
                    this.buildAssetStates();
                    endTimer = performance.now();
                    console.log(this.serviceName + "Current total time to build Asset States: " + (endTimer - buildAssetStatesStartTimer) + "ms...");

                    // ✅ Mark cache as ready and notify subscribers
                    this.fullCache$.next(this.cache);
                    this.markCacheReady('FormatTagsFromWebAPI finished');
                    resolve(true);
                } catch (err) {
                    console.error(this.serviceName + "FormatTagsFromWebAPI error:", err);
                    reject(err);
                }
            });
        });
    }

    buildAssetStates() {
        this.zone.runOutsideAngular(() => {
            this.cache.assets.forEach((asset: IAsset) => {
                this.formatAssetCurrentStatusFromTagRecords(asset);
            });
            //console.log(this.serviceName + "Finished with Asset Current State for each asset. assets = %O", this.cache.assets);
        });
    }

    GetAsset_RunTimeAndCycleCount(siteId: any, startDateMS: any, endDateMS: any) {
        var data$: any = new Observable((subscriber) => {
            var object = this.cache.sitesObject[siteId];
            let orgId = Global.User.currentUser.SelectedOrganizationId;
            if (object === undefined && !_.isNil(orgId)) {
                var object = this.cache.organizationsObject[orgId];
      };

      if (object.TacticalReports != null &&
        object.TacticalReports.TacticalEquipmentCycleCount != null &&
        siteId == object.TacticalReports.TacticalEquipmentCycleCount.siteId &&
        orgId == object.TacticalReports.TacticalEquipmentCycleCount.orgId &&
        startDateMS == object.TacticalReports.TacticalEquipmentCycleCount.startDateMS &&
        endDateMS == object.TacticalReports.TacticalEquipmentCycleCount.endDateMS) {
                let data = object.TacticalReports.TacticalEquipmentCycleCount.data;
                Global.User.DebugMode && console.log("GetAsset_RunTimeAndCycleCount from cache", data);

                subscriber.next(data);
                subscriber.complete();
            } else {
                // if its not in the cache then get from SQL
                let statement = "API.TacticalDashboard_GS_Asset_RunTimeAndCycleCount @accessToken= '" + Global.User.currentUser.AuthenticationTokenFromUserRecord + "'";

                if (!_.isNil(siteId) && siteId != 0) {
                    statement = statement + ", @siteIdList = '" + siteId + "'";
        }
        else if (!_.isNil(orgId)) {
          statement = statement + ", @siteIdList = '" + this.cache.sites.select((site: ISite) => { return site.Id }).toArray().join(",") + "'";
                }

                statement = statement + ", @startDateTimeMS=" + startDateMS + ", @endDateTimeMS= " + endDateMS;

                this.SQLActionAsPromise(statement).then((data: any) => {
                    Global.User.DebugMode && console.log("GetAsset_RunTimeAndCycleCount from sql", data);

                    //save to cache
                    var TacticalEquipmentCycleCount: any = {
                        siteId: siteId,
                        orgId: orgId,
                        startDateMS: startDateMS,
                        endDateMS: endDateMS,
                        data: data,
                    };

                    if (object.TacticalReports == null) {
                        var TacticalReports: any = {
                            TacticalEquipmentCycleCount: TacticalEquipmentCycleCount,
                        };
                        object.TacticalReports = TacticalReports;
                    } else {
                        object.TacticalReports.TacticalEquipmentCycleCount = TacticalEquipmentCycleCount;
                    }

                    subscriber.next(data);
                    subscriber.complete();
                });
            }
        });
        return data$;
    }

    GetSiteTactical_GS_Alarms_Detail_FilterByAsset(siteId: any, startDateMS: any, endDateMS: any) {
        var data$: any = new Observable((subscriber) => {
            var object = this.cache.sitesObject[siteId];
            let orgId = Global.User.currentUser.SelectedOrganizationId;
            if (object === undefined && !_.isNil(orgId)) {
                var object = this.cache.organizationsObject[orgId];
            }


      if (object.TacticalReports != null && object.TacticalReports.TacticalGSAlarmsDetail_FilterByAsset != null &&
        siteId == object.TacticalReports.TacticalGSAlarmsDetail_FilterByAsset.siteId &&
        orgId == object.TacticalReports.TacticalGSAlarmsDetail_FilterByAsset.orgId &&
        startDateMS == object.TacticalReports.TacticalGSAlarmsDetail_FilterByAsset.startDateMS &&
        endDateMS == object.TacticalReports.TacticalGSAlarmsDetail_FilterByAsset.endDateMS) {
                let data = object.TacticalReports.TacticalGSAlarmsDetail_FilterByAsset.data;
                Global.User.DebugMode && console.log("GetSiteTactical_GS_Alarms_Detail_FilterByAsset from cache", data);

                subscriber.next(data);
                subscriber.complete();
            } else {


        let statement = 'API.TacticalDashboard_GS_Alarms_Detail_FilterByAsset ' +
          '@startDateTimeMS=' +
          startDateMS +
          ', @endDateTimeMS= ' +
          endDateMS +
          " , @accessToken='" +
          Global.User.currentUser.AuthenticationTokenFromUserRecord +
          "'";
                if (!_.isNil(siteId) && siteId != 0) {
                    statement = statement + ", @siteIdList = '" + siteId + "'";
        }
        else if (!_.isNil(orgId)) {
          statement = statement + ", @siteIdList = '" + this.cache.sites.select((site: ISite) => { return site.Id }).toArray().join(",") + "'";
                }

                this.SQLActionAsPromise(statement).then((data: any) => {
                    if (data && data.length > 0) {
                        data.forEach((record: any) => {
              record.DateOfObservationDateObject = new Date(
                record.DateOfObservation
              );
              record.ProperAssetName = record.SiteName + ' ' + record.GateName + ' ' + record.AssetName;
              record.DurationInMinutes = (
                record.DurationInSeconds / 60
              ).toFixed(2);
                            record.count = 1;
                        });


                    }

                    //save to cache
                    var TacticalGSAlarmsDetail_FilterByAsset: any = {
                        siteId: siteId,
                        orgId: orgId,
                        startDateMS: startDateMS,
                        endDateMS: endDateMS,
                        data: data,
                    };

                    if (object.TacticalReports == null) {
                        var TacticalReports: any = {
                            TacticalGSAlarmsDetail_FilterByAsset: TacticalGSAlarmsDetail_FilterByAsset,
                        };
                        object.TacticalReports = TacticalReports;
                    } else {
                        object.TacticalReports.TacticalGSAlarmsDetail_FilterByAsset = TacticalGSAlarmsDetail_FilterByAsset;
                    }

                    subscriber.next(data);
                    subscriber.complete();

        })

            }
    })
    return data$
    }


    GetAssetTactical_GS_Alarms_Detail_FilterByAsset(siteId: any, assetId: any, startDateMS: any, endDateMS: any) {
        var data$: any = new Observable((subscriber) => {
      var assetObject = this.cache.assetsObject[assetId]

      if (assetObject.TacticalReports != null && assetObject.TacticalReports.TacticalGSAlarmsDetail_FilterByAsset != null &&
        assetId == assetObject.TacticalReports.TacticalGSAlarmsDetail_FilterByAsset.assetId &&
        startDateMS == assetObject.TacticalReports.TacticalGSAlarmsDetail_FilterByAsset.startDateMS &&
        endDateMS == assetObject.TacticalReports.TacticalGSAlarmsDetail_FilterByAsset.endDateMS) {
                let data = assetObject.TacticalReports.TacticalGSAlarmsDetail_FilterByAsset.data;
                Global.User.DebugMode && console.log("GetAssetTactical_GS_Alarms_Detail_FilterByAsset from cache", data);

                subscriber.next(data);
                subscriber.complete();
            } else {


        let statement = 'API.TacticalDashboard_GS_Alarms_Detail_FilterByAsset ' +
          '@startDateTimeMS=' +
          startDateMS +
          ', @endDateTimeMS= ' +
          endDateMS +
          " , @accessToken='" +
          Global.User.currentUser.AuthenticationTokenFromUserRecord +
          "'";
                if (!_.isNil(siteId) && siteId != 0) {
                    statement = statement + ", @siteIdList = '" + siteId + "'";
                }
                if (!_.isNil(assetId)) {
                    statement = statement + ", @assetId = '" + assetId + "'";
                }
                this.SQLActionAsPromise(statement).then((data: any) => {
                    if (data && data.length > 0) {
                        data.forEach((record: any) => {
              record.DateOfObservationDateObject = new Date(
                record.DateOfObservation
              );
              record.ProperAssetName = record.SiteName + ' ' + record.GateName + ' ' + record.AssetName;
              record.DurationInMinutes = (
                record.DurationInSeconds / 60
              ).toFixed(2);
                            record.count = 1;
                        });


                    }

                    //save to cache
                    var TacticalGSAlarmsDetail_FilterByAsset: any = {
                        assetId: assetId,
                        startDateMS: startDateMS,
                        endDateMS: endDateMS,
                        data: data,
                    };

                    if (assetObject.TacticalReports == null) {
                        var TacticalReports: any = {
                            TacticalGSAlarmsDetail_FilterByAsset: TacticalGSAlarmsDetail_FilterByAsset,
                        };
                        assetObject.TacticalReports = TacticalReports;
                    } else {
                        assetObject.TacticalReports.TacticalGSAlarmsDetail_FilterByAsset = TacticalGSAlarmsDetail_FilterByAsset;
                    }

                    subscriber.next(data);
                    subscriber.complete();

        })

            }
    })
    return data$
    }


    GetSiteTactical_GSE_Alarms_Detail(siteId: any, startDateMS: any, endDateMS: any) {
        var data$: any = new Observable((subscriber) => {
            var object = this.cache.sitesObject[siteId];
            let orgId = Global.User.currentUser.SelectedOrganizationId;
            if (object === undefined && !_.isNil(orgId)) {
                var object = this.cache.organizationsObject[orgId];
            }

      if (object.TacticalReports != null && object.TacticalReports.TacticalGSEAlarmsDetail != null &&
        siteId == object.TacticalReports.TacticalGSEAlarmsDetail.siteId &&
        orgId == object.TacticalReports.TacticalGSEAlarmsDetail.orgId &&
        startDateMS == object.TacticalReports.TacticalGSEAlarmsDetail.startDateMS &&
        endDateMS == object.TacticalReports.TacticalGSEAlarmsDetail.endDateMS) {
                let data = object.TacticalReports.TacticalGSEAlarmsDetail.data;
                Global.User.DebugMode && console.log("GetSiteTactical_GSE_Alarms_Detail_FilterByAsset from cache", data);

                subscriber.next(data);
                subscriber.complete();
            } else {


        let statement = 'API.TacticalDashboard_GSE_Alarms_Detail_FilterByAsset ' +
          '@startDateTimeMS=' +
          startDateMS +
          ', @endDateTimeMS= ' +
          endDateMS +
          " , @accessToken='" +
          Global.User.currentUser.AuthenticationTokenFromUserRecord +
          "'";

                if (!_.isNil(siteId) && siteId != 0) {
                    statement = statement + ", @siteIdList = '" + siteId + "'";
        }
        else if (!_.isNil(orgId)) {
          statement = statement + ", @siteIdList = '" + this.cache.sites.select((site: ISite) => { return site.Id }).toArray().join(",") + "'";
                }

                this.SQLActionAsPromise(statement).then((data: any) => {

                    //save to cache
                    var TacticalGSEAlarmsDetail: any = {
                        siteId: siteId,
                        orgId: orgId,
                        startDateMS: startDateMS,
                        endDateMS: endDateMS,
                        data: data,
                    };

                    if (object.TacticalReports == null) {
                        var TacticalReports: any = {
                            TacticalGSEAlarmsDetail: TacticalGSEAlarmsDetail,
                        };
                        object.TacticalReports = TacticalReports;
                    } else {
                        object.TacticalReports.TacticalGSEAlarmsDetail = TacticalGSEAlarmsDetail;
                    }

                    subscriber.next(data);
                    subscriber.complete();

        })

            }
    })
    return data$
    }


    GetAssetTactical_GSE_Alarms_Detail(siteId: any, assetId: any, startDateMS: any, endDateMS: any) {
        var data$: any = new Observable((subscriber) => {
      var assetObject = this.cache.assetsObject[assetId]

      if (assetObject.TacticalReports != null && assetObject.TacticalReports.TacticalGSEAlarmsDetail != null &&
        assetId == assetObject.TacticalReports.TacticalGSEAlarmsDetail.assetId &&
        startDateMS == assetObject.TacticalReports.TacticalGSEAlarmsDetail.startDateMS &&
        endDateMS == assetObject.TacticalReports.TacticalGSEAlarmsDetail.endDateMS) {
                let data = assetObject.TacticalReports.TacticalGSEAlarmsDetail.data;
                Global.User.DebugMode && console.log("GetSiteTactical_GSE_Alarms_Detail_FilterByAsset from cache", data);

                subscriber.next(data);
                subscriber.complete();
            } else {


        let statement = 'API.TacticalDashboard_GSE_Alarms_Detail_FilterByAsset ' +
          '@startDateTimeMS=' +
          startDateMS +
          ', @endDateTimeMS= ' +
          endDateMS +
          " , @accessToken='" +
          Global.User.currentUser.AuthenticationTokenFromUserRecord +
          "'";
                if (!_.isNil(siteId) && siteId != 0) {
                    statement = statement + ", @siteIdList = '" + siteId + "'";
                }
                if (!_.isNil(assetId)) {
                    statement = statement + ", @assetId = '" + assetId + "'";
                }
                this.SQLActionAsPromise(statement).then((data: any) => {

                    //save to cache
                    var TacticalGSEAlarmsDetail: any = {
                        assetId: assetId,
                        startDateMS: startDateMS,
                        endDateMS: endDateMS,
                        data: data,
                    };

                    if (assetObject.TacticalReports == null) {
                        var TacticalReports: any = {
                            TacticalGSEAlarmsDetail: TacticalGSEAlarmsDetail,
                        };
                        assetObject.TacticalReports = TacticalReports;
                    } else {
                        assetObject.TacticalReports.TacticalGSEAlarmsDetail = TacticalGSEAlarmsDetail;
                    }

                    subscriber.next(data);
                    subscriber.complete();

        })

            }
    })
    return data$
    }


    GetTacticalDashboard_GSE_Equipment_NotUsed(siteId: any, startDateMS: any, endDateMS: any) {
        var data$: any = new Observable((subscriber) => {
            var object = this.cache.sitesObject[siteId];
            let orgId = Global.User.currentUser.SelectedOrganizationId;
            if (object === undefined && !_.isNil(orgId)) {
                var object = this.cache.organizationsObject[orgId];
            }

      if (object.TacticalReports != null && object.TacticalReports.TacticalGSEEquipmentNotUsed != null &&
        siteId == object.TacticalReports.TacticalGSEEquipmentNotUsed.siteId &&
        orgId == object.TacticalReports.TacticalGSEEquipmentNotUsed.orgId &&
        startDateMS == object.TacticalReports.TacticalGSEEquipmentNotUsed.startDateMS &&
        endDateMS == object.TacticalReports.TacticalGSEEquipmentNotUsed.endDateMS) {
                let data = object.TacticalReports.TacticalGSEEquipmentNotUsed.data;
                Global.User.DebugMode && console.log("GetTacticalDashboard_GSE_Equipment_NotUsed from cache", data);

                subscriber.next(data);
                subscriber.complete();
            } else {


        let statement = 'API.TacticalDashboard_GSE_Equipment_NotUsed ' +
          '@startDateTimeMS=' +
          startDateMS +
          ', @endDateTimeMS= ' +
          endDateMS +
          " , @accessToken='" +
          Global.User.currentUser.AuthenticationTokenFromUserRecord +
          "'";
                if (!_.isNil(siteId) && siteId != 0) {
                    statement = statement + ", @siteIdList = '" + siteId + "'";
        }
        else if (!_.isNil(orgId)) {
          statement = statement + ", @siteIdList = '" + this.cache.sites.select((site: ISite) => { return site.Id }).toArray().join(",") + "'";
                }

                this.SQLActionAsPromise(statement).then((data: any) => {

                    //save to cache
                    var TacticalGSEEquipmentNotUsed: any = {
                        siteId: siteId,
                        orgId: orgId,
                        startDateMS: startDateMS,
                        endDateMS: endDateMS,
                        data: data,
                    };

                    if (object.TacticalReports == null) {
                        var TacticalReports: any = {
                            TacticalGSEEquipmentNotUsed: TacticalGSEEquipmentNotUsed,
                        };
                        object.TacticalReports = TacticalReports;
                    } else {
                        object.TacticalReports.TacticalGSEEquipmentNotUsed = TacticalGSEEquipmentNotUsed;
                    }

                    subscriber.next(data);
                    subscriber.complete();

        })

            }
    })
    return data$
    }

    GetTacticalDashboard_Asset_NotUsed_Duration(siteId: any, startDateMS: any, endDateMS: any) {
        var data$: any = new Observable((subscriber) => {
            var object = this.cache.sitesObject[siteId];
            let orgId = Global.User.currentUser.SelectedOrganizationId;
            if (object === undefined && !_.isNil(orgId)) {
                var object = this.cache.organizationsObject[orgId];
            }

      if (object.TacticalReports != null && object.TacticalReports.TacticalGSEAssetNotUsedDuration != null &&
        siteId == object.TacticalReports.TacticalGSEAssetNotUsedDuration.siteId &&
        orgId == object.TacticalReports.TacticalGSEAssetNotUsedDuration.orgId &&
        startDateMS == object.TacticalReports.TacticalGSEAssetNotUsedDuration.startDateMS &&
        endDateMS == object.TacticalReports.TacticalGSEAssetNotUsedDuration.endDateMS) {
                let data = object.TacticalReports.TacticalGSEAssetNotUsedDuration.data;
                Global.User.DebugMode && console.log("GetTacticalDashboard_Asset_NotUsed_Duration from cache", data);

                subscriber.next(data);
                subscriber.complete();
            } else {


        let statement = 'API.TacticalDashboard_Asset_NotUsed_Duration ' +
          '@startDateTimeMS=' +
          startDateMS +
          ', @endDateTimeMS= ' +
          endDateMS +
          " , @accessToken='" +
          Global.User.currentUser.AuthenticationTokenFromUserRecord +
          "'";
                if (!_.isNil(siteId) && siteId != 0) {
                    statement = statement + ", @siteIdList = '" + siteId + "'";
        }
        else if (!_.isNil(orgId)) {
          statement = statement + ", @siteIdList = '" + this.cache.sites.select((site: ISite) => { return site.Id }).toArray().join(",") + "'";
                }

                this.SQLActionAsPromise(statement).then((data: any) => {

                    //save to cache
                    var TacticalGSEAssetNotUsedDuration: any = {
                        siteId: siteId,
                        orgId: orgId,
                        startDateMS: startDateMS,
                        endDateMS: endDateMS,
                        data: data,
                    };

                    if (object.TacticalReports == null) {
                        var TacticalReports: any = {
                            TacticalGSEAssetNotUsedDuration: TacticalGSEAssetNotUsedDuration,
                        };
                        object.TacticalReports = TacticalReports;
                    } else {
                        object.TacticalReports.TacticalGSEAssetNotUsedDuration = TacticalGSEAssetNotUsedDuration;
                    }

                    subscriber.next(data);
                    subscriber.complete();

        })

            }
    })
    return data$
    }

    GetTacticalDashboard_EachDayCountDuration_Used(siteId: any, startDateMS: any, endDateMS: any) {
        var data$: any = new Observable((subscriber) => {
            var object = this.cache.sitesObject[siteId];
            let orgId = Global.User.currentUser.SelectedOrganizationId;
            if (object === undefined && !_.isNil(orgId)) {
                var object = this.cache.organizationsObject[orgId];
            }

      if (object.TacticalReports != null && object.TacticalReports.TacticalGSEEachDayCountDurationUsed != null &&
        siteId == object.TacticalReports.TacticalGSEEachDayCountDurationUsed.siteId &&
        orgId == object.TacticalReports.TacticalGSEEachDayCountDurationUsed.orgId &&
        startDateMS == object.TacticalReports.TacticalGSEEachDayCountDurationUsed.startDateMS &&
        endDateMS == object.TacticalReports.TacticalGSEEachDayCountDurationUsed.endDateMS) {
                let data = object.TacticalReports.TacticalGSEEachDayCountDurationUsed.data;
                Global.User.DebugMode && console.log("GetTacticalDashboard_EachDayCountDuration_Used from cache", data);

                subscriber.next(data);
                subscriber.complete();
            } else {


        let statement = 'API.TacticalDashboard_Equipment_Used_EachDayCount_Duration ' +
          '@startDateTimeMS=' +
          startDateMS +
          ', @endDateTimeMS= ' +
          endDateMS +
          " , @accessToken='" +
          Global.User.currentUser.AuthenticationTokenFromUserRecord +
          "'";
                if (!_.isNil(siteId) && siteId != 0) {
                    statement = statement + ", @siteIdList = '" + siteId + "'";
        }
        else if (!_.isNil(orgId)) {
          statement = statement + ", @siteIdList = '" + this.cache.sites.select((site: ISite) => { return site.Id }).toArray().join(",") + "'";
                }

                this.SQLActionAsPromise(statement).then((data: any) => {

                    //save to cache
                    var TacticalGSEEachDayCountDurationUsed: any = {
                        siteId: siteId,
                        orgId: orgId,
                        startDateMS: startDateMS,
                        endDateMS: endDateMS,
                        data: data,
                    };

                    if (object.TacticalReports == null) {
                        var TacticalReports: any = {
                            TacticalGSEEachDayCountDurationUsed: TacticalGSEEachDayCountDurationUsed,
                        };
                        object.TacticalReports = TacticalReports;
                    } else {
                        object.TacticalReports.TacticalGSEEachDayCountDurationUsed = TacticalGSEEachDayCountDurationUsed;
                    }

                    subscriber.next(data);
                    subscriber.complete();

        })

            }
    })
    return data$
    }

    GetTacticalDashboard_EachDayCountDuration_Driven(siteId: any, startDateMS: any, endDateMS: any) {
        var data$: any = new Observable((subscriber) => {
            var object = this.cache.sitesObject[siteId];
            let orgId = Global.User.currentUser.SelectedOrganizationId;
            if (object === undefined && !_.isNil(orgId)) {
                var object = this.cache.organizationsObject[orgId];
            }

      if (object.TacticalReports != null && object.TacticalReports.TacticalGSEEachDayCountDurationDriven != null &&
        siteId == object.TacticalReports.TacticalGSEEachDayCountDurationDriven.siteId &&
        orgId == object.TacticalReports.TacticalGSEEachDayCountDurationDriven.orgId &&
        startDateMS == object.TacticalReports.TacticalGSEEachDayCountDurationDriven.startDateMS &&
        endDateMS == object.TacticalReports.TacticalGSEEachDayCountDurationDriven.endDateMS) {
                let data = object.TacticalReports.TacticalGSEEachDayCountDurationDriven.data;
                Global.User.DebugMode && console.log("GetTacticalDashboard_EachDayCountDuration_Driven from cache", data);

                subscriber.next(data);
                subscriber.complete();
            } else {


        let statement = 'API.TacticalDashboard_Equipment_Driven_EachDayCount_Duration ' +
          '@startDateTimeMS=' +
          startDateMS +
          ', @endDateTimeMS= ' +
          endDateMS +
          " , @accessToken='" +
          Global.User.currentUser.AuthenticationTokenFromUserRecord +
          "'";
                if (!_.isNil(siteId) && siteId != 0) {
                    statement = statement + ", @siteIdList = '" + siteId + "'";
        }
        else if (!_.isNil(orgId)) {
          statement = statement + ", @siteIdList = '" + this.cache.sites.select((site: ISite) => { return site.Id }).toArray().join(",") + "'";
                }

                this.SQLActionAsPromise(statement).then((data: any) => {

                    //save to cache
                    var TacticalGSEEachDayCountDurationDriven: any = {
                        siteId: siteId,
                        orgId: orgId,
                        startDateMS: startDateMS,
                        endDateMS: endDateMS,
                        data: data,
                    };

                    if (object.TacticalReports == null) {
                        var TacticalReports: any = {
                            TacticalGSEEachDayCountDurationDriven: TacticalGSEEachDayCountDurationDriven,
                        };
                        object.TacticalReports = TacticalReports;
                    } else {
                        object.TacticalReports.TacticalGSEEachDayCountDurationDriven = TacticalGSEEachDayCountDurationDriven;
                    }

                    subscriber.next(data);
                    subscriber.complete();

        })

            }
    })
    return data$
    }

    GetTacticalDashboard_EachDayCountDuration_Idle(siteId: any, startDateMS: any, endDateMS: any) {
        var data$: any = new Observable((subscriber) => {
            var object = this.cache.sitesObject[siteId];
            let orgId = Global.User.currentUser.SelectedOrganizationId;
            if (object === undefined && !_.isNil(orgId)) {
                var object = this.cache.organizationsObject[orgId];
            }

      if (object.TacticalReports != null && object.TacticalReports.TacticalGSEEachDayCountDurationIdle != null &&
        siteId == object.TacticalReports.TacticalGSEEachDayCountDurationIdle.siteId &&
        orgId == object.TacticalReports.TacticalGSEEachDayCountDurationIdle.orgId &&
        startDateMS == object.TacticalReports.TacticalGSEEachDayCountDurationIdle.startDateMS &&
        endDateMS == object.TacticalReports.TacticalGSEEachDayCountDurationIdle.endDateMS) {
                let data = object.TacticalReports.TacticalGSEEachDayCountDurationIdle.data;
                Global.User.DebugMode && console.log("GetTacticalDashboard_EachDayCountDuration_Idle from cache", data);

                subscriber.next(data);
                subscriber.complete();
            } else {


        let statement = 'API.TacticalDashboard_Equipment_Idle_EachDayCount_Duration ' +
          '@startDateTimeMS=' +
          startDateMS +
          ', @endDateTimeMS= ' +
          endDateMS +
          " , @accessToken='" +
          Global.User.currentUser.AuthenticationTokenFromUserRecord +
          "'";
                if (!_.isNil(siteId) && siteId != 0) {
                    statement = statement + ", @siteIdList = '" + siteId + "'";
        }
        else if (!_.isNil(orgId)) {
          statement = statement + ", @siteIdList = '" + this.cache.sites.select((site: ISite) => { return site.Id }).toArray().join(",") + "'";
                }

                this.SQLActionAsPromise(statement).then((data: any) => {

                    //save to cache
                    var TacticalGSEEachDayCountDurationIdle: any = {
                        siteId: siteId,
                        orgId: orgId,
                        startDateMS: startDateMS,
                        endDateMS: endDateMS,
                        data: data,
                    };

                    if (object.TacticalReports == null) {
                        var TacticalReports: any = {
                            TacticalGSEEachDayCountDurationIdle: TacticalGSEEachDayCountDurationIdle,
                        };
                        object.TacticalReports = TacticalReports;
                    } else {
                        object.TacticalReports.TacticalGSEEachDayCountDurationIdle = TacticalGSEEachDayCountDurationIdle;
                    }

                    subscriber.next(data);
                    subscriber.complete();

        })

            }
    })
    return data$
    }

    GetTacticalDashboard_Asset_Idle_Detail(siteId: any, startDateMS: any, endDateMS: any) {
        var data$: any = new Observable((subscriber) => {
            var object = this.cache.sitesObject[siteId];
            let orgId = Global.User.currentUser.SelectedOrganizationId;
            if (object === undefined && !_.isNil(orgId)) {
                var object = this.cache.organizationsObject[orgId];
            }

      if (object.TacticalReports != null && object.TacticalReports.TacticalGSEAssetIdleDetail != null &&
        siteId == object.TacticalReports.TacticalGSEAssetIdleDetail.siteId &&
        orgId == object.TacticalReports.TacticalGSEAssetIdleDetail.orgId &&
        startDateMS == object.TacticalReports.TacticalGSEAssetIdleDetail.startDateMS &&
        endDateMS == object.TacticalReports.TacticalGSEAssetIdleDetail.endDateMS) {
                let data = object.TacticalReports.TacticalGSEAssetIdleDetail.data;
                Global.User.DebugMode && console.log("GetTacticalDashboard_Asset_Idle_Detail from cache", data);

                subscriber.next(data);
                subscriber.complete();
            } else {


        let statement = 'API.TacticalDashboard_Asset_Idle_Detail ' +
          '@startDateTimeMS=' +
          startDateMS +
          ', @endDateTimeMS= ' +
          endDateMS +
          " , @accessToken='" +
          Global.User.currentUser.AuthenticationTokenFromUserRecord +
          "'";
                if (!_.isNil(siteId) && siteId != 0) {
                    statement = statement + ", @siteIdList = '" + siteId + "'";
        }
        else if (!_.isNil(orgId)) {
          statement = statement + ", @siteIdList = '" + this.cache.sites.select((site: ISite) => { return site.Id }).toArray().join(",") + "'";
                }

                this.SQLActionAsPromise(statement).then((data: any) => {

                    //save to cache
                    var TacticalGSEAssetIdleDetail: any = {
                        siteId: siteId,
                        orgId: orgId,
                        startDateMS: startDateMS,
                        endDateMS: endDateMS,
                        data: data,
                    };

                    if (object.TacticalReports == null) {
                        var TacticalReports: any = {
                            TacticalGSEAssetIdleDetail: TacticalGSEAssetIdleDetail,
                        };
                        object.TacticalReports = TacticalReports;
                    } else {
                        object.TacticalReports.TacticalGSEAssetIdleDetail = TacticalGSEAssetIdleDetail;
                    }

                    subscriber.next(data);
                    subscriber.complete();

        })

            }
    })
    return data$
    }

    GetTacticalDashboard_Asset_Driven_Detail(siteId: any, startDateMS: any, endDateMS: any) {
        var data$: any = new Observable((subscriber) => {
            var object = this.cache.sitesObject[siteId];
            let orgId = Global.User.currentUser.SelectedOrganizationId;
            if (object === undefined && !_.isNil(orgId)) {
                var object = this.cache.organizationsObject[orgId];
            }

      if (object.TacticalReports != null && object.TacticalReports.TacticalGSEAssetDrivenDetail != null &&
        siteId == object.TacticalReports.TacticalGSEAssetDrivenDetail.siteId &&
        orgId == object.TacticalReports.TacticalGSEAssetDrivenDetail.orgId &&
        startDateMS == object.TacticalReports.TacticalGSEAssetDrivenDetail.startDateMS &&
        endDateMS == object.TacticalReports.TacticalGSEAssetDrivenDetail.endDateMS) {
                let data = object.TacticalReports.TacticalGSEAssetDrivenDetail.data;
                Global.User.DebugMode && console.log("GetTacticalDashboard_Asset_Driven_Detail from cache", data);

                subscriber.next(data);
                subscriber.complete();
            } else {


        let statement = 'API.TacticalDashboard_Asset_Driven_Detail ' +
          '@startDateTimeMS=' +
          startDateMS +
          ', @endDateTimeMS= ' +
          endDateMS +
          " , @accessToken='" +
          Global.User.currentUser.AuthenticationTokenFromUserRecord +
          "'";
                if (!_.isNil(siteId) && siteId != 0) {
                    statement = statement + ", @siteIdList = '" + siteId + "'";
        }
        else if (!_.isNil(orgId)) {
          statement = statement + ", @siteIdList = '" + this.cache.sites.select((site: ISite) => { return site.Id }).toArray().join(",") + "'";
                }

                this.SQLActionAsPromise(statement).then((data: any) => {

                    //save to cache
                    var TacticalGSEAssetDrivenDetail: any = {
                        siteId: siteId,
                        orgId: orgId,
                        startDateMS: startDateMS,
                        endDateMS: endDateMS,
                        data: data,
                    };

                    if (object.TacticalReports == null) {
                        var TacticalReports: any = {
                            TacticalGSEAssetDrivenDetail: TacticalGSEAssetDrivenDetail,
                        };
                        object.TacticalReports = TacticalReports;
                    } else {
                        object.TacticalReports.TacticalGSEAssetDrivenDetail = TacticalGSEAssetDrivenDetail;
                    }

                    subscriber.next(data);
                    subscriber.complete();

        })

            }
    })
    return data$
    }

    GetTacticalDashboard_Asset_InUse_Detail(siteId: any, startDateMS: any, endDateMS: any) {
        var data$: any = new Observable((subscriber) => {
            var object = this.cache.sitesObject[siteId];
            let orgId = Global.User.currentUser.SelectedOrganizationId;
            if (object === undefined && !_.isNil(orgId)) {
                var object = this.cache.organizationsObject[orgId];
            }

      if (object.TacticalReports != null && object.TacticalReports.TacticalGSEAssetInUseDetail != null &&
        siteId == object.TacticalReports.TacticalGSEAssetInUseDetail.siteId &&
        orgId == object.TacticalReports.TacticalGSEAssetInUseDetail.orgId &&
        startDateMS == object.TacticalReports.TacticalGSEAssetInUseDetail.startDateMS &&
        endDateMS == object.TacticalReports.TacticalGSEAssetInUseDetail.endDateMS) {
                let data = object.TacticalReports.TacticalGSEAssetInUseDetail.data;
                Global.User.DebugMode && console.log("GetTacticalDashboard_Asset_InUse_Detail from cache", data);

                subscriber.next(data);
                subscriber.complete();
            } else {


        let statement = 'API.TacticalDashboard_Asset_InUse_Detail ' +
          '@startDateTimeMS=' +
          startDateMS +
          ', @endDateTimeMS= ' +
          endDateMS +
          " , @accessToken='" +
          Global.User.currentUser.AuthenticationTokenFromUserRecord +
          "'";
                if (!_.isNil(siteId) && siteId != 0) {
                    statement = statement + ", @siteIdList = '" + siteId + "'";
        }
        else if (!_.isNil(orgId)) {
          statement = statement + ", @siteIdList = '" + this.cache.sites.select((site: ISite) => { return site.Id }).toArray().join(",") + "'";
                }

                this.SQLActionAsPromise(statement).then((data: any) => {

                    //save to cache
                    var TacticalGSEAssetInUseDetail: any = {
                        siteId: siteId,
                        orgId: orgId,
                        startDateMS: startDateMS,
                        endDateMS: endDateMS,
                        data: data,
                    };

                    if (object.TacticalReports == null) {
                        var TacticalReports: any = {
                            TacticalGSEAssetInUseDetail: TacticalGSEAssetInUseDetail,
                        };
                        object.TacticalReports = TacticalReports;
                    } else {
                        object.TacticalReports.TacticalGSEAssetInUseDetail = TacticalGSEAssetInUseDetail;
                    }

                    subscriber.next(data);
                    subscriber.complete();

        })

            }
    })
    return data$
    }

    GetTacticalDashboard_GS_EquipmentNotUsed(siteId: any, startDateMS: any, endDateMS: any) {
        var data$: any = new Observable((subscriber) => {
            var object = this.cache.sitesObject[siteId];
            let orgId = Global.User.currentUser.SelectedOrganizationId;
            if (object === undefined && !_.isNil(orgId)) {
                var object = this.cache.organizationsObject[orgId];
            }

      if (object.TacticalReports != null && object.TacticalReports.TacticalGS_EquipmentNotUsed != null &&
        siteId == object.TacticalReports.TacticalGS_EquipmentNotUsed.siteId &&
        orgId == object.TacticalReports.TacticalGS_EquipmentNotUsed.orgId &&
        startDateMS == object.TacticalReports.TacticalGS_EquipmentNotUsed.startDateMS &&
        endDateMS == object.TacticalReports.TacticalGS_EquipmentNotUsed.endDateMS) {
                let data = object.TacticalReports.TacticalGS_EquipmentNotUsed.data;
                Global.User.DebugMode && console.log("GetTacticalDashboard_GS_EquipmentNotUsed from cache", data);

                subscriber.next(data);
                subscriber.complete();
            } else {


        let statement = 'API.TacticalDashboard_GS_Equipment_NotUsed ' +
          '@startDateTimeMS=' +
          startDateMS +
          ', @endDateTimeMS= ' +
          endDateMS +
          " , @accessToken='" +
          Global.User.currentUser.AuthenticationTokenFromUserRecord +
          "'";
                if (!_.isNil(siteId) && siteId != 0) {
                    statement = statement + ", @siteIdList = '" + siteId + "'";
        }
        else if (!_.isNil(orgId)) {
          statement = statement + ", @siteIdList = '" + this.cache.sites.select((site: ISite) => { return site.Id }).toArray().join(",") + "'";
                }

                this.SQLActionAsPromise(statement).then((data: any) => {

                    //save to cache
                    var TacticalGS_EquipmentNotUsed: any = {
                        siteId: siteId,
                        orgId: orgId,
                        startDateMS: startDateMS,
                        endDateMS: endDateMS,
                        data: data,
                    };

                    if (object.TacticalReports == null) {
                        var TacticalReports: any = {
                            TacticalGS_EquipmentNotUsed: TacticalGS_EquipmentNotUsed,
                        };
                        object.TacticalReports = TacticalReports;
                    } else {
                        object.TacticalReports.TacticalGS_EquipmentNotUsed = TacticalGS_EquipmentNotUsed;
                    }

                    subscriber.next(data);
                    subscriber.complete();

        })

            }
    })
    return data$
    }

    GetTacticalDashboard_GS_AssetNotUsedDuration(siteId: any, startDateMS: any, endDateMS: any) {
        var data$: any = new Observable((subscriber) => {
            var object = this.cache.sitesObject[siteId];
            let orgId = Global.User.currentUser.SelectedOrganizationId;
            if (object === undefined && !_.isNil(orgId)) {
                var object = this.cache.organizationsObject[orgId];
            }

      if (object.TacticalReports != null && object.TacticalReports.TacticalGS_AssetNotUsedDuration != null &&
        siteId == object.TacticalReports.TacticalGS_AssetNotUsedDuration.siteId &&
        orgId == object.TacticalReports.TacticalGS_AssetNotUsedDuration.orgId &&
        startDateMS == object.TacticalReports.TacticalGS_AssetNotUsedDuration.startDateMS &&
        endDateMS == object.TacticalReports.TacticalGS_AssetNotUsedDuration.endDateMS) {
                let data = object.TacticalReports.TacticalGS_AssetNotUsedDuration.data;
                Global.User.DebugMode && console.log("GetTacticalDashboard_GS_AssetNotUsedDuration from cache", data);

                subscriber.next(data);
                subscriber.complete();
            } else {


        let statement = 'API.TacticalDashboard_GS_Asset_NotUsed_Duration ' +
          '@startDateTimeMS=' +
          startDateMS +
          ', @endDateTimeMS= ' +
          endDateMS +
          " , @accessToken='" +
          Global.User.currentUser.AuthenticationTokenFromUserRecord +
          "'";
                if (!_.isNil(siteId) && siteId != 0) {
                    statement = statement + ", @siteIdList = '" + siteId + "'";
        }
        else if (!_.isNil(orgId)) {
          statement = statement + ", @siteIdList = '" + this.cache.sites.select((site: ISite) => { return site.Id }).toArray().join(",") + "'";
                }

                this.SQLActionAsPromise(statement).then((data: any) => {

                    //save to cache
                    var TacticalGS_AssetNotUsedDuration: any = {
                        siteId: siteId,
                        orgId: orgId,
                        startDateMS: startDateMS,
                        endDateMS: endDateMS,
                        data: data,
                    };

                    if (object.TacticalReports == null) {
                        var TacticalReports: any = {
                            TacticalGS_AssetNotUsedDuration: TacticalGS_AssetNotUsedDuration,
                        };
                        object.TacticalReports = TacticalReports;
                    } else {
                        object.TacticalReports.TacticalGS_AssetNotUsedDuration = TacticalGS_AssetNotUsedDuration;
                    }

                    subscriber.next(data);
                    subscriber.complete();

        })

            }
    })
    return data$
    }

    GetTacticalDashboard_GS_EachDayCountDuration_Used(siteId: any, startDateMS: any, endDateMS: any) {
        var data$: any = new Observable((subscriber) => {
            var object = this.cache.sitesObject[siteId];
            let orgId = Global.User.currentUser.SelectedOrganizationId;
            if (object === undefined && !_.isNil(orgId)) {
                var object = this.cache.organizationsObject[orgId];
            }

      if (object.TacticalReports != null && object.TacticalReports.TacticalGS_EachDayCountDurationUsed != null &&
        siteId == object.TacticalReports.TacticalGS_EachDayCountDurationUsed.siteId &&
        orgId == object.TacticalReports.TacticalGS_EachDayCountDurationUsed.orgId &&
        startDateMS == object.TacticalReports.TacticalGS_EachDayCountDurationUsed.startDateMS &&
        endDateMS == object.TacticalReports.TacticalGS_EachDayCountDurationUsed.endDateMS) {
                let data = object.TacticalReports.TacticalGS_EachDayCountDurationUsed.data;
                Global.User.DebugMode && console.log("GetTacticalDashboard_GS_EachDayCountDuration_Used from cache", data);

                subscriber.next(data);
                subscriber.complete();
            } else {


        let statement = 'API.TacticalDashboard_GS_Asset_PoweredOn_Duration ' +
          '@startDateTimeMS=' +
          startDateMS +
          ', @endDateTimeMS= ' +
          endDateMS +
          " , @accessToken='" +
          Global.User.currentUser.AuthenticationTokenFromUserRecord +
          "'";
                if (!_.isNil(siteId) && siteId != 0) {
                    statement = statement + ", @siteIdList = '" + siteId + "'";
        }
        else if (!_.isNil(orgId)) {
          statement = statement + ", @siteIdList = '" + this.cache.sites.select((site: ISite) => { return site.Id }).toArray().join(",") + "'";
                }

                this.SQLActionAsPromise(statement).then((data: any) => {

                    //save to cache
                    var TacticalGS_EachDayCountDurationUsed: any = {
                        siteId: siteId,
                        orgId: orgId,
                        startDateMS: startDateMS,
                        endDateMS: endDateMS,
                        data: data,
                    };

                    if (object.TacticalReports == null) {
                        var TacticalReports: any = {
                            TacticalGS_EachDayCountDurationUsed: TacticalGS_EachDayCountDurationUsed,
                        };
                        object.TacticalReports = TacticalReports;
                    } else {
                        object.TacticalReports.TacticalGS_EachDayCountDurationUsed = TacticalGS_EachDayCountDurationUsed;
                    }

                    subscriber.next(data);
                    subscriber.complete();

        })

            }
    })
    return data$
    }

    GetTacticalDashboard_GS_AssetInUseDetail(siteId: any, startDateMS: any, endDateMS: any) {
        var data$: any = new Observable((subscriber) => {
            var object = this.cache.sitesObject[siteId];
            let orgId = Global.User.currentUser.SelectedOrganizationId;
            if (object === undefined && !_.isNil(orgId)) {
                var object = this.cache.organizationsObject[orgId];
            }

      if (object.TacticalReports != null && object.TacticalReports.TacticalGS_AssetInUseDetail != null &&
        siteId == object.TacticalReports.TacticalGS_AssetInUseDetail.siteId &&
        orgId == object.TacticalReports.TacticalGS_AssetInUseDetail.orgId &&
        startDateMS == object.TacticalReports.TacticalGS_AssetInUseDetail.startDateMS &&
        endDateMS == object.TacticalReports.TacticalGS_AssetInUseDetail.endDateMS) {
                let data = object.TacticalReports.TacticalGS_AssetInUseDetail.data;
                Global.User.DebugMode && console.log("GetTacticalDashboard_GS_AssetInUseDetail from cache", data);

                subscriber.next(data);
                subscriber.complete();
            } else {


        let statement = 'API.TacticalDashboard_GS_Asset_InUse_Detail ' +
          '@startDateTimeMS=' +
          startDateMS +
          ', @endDateTimeMS= ' +
          endDateMS +
          " , @accessToken='" +
          Global.User.currentUser.AuthenticationTokenFromUserRecord +
          "'";
                if (!_.isNil(siteId) && siteId != 0) {
                    statement = statement + ", @siteIdList = '" + siteId + "'";
        }
        else if (!_.isNil(orgId)) {
          statement = statement + ", @siteIdList = '" + this.cache.sites.select((site: ISite) => { return site.Id }).toArray().join(",") + "'";
                }

                this.SQLActionAsPromise(statement).then((data: any) => {

                    //save to cache
                    var TacticalGS_AssetInUseDetail: any = {
                        siteId: siteId,
                        orgId: orgId,
                        startDateMS: startDateMS,
                        endDateMS: endDateMS,
                        data: data,
                    };

                    if (object.TacticalReports == null) {
                        var TacticalReports: any = {
                            TacticalGS_AssetInUseDetail: TacticalGS_AssetInUseDetail,
                        };
                        object.TacticalReports = TacticalReports;
                    } else {
                        object.TacticalReports.TacticalGS_AssetInUseDetail = TacticalGS_AssetInUseDetail;
                    }

                    subscriber.next(data);
                    subscriber.complete();

        })

            }
    })
    return data$
    }

    GetTacticalDashboard_GS_AssetOutOfServiceOrMaintenanceModeDuration(siteId: any, startDateMS: any, endDateMS: any) {
        var data$: any = new Observable((subscriber) => {
            var object = this.cache.sitesObject[siteId];
            let orgId = Global.User.currentUser.SelectedOrganizationId;
            if (object === undefined && !_.isNil(orgId)) {
                var object = this.cache.organizationsObject[orgId];
            }

      if (object.TacticalReports != null && object.TacticalReports.TacticalGS_AssetOutOfServiceOrMaintenanceModeDuration != null &&
        siteId == object.TacticalReports.TacticalGS_AssetOutOfServiceOrMaintenanceModeDuration.siteId &&
        orgId == object.TacticalReports.TacticalGS_AssetOutOfServiceOrMaintenanceModeDuration.orgId &&
        startDateMS == object.TacticalReports.TacticalGS_AssetOutOfServiceOrMaintenanceModeDuration.startDateMS &&
        endDateMS == object.TacticalReports.TacticalGS_AssetOutOfServiceOrMaintenanceModeDuration.endDateMS) {
                let data = object.TacticalReports.TacticalGS_AssetOutOfServiceOrMaintenanceModeDuration.data;
                Global.User.DebugMode && console.log("GetTacticalDashboard_GS_AssetOutOfServiceOrMaintenanceModeDuration from cache", data);

                subscriber.next(data);
                subscriber.complete();
            } else {


        let statement = 'API.TacticalDashboard_GS_Asset_OutOfServiceOrMaintenanceMode_Duration ' +
          '@startDateTimeMS=' +
          startDateMS +
          ', @endDateTimeMS= ' +
          endDateMS +
          " , @accessToken='" +
          Global.User.currentUser.AuthenticationTokenFromUserRecord +
          "'";
                if (!_.isNil(siteId) && siteId != 0) {
                    statement = statement + ", @siteIdList = '" + siteId + "'";
        }
        else if (!_.isNil(orgId)) {
          statement = statement + ", @siteIdList = '" + this.cache.sites.select((site: ISite) => { return site.Id }).toArray().join(",") + "'";
                }

                this.SQLActionAsPromise(statement).then((data: any) => {

                    //save to cache
                    var TacticalGS_AssetOutOfServiceOrMaintenanceModeDuration: any = {
                        siteId: siteId,
                        orgId: orgId,
                        startDateMS: startDateMS,
                        endDateMS: endDateMS,
                        data: data,
                    };

                    if (object.TacticalReports == null) {
                        var TacticalReports: any = {
                            TacticalGS_AssetOutOfServiceOrMaintenanceModeDuration: TacticalGS_AssetOutOfServiceOrMaintenanceModeDuration,
                        };
                        object.TacticalReports = TacticalReports;
                    } else {
                        object.TacticalReports.TacticalGS_AssetOutOfServiceOrMaintenanceModeDuration = TacticalGS_AssetOutOfServiceOrMaintenanceModeDuration;
                    }

                    subscriber.next(data);
                    subscriber.complete();

        })

            }
    })
    return data$
    }


    GetTacticalDashboard_AssetOutOfServiceOrMaintenanceModeDuration(siteId: any, startDateMS: any, endDateMS: any) {
        var data$: any = new Observable((subscriber) => {
            var object = this.cache.sitesObject[siteId];
            let orgId = Global.User.currentUser.SelectedOrganizationId;
            if (object === undefined && !_.isNil(orgId)) {
                var object = this.cache.organizationsObject[orgId];
            }

      if (object.TacticalReports != null && object.TacticalReports.Tactical_AssetOutOfServiceOrMaintenanceModeDuration != null &&
        siteId == object.TacticalReports.Tactical_AssetOutOfServiceOrMaintenanceModeDuration.siteId &&
        orgId == object.TacticalReports.Tactical_AssetOutOfServiceOrMaintenanceModeDuration.orgId &&
        startDateMS == object.TacticalReports.Tactical_AssetOutOfServiceOrMaintenanceModeDuration.startDateMS &&
        endDateMS == object.TacticalReports.Tactical_AssetOutOfServiceOrMaintenanceModeDuration.endDateMS) {
                let data = object.TacticalReports.Tactical_AssetOutOfServiceOrMaintenanceModeDuration.data;
                Global.User.DebugMode && console.log("GetTacticalDashboard_AssetOutOfServiceOrMaintenanceModeDuration from cache", data);

                subscriber.next(data);
                subscriber.complete();
            } else {


        let statement = 'API.TacticalDashboard_Asset_OutOfServiceOrMaintenanceMode_Duration ' +
          '@startDateTimeMS=' +
          startDateMS +
          ', @endDateTimeMS= ' +
          endDateMS +
          " , @accessToken='" +
          Global.User.currentUser.AuthenticationTokenFromUserRecord +
          "'";
                if (!_.isNil(siteId) && siteId != 0) {
                    statement = statement + ", @siteIdList = '" + siteId + "'";
        }
        else if (!_.isNil(orgId)) {
          statement = statement + ", @siteIdList = '" + this.cache.sites.select((site: ISite) => { return site.Id }).toArray().join(",") + "'";
                }

                this.SQLActionAsPromise(statement).then((data: any) => {

                    //save to cache
                    var Tactical_AssetOutOfServiceOrMaintenanceModeDuration: any = {
                        siteId: siteId,
                        orgId: orgId,
                        startDateMS: startDateMS,
                        endDateMS: endDateMS,
                        data: data,
                    };

                    if (object.TacticalReports == null) {
                        var TacticalReports: any = {
                            Tactical_AssetOutOfServiceOrMaintenanceModeDuration: Tactical_AssetOutOfServiceOrMaintenanceModeDuration,
                        };
                        object.TacticalReports = TacticalReports;
                    } else {
                        object.TacticalReports.Tactical_AssetOutOfServiceOrMaintenanceModeDuration = Tactical_AssetOutOfServiceOrMaintenanceModeDuration;
                    }

                    subscriber.next(data);
                    subscriber.complete();

        })

            }
    })
    return data$
    }

    GetTacticalDashboard_GS_CommsLossDetail(siteId: any, startDateMS: any, endDateMS: any) {
        var data$: any = new Observable((subscriber) => {
            var object = this.cache.sitesObject[siteId];
            let orgId = Global.User.currentUser.SelectedOrganizationId;
            if (object === undefined && !_.isNil(orgId)) {
                var object = this.cache.organizationsObject[orgId];
            }

      if (object.TacticalReports != null && object.TacticalReports.Tactical_GS_CommsLossDetail != null &&
        siteId == object.TacticalReports.Tactical_GS_CommsLossDetail.siteId &&
        orgId == object.TacticalReports.Tactical_GS_CommsLossDetail.orgId &&
        startDateMS == object.TacticalReports.Tactical_GS_CommsLossDetail.startDateMS &&
        endDateMS == object.TacticalReports.Tactical_GS_CommsLossDetail.endDateMS) {
                let data = object.TacticalReports.Tactical_GS_CommsLossDetail.data;
                Global.User.DebugMode && console.log("GetTacticalDashboard_GS_CommsLossDetail from cache", data);

                subscriber.next(data);
                subscriber.complete();
            } else {


        let statement = 'API.TacticalDashboard_GS_CommsLoss_Detail ' +
          '@startDateTimeMS=' +
          startDateMS +
          ', @endDateTimeMS= ' +
          endDateMS +
          " , @accessToken='" +
          Global.User.currentUser.AuthenticationTokenFromUserRecord +
          "'";
                if (!_.isNil(siteId) && siteId != 0) {
                    statement = statement + ", @siteIdList = '" + siteId + "'";
        }
        else if (!_.isNil(orgId)) {
          statement = statement + ", @siteIdList = '" + this.cache.sites.select((site: ISite) => { return site.Id }).toArray().join(",") + "'";
                }

                this.SQLActionAsPromise(statement).then((data: any) => {

                    //save to cache
                    var Tactical_GS_CommsLossDetail: any = {
                        siteId: siteId,
                        orgId: orgId,
                        startDateMS: startDateMS,
                        endDateMS: endDateMS,
                        data: data,
                    };

                    if (object.TacticalReports == null) {
                        var TacticalReports: any = {
                            Tactical_GS_CommsLossDetail: Tactical_GS_CommsLossDetail,
                        };
                        object.TacticalReports = TacticalReports;
                    } else {
                        object.TacticalReports.Tactical_GS_CommsLossDetail = Tactical_GS_CommsLossDetail;
                    }

                    subscriber.next(data);
                    subscriber.complete();

        })

            }
    })
    return data$
    }

    GetTacticalDashboard_ICS_Connected_Duration(siteId: any, startDateMS: any, endDateMS: any) {
        var data$: any = new Observable((subscriber) => {
            var object = this.cache.sitesObject[siteId];
            let orgId = Global.User.currentUser.SelectedOrganizationId;
            if (object === undefined && !_.isNil(orgId)) {
                var object = this.cache.organizationsObject[orgId];
            }

      if (object.TacticalReports != null && object.TacticalReports.Tactical_ICS_Connected_Duration != null &&
        siteId == object.TacticalReports.Tactical_ICS_Connected_Duration.siteId &&
        orgId == object.TacticalReports.Tactical_ICS_Connected_Duration.orgId &&
        startDateMS == object.TacticalReports.Tactical_ICS_Connected_Duration.startDateMS &&
        endDateMS == object.TacticalReports.Tactical_ICS_Connected_Duration.endDateMS) {
                let data = object.TacticalReports.Tactical_ICS_Connected_Duration.data;
                Global.User.DebugMode && console.log("GetTacticalDashboard_ICS_Connected_Duration from cache", data);

                subscriber.next(data);
                subscriber.complete();
            } else {


        let statement = 'API.TacticalDashboard_ICS_Connected_Duration ' +
          '@startDateTimeMS=' +
          startDateMS +
          ', @endDateTimeMS= ' +
          endDateMS +
          " , @accessToken='" +
          Global.User.currentUser.AuthenticationTokenFromUserRecord +
          "'";
                if (!_.isNil(siteId) && siteId != 0) {
                    statement = statement + ", @siteIdList = '" + siteId + "'";
        }
        else if (!_.isNil(orgId)) {
          statement = statement + ", @siteIdList = '" + this.cache.sites.select((site: ISite) => { return site.Id }).toArray().join(",") + "'";
                }

                this.SQLActionAsPromise(statement).then((data: any) => {

                    //save to cache
                    var Tactical_ICS_Connected_Duration: any = {
                        siteId: siteId,
                        orgId: orgId,
                        startDateMS: startDateMS,
                        endDateMS: endDateMS,
                        data: data,
                    };

                    if (object.TacticalReports == null) {
                        var TacticalReports: any = {
                            Tactical_ICS_Connected_Duration: Tactical_ICS_Connected_Duration,
                        };
                        object.TacticalReports = TacticalReports;
                    } else {
                        object.TacticalReports.Tactical_ICS_Connected_Duration = Tactical_ICS_Connected_Duration;
                    }

                    subscriber.next(data);
                    subscriber.complete();

        })

            }
    })
    return data$
    }

    GetTacticalDashboard_ICS_FullyCharged_Historic(siteId: any, startDateMS: any, endDateMS: any) {
        var data$: any = new Observable((subscriber) => {
            var object = this.cache.sitesObject[siteId];
            let orgId = Global.User.currentUser.SelectedOrganizationId;
            if (object === undefined && !_.isNil(orgId)) {
                var object = this.cache.organizationsObject[orgId];
            }

      if (object.TacticalReports != null && object.TacticalReports.Tactical_ICS_FullyCharged_Historic != null &&
        siteId == object.TacticalReports.Tactical_ICS_FullyCharged_Historic.siteId &&
        orgId == object.TacticalReports.Tactical_ICS_FullyCharged_Historic.orgId &&
        startDateMS == object.TacticalReports.Tactical_ICS_FullyCharged_Historic.startDateMS &&
        endDateMS == object.TacticalReports.Tactical_ICS_FullyCharged_Historic.endDateMS) {
                let data = object.TacticalReports.Tactical_ICS_FullyCharged_Historic.data;
                Global.User.DebugMode && console.log("GetTacticalDashboard_ICS_FullyCharged_Historic from cache", data);

                subscriber.next(data);
                subscriber.complete();
            } else {


        let statement = 'API.TacticalDashboard_ICS_FullyCharged_Historic ' +
          '@startDateTimeMS=' +
          startDateMS +
          ', @endDateTimeMS= ' +
          endDateMS +
          " , @accessToken='" +
          Global.User.currentUser.AuthenticationTokenFromUserRecord +
          "'";
                if (!_.isNil(siteId) && siteId != 0) {
                    statement = statement + ", @siteIdList = '" + siteId + "'";
        }
        else if (!_.isNil(orgId)) {
          statement = statement + ", @siteIdList = '" + this.cache.sites.select((site: ISite) => { return site.Id }).toArray().join(",") + "'";
                }

                this.SQLActionAsPromise(statement).then((data: any) => {

                    //save to cache
                    var Tactical_ICS_FullyCharged_Historic: any = {
                        siteId: siteId,
                        orgId: orgId,
                        startDateMS: startDateMS,
                        endDateMS: endDateMS,
                        data: data,
                    };

                    if (object.TacticalReports == null) {
                        var TacticalReports: any = {
                            Tactical_ICS_FullyCharged_Historic: Tactical_ICS_FullyCharged_Historic,
                        };
                        object.TacticalReports = TacticalReports;
                    } else {
                        object.TacticalReports.Tactical_ICS_FullyCharged_Historic = Tactical_ICS_FullyCharged_Historic;
                    }

                    subscriber.next(data);
                    subscriber.complete();

        })

            }
    })
    return data$
    }

    GetTacticalDashboard_GS_CommsLossDuration(siteId: any, startDateMS: any, endDateMS: any) {
        var data$: any = new Observable((subscriber) => {
            var object = this.cache.sitesObject[siteId];
            let orgId = Global.User.currentUser.SelectedOrganizationId;
            if (object === undefined && !_.isNil(orgId)) {
                var object = this.cache.organizationsObject[orgId];
            }

      if (object.TacticalReports != null && object.TacticalReports.Tactical_GS_CommsLossDuration != null &&
        siteId == object.TacticalReports.Tactical_GS_CommsLossDuration.siteId &&
        orgId == object.TacticalReports.Tactical_GS_CommsLossDuration.orgId &&
        startDateMS == object.TacticalReports.Tactical_GS_CommsLossDuration.startDateMS &&
        endDateMS == object.TacticalReports.Tactical_GS_CommsLossDuration.endDateMS) {
                let data = object.TacticalReports.Tactical_GS_CommsLossDuration.data;
                Global.User.DebugMode && console.log("GetTacticalDashboard_GS_CommsLossDuration from cache", data);

                subscriber.next(data);
                subscriber.complete();
            } else {


        let statement = 'API.TacticalDashboard_GS_CommsLoss_Duration ' +
          '@startDateTimeMS=' +
          startDateMS +
          ', @endDateTimeMS= ' +
          endDateMS +
          " , @accessToken='" +
          Global.User.currentUser.AuthenticationTokenFromUserRecord +
          "'";
                if (!_.isNil(siteId) && siteId != 0) {
                    statement = statement + ", @siteIdList = '" + siteId + "'";
        }
        else if (!_.isNil(orgId)) {
          statement = statement + ", @siteIdList = '" + this.cache.sites.select((site: ISite) => { return site.Id }).toArray().join(",") + "'";
                }

                this.SQLActionAsPromise(statement).then((data: any) => {

                    //save to cache
                    var Tactical_GS_CommsLossDuration: any = {
                        siteId: siteId,
                        orgId: orgId,
                        startDateMS: startDateMS,
                        endDateMS: endDateMS,
                        data: data,
                    };

                    if (object.TacticalReports == null) {
                        var TacticalReports: any = {
                            Tactical_GS_CommsLossDuration: Tactical_GS_CommsLossDuration,
                        };
                        object.TacticalReports = TacticalReports;
                    } else {
                        object.TacticalReports.Tactical_GS_CommsLossDuration = Tactical_GS_CommsLossDuration;
                    }

                    subscriber.next(data);
                    subscriber.complete();

        })

            }
    })
    return data$
    }


    GetTacticalDashboard_PBBDockingCount(siteId: any, startDateMS: any, endDateMS: any) {
        var data$: any = new Observable((subscriber) => {
            var object = this.cache.sitesObject[siteId];
            let orgId = Global.User.currentUser.SelectedOrganizationId;
            if (object === undefined && !_.isNil(orgId)) {
                var object = this.cache.organizationsObject[orgId];
            }

      if (object.TacticalReports != null && object.TacticalReports.PBBDockingCountReport != null &&
        siteId == object.TacticalReports.PBBDockingCountReport.siteId &&
        orgId == object.TacticalReports.PBBDockingCountReport.orgId &&
        startDateMS == object.TacticalReports.PBBDockingCountReport.startDateMS &&
        endDateMS == object.TacticalReports.PBBDockingCountReport.endDateMS) {
                let data = object.TacticalReports.PBBDockingCountReport.data;
                Global.User.DebugMode && console.log("GetTacticalDashboard_PBBDockingCount from cache", data);

                subscriber.next(data);
                subscriber.complete();
            } else {


        let statement = 'API.TacticalDashboard_GS_AircraftDocked_Duration ' +
          '@startDateTimeMS=' +
          startDateMS +
          ', @endDateTimeMS= ' +
          endDateMS +
          " , @accessToken='" +
          Global.User.currentUser.AuthenticationTokenFromUserRecord +
          "'";
                if (!_.isNil(siteId) && siteId != 0) {
                    statement = statement + ", @siteIdList = '" + siteId + "'";
        }
        else if (!_.isNil(orgId)) {
          statement = statement + ", @siteIdList = '" + this.cache.sites.select((site: ISite) => { return site.Id }).toArray().join(",") + "'";
                }

                this.SQLActionAsPromise(statement).then((data: any) => {

                    //save to cache
                    var PBBDockingCountReport: any = {
                        siteId: siteId,
                        orgId: orgId,
                        startDateMS: startDateMS,
                        endDateMS: endDateMS,
                        data: data,
                    };

                    if (object.TacticalReports == null) {
                        var TacticalReports: any = {
                            PBBDockingCountReport: PBBDockingCountReport,
                        };
                        object.TacticalReports = TacticalReports;
                    } else {
                        object.TacticalReports.PBBDockingCountReport = PBBDockingCountReport;
                    }

                    subscriber.next(data);
                    subscriber.complete();

        })

            }
    })
    return data$
    }

    GetTacticalDashboard_PCANotMeetingPerformanceDetail(siteId: any, startDateMS: any, endDateMS: any) {
        var data$: any = new Observable((subscriber) => {
            var object = this.cache.sitesObject[siteId];
            let orgId = Global.User.currentUser.SelectedOrganizationId;
            if (object === undefined && !_.isNil(orgId)) {
                var object = this.cache.organizationsObject[orgId];
            }

      if (object.TacticalReports != null && object.TacticalReports.PCANotMeetingPerformanceDetail != null &&
        siteId == object.TacticalReports.PCANotMeetingPerformanceDetail.siteId &&
        orgId == object.TacticalReports.PCANotMeetingPerformanceDetail.orgId &&
        startDateMS == object.TacticalReports.PCANotMeetingPerformanceDetail.startDateMS &&
        endDateMS == object.TacticalReports.PCANotMeetingPerformanceDetail.endDateMS) {
                let data = object.TacticalReports.PCANotMeetingPerformanceDetail.data;
                Global.User.DebugMode && console.log("GetTacticalDashboard_PCANotMeetingPerformanceDetail from cache", data);

                subscriber.next(data);
                subscriber.complete();
            } else {


        let statement = 'API.TacticalDashboard_PCANotMeetingPerformance_Detail ' +
          '@startDateTimeMS=' +
          startDateMS +
          ', @endDateTimeMS= ' +
          endDateMS +
          " , @accessToken='" +
          Global.User.currentUser.AuthenticationTokenFromUserRecord +
          "'";
                if (!_.isNil(siteId) && siteId != 0) {
                    statement = statement + ", @siteIdList = '" + siteId + "'";
        }
        else if (!_.isNil(orgId)) {
          statement = statement + ", @siteIdList = '" + this.cache.sites.select((site: ISite) => { return site.Id }).toArray().join(",") + "'";
                }

                this.SQLActionAsPromise(statement).then((data: any) => {

                    //save to cache
                    var PCANotMeetingPerformanceDetail: any = {
                        siteId: siteId,
                        orgId: orgId,
                        startDateMS: startDateMS,
                        endDateMS: endDateMS,
                        data: data,
                    };

                    if (object.TacticalReports == null) {
                        var TacticalReports: any = {
                            PCANotMeetingPerformanceDetail: PCANotMeetingPerformanceDetail,
                        };
                        object.TacticalReports = TacticalReports;
                    } else {
                        object.TacticalReports.PCANotMeetingPerformanceDetail = PCANotMeetingPerformanceDetail;
                    }

                    subscriber.next(data);
                    subscriber.complete();

        })

            }
    })
    return data$
    }

    GetTacticalDashboard_PCANotMeetingPerformanceDuration(siteId: any, startDateMS: any, endDateMS: any) {
        var data$: any = new Observable((subscriber) => {
            var object = this.cache.sitesObject[siteId];
            let orgId = Global.User.currentUser.SelectedOrganizationId;
            if (object === undefined && !_.isNil(orgId)) {
                var object = this.cache.organizationsObject[orgId];
            }

      if (object.TacticalReports != null && object.TacticalReports.PCANotMeetingPerformanceDuration != null &&
        siteId == object.TacticalReports.PCANotMeetingPerformanceDuration.siteId &&
        orgId == object.TacticalReports.PCANotMeetingPerformanceDuration.orgId &&
        startDateMS == object.TacticalReports.PCANotMeetingPerformanceDuration.startDateMS &&
        endDateMS == object.TacticalReports.PCANotMeetingPerformanceDuration.endDateMS) {
                let data = object.TacticalReports.PCANotMeetingPerformanceDuration.data;
                Global.User.DebugMode && console.log("GetTacticalDashboard_PCANotMeetingPerformanceDuration from cache", data);

                subscriber.next(data);
                subscriber.complete();
            } else {


        let statement = 'API.TacticalDashboard_GS_PCANotMeetingPerformance_Duration ' +
          '@startDateTimeMS=' +
          startDateMS +
          ', @endDateTimeMS= ' +
          endDateMS +
          " , @accessToken='" +
          Global.User.currentUser.AuthenticationTokenFromUserRecord +
          "'";
                if (!_.isNil(siteId) && siteId != 0) {
                    statement = statement + ", @siteIdList = '" + siteId + "'";
        }
        else if (!_.isNil(orgId)) {
          statement = statement + ", @siteIdList = '" + this.cache.sites.select((site: ISite) => { return site.Id }).toArray().join(",") + "'";
                }

                this.SQLActionAsPromise(statement).then((data: any) => {

                    //save to cache
                    var PCANotMeetingPerformanceDuration: any = {
                        siteId: siteId,
                        orgId: orgId,
                        startDateMS: startDateMS,
                        endDateMS: endDateMS,
                        data: data,
                    };

                    if (object.TacticalReports == null) {
                        var TacticalReports: any = {
                            PCANotMeetingPerformanceDuration: PCANotMeetingPerformanceDuration,
                        };
                        object.TacticalReports = TacticalReports;
                    } else {
                        object.TacticalReports.PCANotMeetingPerformanceDuration = PCANotMeetingPerformanceDuration;
                    }

                    subscriber.next(data);
                    subscriber.complete();

        })

            }
    })
    return data$
    }

    GetTacticalDashboard_GSEAssetsAntiIceUsageHistoric(siteId: any, startDateMS: any, endDateMS: any) {
        var data$: any = new Observable((subscriber) => {
            var object = this.cache.sitesObject[siteId];
            let orgId = Global.User.currentUser.SelectedOrganizationId;
            if (object === undefined && !_.isNil(orgId)) {
                var object = this.cache.organizationsObject[orgId];
            }

      if (object.TacticalReports != null && object.TacticalReports.GSEAssetsAntiIceUsageHistoric != null &&
        siteId == object.TacticalReports.GSEAssetsAntiIceUsageHistoric.siteId &&
        orgId == object.TacticalReports.GSEAssetsAntiIceUsageHistoric.orgId &&
        startDateMS == object.TacticalReports.GSEAssetsAntiIceUsageHistoric.startDateMS &&
        endDateMS == object.TacticalReports.GSEAssetsAntiIceUsageHistoric.endDateMS) {
                let data = object.TacticalReports.GSEAssetsAntiIceUsageHistoric.data;
                Global.User.DebugMode && console.log("GetTacticalDashboard_GSEAssetsAntiIceUsageHistoric from cache", data);

                subscriber.next(data);
                subscriber.complete();
            } else {


        let statement = 'API.TacticalDashboard_GSE_Assets_AntiIceUsageHistoric ' +
          '@startDateTimeMS=' +
          startDateMS +
          ', @endDateTimeMS= ' +
          endDateMS +
          " , @accessToken='" +
          Global.User.currentUser.AuthenticationTokenFromUserRecord +
          "'";
                if (!_.isNil(siteId) && siteId != 0) {
                    statement = statement + ", @siteIdList = '" + siteId + "'";
        }
        else if (!_.isNil(orgId)) {
          statement = statement + ", @siteIdList = '" + this.cache.sites.select((site: ISite) => { return site.Id }).toArray().join(",") + "'";
                }

                this.SQLActionAsPromise(statement).then((data: any) => {

                    //save to cache
                    var GSEAssetsAntiIceUsageHistoric: any = {
                        siteId: siteId,
                        orgId: orgId,
                        startDateMS: startDateMS,
                        endDateMS: endDateMS,
                        data: data,
                    };

                    if (object.TacticalReports == null) {
                        var TacticalReports: any = {
                            GSEAssetsAntiIceUsageHistoric: GSEAssetsAntiIceUsageHistoric,
                        };
                        object.TacticalReports = TacticalReports;
                    } else {
                        object.TacticalReports.GSEAssetsAntiIceUsageHistoric = GSEAssetsAntiIceUsageHistoric;
                    }

                    subscriber.next(data);
                    subscriber.complete();

        })

            }
    })
    return data$
    }


    GetTacticalDashboard_GSEAssetsBatteryLevelUsageHistoric(siteId: any, startDateMS: any, endDateMS: any) {
        var data$: any = new Observable((subscriber) => {
            var object = this.cache.sitesObject[siteId];
            let orgId = Global.User.currentUser.SelectedOrganizationId;
            if (object === undefined && !_.isNil(orgId)) {
                var object = this.cache.organizationsObject[orgId];
            }

      if (object.TacticalReports != null && object.TacticalReports.GSEAssetsBatteryLevelUsageHistoric != null &&
        siteId == object.TacticalReports.GSEAssetsBatteryLevelUsageHistoric.siteId &&
        orgId == object.TacticalReports.GSEAssetsBatteryLevelUsageHistoric.orgId &&
        startDateMS == object.TacticalReports.GSEAssetsBatteryLevelUsageHistoric.startDateMS &&
        endDateMS == object.TacticalReports.GSEAssetsBatteryLevelUsageHistoric.endDateMS) {
                let data = object.TacticalReports.GSEAssetsBatteryLevelUsageHistoric.data;
                Global.User.DebugMode && console.log("GetTacticalDashboard_GSEAssetsBatteryLevelUsageHistoric from cache", data);

                subscriber.next(data);
                subscriber.complete();
            } else {


        let statement = 'API.TacticalDashboard_GSE_Assets_BatteryLevelUsageHistoric ' +
          '@startDateTimeMS=' +
          startDateMS +
          ', @endDateTimeMS= ' +
          endDateMS +
          " , @accessToken='" +
          Global.User.currentUser.AuthenticationTokenFromUserRecord +
          "'";
                if (!_.isNil(siteId) && siteId != 0) {
                    statement = statement + ", @siteIdList = '" + siteId + "'";
        }
        else if (!_.isNil(orgId)) {
          statement = statement + ", @siteIdList = '" + this.cache.sites.select((site: ISite) => { return site.Id }).toArray().join(",") + "'";
                }

                this.SQLActionAsPromise(statement).then((data: any) => {

                    //save to cache
                    var GSEAssetsBatteryLevelUsageHistoric: any = {
                        siteId: siteId,
                        orgId: orgId,
                        startDateMS: startDateMS,
                        endDateMS: endDateMS,
                        data: data,
                    };

                    if (object.TacticalReports == null) {
                        var TacticalReports: any = {
                            GSEAssetsBatteryLevelUsageHistoric: GSEAssetsBatteryLevelUsageHistoric,
                        };
                        object.TacticalReports = TacticalReports;
                    } else {
                        object.TacticalReports.GSEAssetsBatteryLevelUsageHistoric = GSEAssetsBatteryLevelUsageHistoric;
                    }

                    subscriber.next(data);
                    subscriber.complete();

        })

            }
    })
    return data$
    }

    GetTacticalDashboard_GSEAssetsDefTankLevelUsageHistoric(siteId: any, startDateMS: any, endDateMS: any) {
        var data$: any = new Observable((subscriber) => {
            var object = this.cache.sitesObject[siteId];
            let orgId = Global.User.currentUser.SelectedOrganizationId;
            if (object === undefined && !_.isNil(orgId)) {
                var object = this.cache.organizationsObject[orgId];
            }

      if (object.TacticalReports != null && object.TacticalReports.GSEAssetsDefTankLevelUsageHistoric != null &&
        siteId == object.TacticalReports.GSEAssetsDefTankLevelUsageHistoric.siteId &&
        orgId == object.TacticalReports.GSEAssetsDefTankLevelUsageHistoric.orgId &&
        startDateMS == object.TacticalReports.GSEAssetsDefTankLevelUsageHistoric.startDateMS &&
        endDateMS == object.TacticalReports.GSEAssetsDefTankLevelUsageHistoric.endDateMS) {
                let data = object.TacticalReports.GSEAssetsDefTankLevelUsageHistoric.data;
                Global.User.DebugMode && console.log("GetTacticalDashboard_GSEAssetsDefTankLevelUsageHistoric from cache", data);

                subscriber.next(data);
                subscriber.complete();
            } else {


        let statement = 'API.TacticalDashboard_GSE_Assets_DefTankLevelUsageHistoric ' +
          '@startDateTimeMS=' +
          startDateMS +
          ', @endDateTimeMS= ' +
          endDateMS +
          " , @accessToken='" +
          Global.User.currentUser.AuthenticationTokenFromUserRecord +
          "'";
                if (!_.isNil(siteId) && siteId != 0) {
                    statement = statement + ", @siteIdList = '" + siteId + "'";
        }
        else if (!_.isNil(orgId)) {
          statement = statement + ", @siteIdList = '" + this.cache.sites.select((site: ISite) => { return site.Id }).toArray().join(",") + "'";
                }

                this.SQLActionAsPromise(statement).then((data: any) => {

                    //save to cache
                    var GSEAssetsDefTankLevelUsageHistoric: any = {
                        siteId: siteId,
                        orgId: orgId,
                        startDateMS: startDateMS,
                        endDateMS: endDateMS,
                        data: data,
                    };

                    if (object.TacticalReports == null) {
                        var TacticalReports: any = {
                            GSEAssetsDefTankLevelUsageHistoric: GSEAssetsDefTankLevelUsageHistoric,
                        };
                        object.TacticalReports = TacticalReports;
                    } else {
                        object.TacticalReports.GSEAssetsDefTankLevelUsageHistoric = GSEAssetsDefTankLevelUsageHistoric;
                    }

                    subscriber.next(data);
                    subscriber.complete();

        })

            }
    })
    return data$
    }

    GetTacticalDashboard_GSEAssetsDeiceUsageHistoric(siteId: any, startDateMS: any, endDateMS: any) {
        var data$: any = new Observable((subscriber) => {
            var object = this.cache.sitesObject[siteId];
            let orgId = Global.User.currentUser.SelectedOrganizationId;
            if (object === undefined && !_.isNil(orgId)) {
                var object = this.cache.organizationsObject[orgId];
            }

      if (object.TacticalReports != null && object.TacticalReports.GSEAssetsDeiceUsageHistoric != null &&
        siteId == object.TacticalReports.GSEAssetsDeiceUsageHistoric.siteId &&
        orgId == object.TacticalReports.GSEAssetsDeiceUsageHistoric.orgId &&
        startDateMS == object.TacticalReports.GSEAssetsDeiceUsageHistoric.startDateMS &&
        endDateMS == object.TacticalReports.GSEAssetsDeiceUsageHistoric.endDateMS) {
                let data = object.TacticalReports.GSEAssetsDeiceUsageHistoric.data;
                Global.User.DebugMode && console.log("GetTacticalDashboard_GSEAssetsDeiceUsageHistoric from cache", data);

                subscriber.next(data);
                subscriber.complete();
            } else {


        let statement = 'API.TacticalDashboard_GSE_Assets_DeiceUsageHistoric ' +
          '@startDateTimeMS=' +
          startDateMS +
          ', @endDateTimeMS= ' +
          endDateMS +
          " , @accessToken='" +
          Global.User.currentUser.AuthenticationTokenFromUserRecord +
          "'";
                if (!_.isNil(siteId) && siteId != 0) {
                    statement = statement + ", @siteIdList = '" + siteId + "'";
        }
        else if (!_.isNil(orgId)) {
          statement = statement + ", @siteIdList = '" + this.cache.sites.select((site: ISite) => { return site.Id }).toArray().join(",") + "'";
                }

                this.SQLActionAsPromise(statement).then((data: any) => {

                    //save to cache
                    var GSEAssetsDeiceUsageHistoric: any = {
                        siteId: siteId,
                        orgId: orgId,
                        startDateMS: startDateMS,
                        endDateMS: endDateMS,
                        data: data,
                    };

                    if (object.TacticalReports == null) {
                        var TacticalReports: any = {
                            GSEAssetsDeiceUsageHistoric: GSEAssetsDeiceUsageHistoric,
                        };
                        object.TacticalReports = TacticalReports;
                    } else {
                        object.TacticalReports.GSEAssetsDeiceUsageHistoric = GSEAssetsDeiceUsageHistoric;
                    }

                    subscriber.next(data);
                    subscriber.complete();

        })

            }
    })
    return data$
    }

    GetTacticalDashboard_GSEAssetsFuelLevelHistoric(siteId: any, startDateMS: any, endDateMS: any) {
        var data$: any = new Observable((subscriber) => {
            var object = this.cache.sitesObject[siteId];
            let orgId = Global.User.currentUser.SelectedOrganizationId;
            if (object === undefined && !_.isNil(orgId)) {
                var object = this.cache.organizationsObject[orgId];
            }

      if (object.TacticalReports != null && object.TacticalReports.GSEAssetsFuelLevelHistoric != null &&
        siteId == object.TacticalReports.GSEAssetsFuelLevelHistoric.siteId &&
        orgId == object.TacticalReports.GSEAssetsFuelLevelHistoric.orgId &&
        startDateMS == object.TacticalReports.GSEAssetsFuelLevelHistoric.startDateMS &&
        endDateMS == object.TacticalReports.GSEAssetsFuelLevelHistoric.endDateMS) {
                let data = object.TacticalReports.GSEAssetsFuelLevelHistoric.data;
                Global.User.DebugMode && console.log("GetTacticalDashboard_GSEAssetsFuelLevelHistoric from cache", data);

                subscriber.next(data);
                subscriber.complete();
            } else {


        let statement = 'API.TacticalDashboard_GSE_Assets_FuelUsageHistoric ' +
          '@startDateTimeMS=' +
          startDateMS +
          ', @endDateTimeMS= ' +
          endDateMS +
          " , @accessToken='" +
          Global.User.currentUser.AuthenticationTokenFromUserRecord +
          "'";
                if (!_.isNil(siteId) && siteId != 0) {
                    statement = statement + ", @siteIdList = '" + siteId + "'";
        }
        else if (!_.isNil(orgId)) {
          statement = statement + ", @siteIdList = '" + this.cache.sites.select((site: ISite) => { return site.Id }).toArray().join(",") + "'";
                }

                this.SQLActionAsPromise(statement).then((data: any) => {

                    //save to cache
                    var GSEAssetsFuelLevelHistoric: any = {
                        siteId: siteId,
                        orgId: orgId,
                        startDateMS: startDateMS,
                        endDateMS: endDateMS,
                        data: data,
                    };

                    if (object.TacticalReports == null) {
                        var TacticalReports: any = {
                            GSEAssetsFuelLevelHistoric: GSEAssetsFuelLevelHistoric,
                        };
                        object.TacticalReports = TacticalReports;
                    } else {
                        object.TacticalReports.GSEAssetsFuelLevelHistoric = GSEAssetsFuelLevelHistoric;
                    }

                    subscriber.next(data);
                    subscriber.complete();

        })

            }
    })
    return data$
    }



    GetTacticalDashboard_GS_AssetStatus_PoweredOn_ForSpecificMomentInTime(siteId: any, startDateMS: any, endDateMS: any) {
        var data$: any = new Observable((subscriber) => {
            var object = this.cache.sitesObject[siteId];
            let orgId = Global.User.currentUser.SelectedOrganizationId;
            if (object === undefined && !_.isNil(orgId)) {
                var object = this.cache.organizationsObject[orgId];
            }

      if (object.TacticalReports != null && object.TacticalReports.GS_AssetStatus_PoweredOn_ForSpecificMomentInTime != null &&
        siteId == object.TacticalReports.GS_AssetStatus_PoweredOn_ForSpecificMomentInTime.siteId &&
        orgId == object.TacticalReports.GS_AssetStatus_PoweredOn_ForSpecificMomentInTime.orgId &&
        startDateMS == object.TacticalReports.GS_AssetStatus_PoweredOn_ForSpecificMomentInTime.startDateMS &&
        endDateMS == object.TacticalReports.GS_AssetStatus_PoweredOn_ForSpecificMomentInTime.endDateMS) {
                let data = object.TacticalReports.GS_AssetStatus_PoweredOn_ForSpecificMomentInTime.data;
                Global.User.DebugMode && console.log("GetTacticalDashboard_GS_AssetStatus_PoweredOn_ForSpecificMomentInTime from cache", data);

                subscriber.next(data);
                subscriber.complete();
            } else {


        let statement = 'API.TacticalDashboard_GS_AssetStatus_PoweredOn_ForSpecificMomentInTime ' +
          '@startDateTimeMS=' +
          startDateMS +
          ', @endDateTimeMS= ' +
          endDateMS +
          " , @accessToken='" +
          Global.User.currentUser.AuthenticationTokenFromUserRecord +
          "'";
                if (!_.isNil(siteId) && siteId != 0) {
                    statement = statement + ", @siteIdList = '" + siteId + "'";
        }
        else if (!_.isNil(orgId)) {
          statement = statement + ", @siteIdList = '" + this.cache.sites.select((site: ISite) => { return site.Id }).toArray().join(",") + "'";
                }

                this.SQLActionAsPromise(statement).then((data: any) => {

                    //save to cache
                    var GS_AssetStatus_PoweredOn_ForSpecificMomentInTime: any = {
                        siteId: siteId,
                        orgId: orgId,
                        startDateMS: startDateMS,
                        endDateMS: endDateMS,
                        data: data,
                    };

                    if (object.TacticalReports == null) {
                        var TacticalReports: any = {
                            GS_AssetStatus_PoweredOn_ForSpecificMomentInTime: GS_AssetStatus_PoweredOn_ForSpecificMomentInTime,
                        };
                        object.TacticalReports = TacticalReports;
                    } else {
                        object.TacticalReports.GS_AssetStatus_PoweredOn_ForSpecificMomentInTime = GS_AssetStatus_PoweredOn_ForSpecificMomentInTime;
                    }

                    subscriber.next(data);
                    subscriber.complete();

        })

            }
    })
    return data$
    }

    GetTacticalDashboard_AssetStatus_PoweredOn_ForSpecificMomentInTime(siteId: any, startDateMS: any, endDateMS: any) {
        var data$: any = new Observable((subscriber) => {
            var object = this.cache.sitesObject[siteId];
            let orgId = Global.User.currentUser.SelectedOrganizationId;
            if (object === undefined && !_.isNil(orgId)) {
                var object = this.cache.organizationsObject[orgId];
            }

      if (object.TacticalReports != null && object.TacticalReports.AssetStatus_PoweredOn_ForSpecificMomentInTime != null &&
        siteId == object.TacticalReports.AssetStatus_PoweredOn_ForSpecificMomentInTime.siteId &&
        orgId == object.TacticalReports.AssetStatus_PoweredOn_ForSpecificMomentInTime.orgId &&
        startDateMS == object.TacticalReports.AssetStatus_PoweredOn_ForSpecificMomentInTime.startDateMS &&
        endDateMS == object.TacticalReports.AssetStatus_PoweredOn_ForSpecificMomentInTime.endDateMS) {
                let data = object.TacticalReports.AssetStatus_PoweredOn_ForSpecificMomentInTime.data;
                Global.User.DebugMode && console.log("GetTacticalDashboard_AssetStatus_PoweredOn_ForSpecificMomentInTime from cache", data);

                subscriber.next(data);
                subscriber.complete();
            } else {


        let statement = 'API.TacticalDashboard_AssetStatus_PoweredOn_ForSpecificMomentInTime ' +
          '@startDateTimeMS=' +
          startDateMS +
          ', @endDateTimeMS= ' +
          endDateMS +
          " , @accessToken='" +
          Global.User.currentUser.AuthenticationTokenFromUserRecord +
          "'";
                if (!_.isNil(siteId) && siteId != 0) {
                    statement = statement + ", @siteIdList = '" + siteId + "'";
        }
        else if (!_.isNil(orgId)) {
          statement = statement + ", @siteIdList = '" + this.cache.sites.select((site: ISite) => { return site.Id }).toArray().join(",") + "'";
                }

                this.SQLActionAsPromise(statement).then((data: any) => {

                    //save to cache
                    var AssetStatus_PoweredOn_ForSpecificMomentInTime: any = {
                        siteId: siteId,
                        orgId: orgId,
                        startDateMS: startDateMS,
                        endDateMS: endDateMS,
                        data: data,
                    };

                    if (object.TacticalReports == null) {
                        var TacticalReports: any = {
                            AssetStatus_PoweredOn_ForSpecificMomentInTime: AssetStatus_PoweredOn_ForSpecificMomentInTime,
                        };
                        object.TacticalReports = TacticalReports;
                    } else {
                        object.TacticalReports.AssetStatus_PoweredOn_ForSpecificMomentInTime = AssetStatus_PoweredOn_ForSpecificMomentInTime;
                    }

                    subscriber.next(data);
                    subscriber.complete();

        })

            }
    })
    return data$
    }

    GetTacticalDashboard_AssetStatus_PoweredOn_Duration(siteId: any, startDateMS: any, endDateMS: any) {
        var data$: any = new Observable((subscriber) => {
            var object = this.cache.sitesObject[siteId];
            let orgId = Global.User.currentUser.SelectedOrganizationId;
            if (object === undefined && !_.isNil(orgId)) {
                var object = this.cache.organizationsObject[orgId];
            }

      if (object.TacticalReports != null && object.TacticalReports.AssetStatus_PoweredOn_Duration != null &&
        siteId == object.TacticalReports.AssetStatus_PoweredOn_Duration.siteId &&
        orgId == object.TacticalReports.AssetStatus_PoweredOn_Duration.orgId &&
        startDateMS == object.TacticalReports.AssetStatus_PoweredOn_Duration.startDateMS &&
        endDateMS == object.TacticalReports.AssetStatus_PoweredOn_Duration.endDateMS) {
                let data = object.TacticalReports.AssetStatus_PoweredOn_Duration.data;
                Global.User.DebugMode && console.log("GetTacticalDashboard_AssetStatus_PoweredOn_Duration from cache", data);

                subscriber.next(data);
                subscriber.complete();
            } else {


        let statement = 'API.TacticalDashboard_Asset_PoweredOn_Duration ' +
          '@startDateTimeMS=' +
          startDateMS +
          ', @endDateTimeMS= ' +
          endDateMS +
          " , @accessToken='" +
          Global.User.currentUser.AuthenticationTokenFromUserRecord +
          "'";
                if (!_.isNil(siteId) && siteId != 0) {
                    statement = statement + ", @siteIdList = '" + siteId + "'";
        }
        else if (!_.isNil(orgId)) {
          statement = statement + ", @siteIdList = '" + this.cache.sites.select((site: ISite) => { return site.Id }).toArray().join(",") + "'";
                }

                this.SQLActionAsPromise(statement).then((data: any) => {

                    //save to cache
                    var AssetStatus_PoweredOn_Duration: any = {
                        siteId: siteId,
                        orgId: orgId,
                        startDateMS: startDateMS,
                        endDateMS: endDateMS,
                        data: data,
                    };

                    if (object.TacticalReports == null) {
                        var TacticalReports: any = {
                            AssetStatus_PoweredOn_Duration: AssetStatus_PoweredOn_Duration,
                        };
                        object.TacticalReports = TacticalReports;
                    } else {
                        object.TacticalReports.AssetStatus_PoweredOn_Duration = AssetStatus_PoweredOn_Duration;
                    }

                    subscriber.next(data);
                    subscriber.complete();

        })

            }
    })
    return data$
    }

    GetTacticalDashboard_Equipment_Active_Time(siteId: any, startDateMS: any, endDateMS: any) {
        var data$: any = new Observable((subscriber) => {
            var object = this.cache.sitesObject[siteId];
            let orgId = Global.User.currentUser.SelectedOrganizationId;
            if (object === undefined && !_.isNil(orgId)) {
                var object = this.cache.organizationsObject[orgId];
            }

      if (object.TacticalReports != null && object.TacticalReports.Equipment_Active_Time != null &&
        siteId == object.TacticalReports.Equipment_Active_Time.siteId &&
        orgId == object.TacticalReports.Equipment_Active_Time.orgId &&
        startDateMS == object.TacticalReports.Equipment_Active_Time.startDateMS &&
        endDateMS == object.TacticalReports.Equipment_Active_Time.endDateMS) {
                let data = object.TacticalReports.Equipment_Active_Time.data;
                Global.User.DebugMode && console.log("GetTacticalDashboard_Equipment_Active_Time from cache", data);

                subscriber.next(data);
                subscriber.complete();
            } else {


        let statement = 'API.TacticalDashboard_Equipment_Active_Time ' +
          '@startDateTimeMS=' +
          startDateMS +
          ', @endDateTimeMS= ' +
          endDateMS +
          " , @accessToken='" +
          Global.User.currentUser.AuthenticationTokenFromUserRecord +
          "'";
                if (!_.isNil(siteId) && siteId != 0) {
                    statement = statement + ", @siteIdList = '" + siteId + "'";
        }
        else if (!_.isNil(orgId)) {
          statement = statement + ", @siteIdList = '" + this.cache.sites.select((site: ISite) => { return site.Id }).toArray().join(",") + "'";
                }

                this.SQLActionAsPromise(statement).then((data: any) => {

                    //save to cache
                    var Equipment_Active_Time: any = {
                        siteId: siteId,
                        orgId: orgId,
                        startDateMS: startDateMS,
                        endDateMS: endDateMS,
                        data: data,
                    };

                    if (object.TacticalReports == null) {
                        var TacticalReports: any = {
                            Equipment_Active_Time: Equipment_Active_Time,
                        };
                        object.TacticalReports = TacticalReports;
                    } else {
                        object.TacticalReports.Equipment_Active_Time = Equipment_Active_Time;
                    }

                    subscriber.next(data);
                    subscriber.complete();

        })

            }
    })
    return data$
    }

    GetTacticalDashboard_GS_Alarms_Duration(siteId: any, startDateMS: any, endDateMS: any) {
        var data$: any = new Observable((subscriber) => {
            var object = this.cache.sitesObject[siteId];
            let orgId = Global.User.currentUser.SelectedOrganizationId;
            if (object === undefined && !_.isNil(orgId)) {
                var object = this.cache.organizationsObject[orgId];
            }

      if (object.TacticalReports != null && object.TacticalReports.GS_Alarms_Duration != null &&
        siteId == object.TacticalReports.GS_Alarms_Duration.siteId &&
        orgId == object.TacticalReports.GS_Alarms_Duration.orgId &&
        startDateMS == object.TacticalReports.GS_Alarms_Duration.startDateMS &&
        endDateMS == object.TacticalReports.GS_Alarms_Duration.endDateMS) {
                let data = object.TacticalReports.GS_Alarms_Duration.data;
                Global.User.DebugMode && console.log("GetTacticalDashboard_GS_Alarms_Duration from cache", data);

                subscriber.next(data);
                subscriber.complete();
            } else {


        let statement = 'API.TacticalDashboard_GS_Alarms_Duration ' +
          '@startDateTimeMS=' +
          startDateMS +
          ', @endDateTimeMS= ' +
          endDateMS +
          " , @accessToken='" +
          Global.User.currentUser.AuthenticationTokenFromUserRecord +
          "'";
                if (!_.isNil(siteId) && siteId != 0) {
                    statement = statement + ", @siteIdList = '" + siteId + "'";
        }
        else if (!_.isNil(orgId)) {
          statement = statement + ", @siteIdList = '" + this.cache.sites.select((site: ISite) => { return site.Id }).toArray().join(",") + "'";
                }

                this.SQLActionAsPromise(statement).then((data: any) => {

                    //save to cache
                    var GS_Alarms_Duration: any = {
                        siteId: siteId,
                        orgId: orgId,
                        startDateMS: startDateMS,
                        endDateMS: endDateMS,
                        data: data,
                    };

                    if (object.TacticalReports == null) {
                        var TacticalReports: any = {
                            GS_Alarms_Duration: GS_Alarms_Duration,
                        };
                        object.TacticalReports = TacticalReports;
                    } else {
                        object.TacticalReports.GS_Alarms_Duration = GS_Alarms_Duration;
                    }

                    subscriber.next(data);
                    subscriber.complete();

        })

            }
    })
    return data$
    }

    GetTacticalDashboard_GS_Alarms_Detail(siteId: any, startDateMS: any, endDateMS: any) {
        var data$: any = new Observable((subscriber) => {
            var object = this.cache.sitesObject[siteId];
            let orgId = Global.User.currentUser.SelectedOrganizationId;
            if (object === undefined && !_.isNil(orgId)) {
                var object = this.cache.organizationsObject[orgId];
            }

      if (object.TacticalReports != null && object.TacticalReports.GS_Alarms_Detail != null &&
        siteId == object.TacticalReports.GS_Alarms_Detail.siteId &&
        orgId == object.TacticalReports.GS_Alarms_Detail.orgId &&
        startDateMS == object.TacticalReports.GS_Alarms_Detail.startDateMS &&
        endDateMS == object.TacticalReports.GS_Alarms_Detail.endDateMS) {
                let data = object.TacticalReports.GS_Alarms_Detail.data;
                Global.User.DebugMode && console.log("GetTacticalDashboard_GS_Alarms_Detail from cache", data);

                subscriber.next(data);
                subscriber.complete();
            } else {


        let statement = 'API.TacticalDashboard_GS_Alarms_Detail ' +
          '@startDateTimeMS=' +
          startDateMS +
          ', @endDateTimeMS= ' +
          endDateMS +
          " , @accessToken='" +
          Global.User.currentUser.AuthenticationTokenFromUserRecord +
          "'";
                if (!_.isNil(siteId) && siteId != 0) {
                    statement = statement + ", @siteIdList = '" + siteId + "'";
        }
        else if (!_.isNil(orgId)) {
          statement = statement + ", @siteIdList = '" + this.cache.sites.select((site: ISite) => { return site.Id }).toArray().join(",") + "'";
                }

                this.SQLActionAsPromise(statement).then((data: any) => {

                    //save to cache
                    var GS_Alarms_Detail: any = {
                        siteId: siteId,
                        orgId: orgId,
                        startDateMS: startDateMS,
                        endDateMS: endDateMS,
                        data: data,
                    };

                    if (object.TacticalReports == null) {
                        var TacticalReports: any = {
                            GS_Alarms_Detail: GS_Alarms_Detail,
                        };
                        object.TacticalReports = TacticalReports;
                    } else {
                        object.TacticalReports.GS_Alarms_Detail = GS_Alarms_Detail;
                    }

                    subscriber.next(data);
                    subscriber.complete();

        })

            }
    })
    return data$
    }


    GetTacticalDashboard_GS_Asset_NotUsed_Duration(siteId: any, startDateMS: any, endDateMS: any) {
        var data$: any = new Observable((subscriber) => {
            var object = this.cache.sitesObject[siteId];
            let orgId = Global.User.currentUser.SelectedOrganizationId;
            if (object === undefined && !_.isNil(orgId)) {
                var object = this.cache.organizationsObject[orgId];
            }

      if (object.TacticalReports != null && object.TacticalReports.GS_Asset_NotUsed_Duration != null &&
        siteId == object.TacticalReports.GS_Asset_NotUsed_Duration.siteId &&
        orgId == object.TacticalReports.GS_Asset_NotUsed_Duration.orgId &&
        startDateMS == object.TacticalReports.GS_Asset_NotUsed_Duration.startDateMS &&
        endDateMS == object.TacticalReports.GS_Asset_NotUsed_Duration.endDateMS) {
                let data = object.TacticalReports.GS_Asset_NotUsed_Duration.data;
                Global.User.DebugMode && console.log("GetTacticalDashboard_GS_Asset_NotUsed_Duration from cache", data);

                subscriber.next(data);
                subscriber.complete();
            } else {


        let statement = 'API.TacticalDashboard_GS_Asset_NotUsed_Duration ' +
          '@startDateTimeMS=' +
          startDateMS +
          ', @endDateTimeMS= ' +
          endDateMS +
          " , @accessToken='" +
          Global.User.currentUser.AuthenticationTokenFromUserRecord +
          "'";
                if (!_.isNil(siteId) && siteId != 0) {
                    statement = statement + ", @siteIdList = '" + siteId + "'";
        }
        else if (!_.isNil(orgId)) {
          statement = statement + ", @siteIdList = '" + this.cache.sites.select((site: ISite) => { return site.Id }).toArray().join(",") + "'";
                }

                this.SQLActionAsPromise(statement).then((data: any) => {

                    //save to cache
                    var GS_Asset_NotUsed_Duration: any = {
                        siteId: siteId,
                        orgId: orgId,
                        startDateMS: startDateMS,
                        endDateMS: endDateMS,
                        data: data,
                    };

                    if (object.TacticalReports == null) {
                        var TacticalReports: any = {
                            GS_Asset_NotUsed_Duration: GS_Asset_NotUsed_Duration,
                        };
                        object.TacticalReports = TacticalReports;
                    } else {
                        object.TacticalReports.GS_Asset_NotUsed_Duration = GS_Asset_NotUsed_Duration;
                    }

                    subscriber.next(data);
                    subscriber.complete();

        })

            }
    })
    return data$
    }

    GetPMTemplateSummary(assetId: any, organizationId: any) {
        var data$: any = new Observable((subscriber) => {
            const safeAssetId = Number(assetId);
            const safeOrganizationId = Number(organizationId);
            if (!Number.isFinite(safeAssetId) || safeAssetId <= 0) {
                subscriber.next([]);
                subscriber.complete();
                return;
            }

            if (!Number.isFinite(safeOrganizationId) || safeOrganizationId <= 0) {
                subscriber.error(new Error('A valid organization id is required.'));
                return;
            }

            const statement = `PMGetTemplateSummary @assetId=${safeAssetId}, @OrganizationId=${safeOrganizationId}`;
            console.log('[data.service] GetPMTemplateSummary call:', statement);
            this.SQLActionAsPromise(statement)
                .then((data: any) => {
                    console.log('[data.service] GetPMTemplateSummary raw response:', Array.isArray(data) ? { rowCount: data.length } : data);
                    subscriber.next(data ?? []);
                    subscriber.complete();
                })
                .catch((err: any) => subscriber.error(err));
        });
        return data$;
    }

    GetPMAssetHistory(assetId: any) {
        var data$: any = new Observable((subscriber) => {
            const safeAssetId = Number(assetId);
            if (!Number.isFinite(safeAssetId) || safeAssetId <= 0) {
                subscriber.next([]);
                subscriber.complete();
                return;
            }

            const statement = `PMGetAssetHistory @assetId=${safeAssetId}`;
            console.log('[data.service] GetPMAssetHistory call:', statement);
            this.SQLActionAsPromise(statement)
                .then((data: any) => {
                    console.log('[data.service] GetPMAssetHistory raw response:', Array.isArray(data) ? { rowCount: data.length } : data);
                    subscriber.next(data ?? []);
                    subscriber.complete();
                })
                .catch((err: any) => subscriber.error(err));
        });
        return data$;
    }

    GetPMTemplateList(organizationId: any) {
        var data$: any = new Observable((subscriber) => {
            const safeOrganizationId = Number(organizationId);
            if (!Number.isFinite(safeOrganizationId) || safeOrganizationId <= 0) {
                subscriber.error(new Error('A valid organization id is required.'));
                return;
            }

            const statement = `API.PMGetTemplateList @OrganizationId = ${safeOrganizationId}`;
            console.log('[data.service] GetPMTemplateList call:', statement);
            this.SQLActionAsPromise(statement)
                .then((data: any) => {
                    console.log('[data.service] GetPMTemplateList raw response:', Array.isArray(data) ? { rowCount: data.length } : data);
                    subscriber.next(data ?? []);
                    subscriber.complete();
                })
                .catch((err: any) => subscriber.error(err));
        });
        return data$;
    }

    GetPMTemplateFrequencyList() {
        var data$: any = new Observable((subscriber) => {
            const statement = `API.PMGetTemplateFrequencyList`;
            console.log('[data.service] GetPMTemplateFrequencyList call:', statement);
            this.SQLActionAsPromise(statement)
                .then((data: any) => {
                    console.log('[data.service] GetPMTemplateFrequencyList raw response:', Array.isArray(data) ? { rowCount: data.length } : data);
                    subscriber.next(data ?? []);
                    subscriber.complete();
                })
                .catch((err: any) => subscriber.error(err));
        });
        return data$;
    }

    GetPMTemplateDetail(pmTemplateId: any) {
        var data$: any = new Observable((subscriber) => {
            const safeTemplateId = Number(pmTemplateId);
            if (!Number.isFinite(safeTemplateId) || safeTemplateId <= 0) {
                subscriber.error(new Error('A valid PM template id is required.'));
                return;
            }

            const statement = `API.PMGetTemplateDetail @PMTemplateId = ${safeTemplateId}`;
            console.log('[data.service] GetPMTemplateDetail call:', statement);
            this.SQLActionAsPromise(statement)
                .then((data: any) => {
                    console.log('[data.service] GetPMTemplateDetail raw response:', Array.isArray(data) ? { rowCount: data.length } : data);
                    subscriber.next(data ?? []);
                    subscriber.complete();
                })
                .catch((err: any) => subscriber.error(err));
        });
        return data$;
    }

    GetPMTemplateTaskList(pmTemplateId: any) {
        var data$: any = new Observable((subscriber) => {
            const safeTemplateId = Number(pmTemplateId);
            if (!Number.isFinite(safeTemplateId) || safeTemplateId <= 0) {
                subscriber.error(new Error('A valid PM template id is required.'));
                return;
            }

            const statement = `API.PMGetTemplateTaskList @PMTemplateId = ${safeTemplateId}`;
            console.log('[data.service] GetPMTemplateTaskList call:', statement);
            this.SQLActionAsPromise(statement)
                .then((data: any) => {
                    console.log('[data.service] GetPMTemplateTaskList raw response:', Array.isArray(data) ? { rowCount: data.length } : data);
                    subscriber.next(data ?? []);
                    subscriber.complete();
                })
                .catch((err: any) => subscriber.error(err));
        });
        return data$;
    }

    GetPMValidationTypeList() {
        var data$: any = new Observable((subscriber) => {
            const statement = `API.PMGetValidationTypeList`;
            console.log('[data.service] GetPMValidationTypeList call:', statement);
            this.SQLActionAsPromise(statement)
                .then((data: any) => {
                    console.log('[data.service] GetPMValidationTypeList raw response:', Array.isArray(data) ? { rowCount: data.length } : data);
                    subscriber.next(data ?? []);
                    subscriber.complete();
                })
                .catch((err: any) => subscriber.error(err));
        });
        return data$;
    }

    GetPMValidationOperatorList() {
        var data$: any = new Observable((subscriber) => {
            const statement = `API.PMGetValidationOperatorList`;
            console.log('[data.service] GetPMValidationOperatorList call:', statement);
            this.SQLActionAsPromise(statement)
                .then((data: any) => {
                    console.log('[data.service] GetPMValidationOperatorList raw response:', Array.isArray(data) ? { rowCount: data.length } : data);
                    subscriber.next(data ?? []);
                    subscriber.complete();
                })
                .catch((err: any) => subscriber.error(err));
        });
        return data$;
    }

    UpdatePMTemplateHeader(pmTemplateId: any, templateName: any, pmTemplateFrequencyId: any, assetTypeId: any, userId: any) {
        var data$: any = new Observable((subscriber) => {
            const safeTemplateId = Number(pmTemplateId);
            const safeTemplateName = String(templateName ?? '').trim();
            const safeFrequencyId = Number(pmTemplateFrequencyId);
            const safeAssetTypeId = Number(assetTypeId);
            const safeUserId = Number(userId);

            if (!Number.isFinite(safeTemplateId) || safeTemplateId <= 0) {
                subscriber.error(new Error('A valid PM template id is required.'));
                return;
            }

            if (!safeTemplateName.length) {
                subscriber.error(new Error('A template name is required.'));
                return;
            }

            if (!Number.isFinite(safeFrequencyId) || safeFrequencyId <= 0) {
                subscriber.error(new Error('A valid PM template frequency id is required.'));
                return;
            }

            if (!Number.isFinite(safeUserId) || safeUserId <= 0) {
                subscriber.error(new Error('A valid current user id is required.'));
                return;
            }

            const escapedTemplateName = safeTemplateName.replace(/'/g, "''");
            const statement =
                `API.PMUpdateTemplateHeader ` +
                `@PMTemplateId = ${safeTemplateId}, ` +
                `@TemplateName = N'${escapedTemplateName}', ` +
                `@PMTemplateFrequencyId = ${safeFrequencyId}, ` +
                `@AssetTypeId = ${Number.isFinite(safeAssetTypeId) && safeAssetTypeId > 0 ? safeAssetTypeId : 'NULL'}, ` +
                `@UserId = ${safeUserId}`;
            console.log('[data.service] UpdatePMTemplateHeader call:', statement);

            this.SQLActionAsPromise(statement)
                .then((data: any) => {
                    console.log('[data.service] UpdatePMTemplateHeader raw response:', Array.isArray(data) ? { rowCount: data.length } : data);
                    subscriber.next(data ?? []);
                    subscriber.complete();
                })
                .catch((err: any) => subscriber.error(err));
        });
        return data$;
    }

    AddPMTemplateTask(
        pmTemplateId: any,
        sequenceNumber: any,
        taskName: any,
        taskDescription: any,
        pmValidationTypeId: any,
        pmValidationOperatorId: any,
        jbtStandardObservationId: any,
        expectedValue: any,
        expectedValueMin: any,
        expectedValueMax: any,
        telemetryFreshnessMinutes: any,
        isRequired: any,
        requiresNoteOnFailure: any,
        userId: any
    ) {
        var data$: any = new Observable((subscriber) => {
            const safeTemplateId = Number(pmTemplateId);
            const safeSequenceNumber = Number(sequenceNumber);
            const safeTaskName = String(taskName ?? '').trim();
            const safeTaskDescription = String(taskDescription ?? '').trim();
            const safeValidationTypeId = Number(pmValidationTypeId);
            const safeValidationOperatorId = Number(pmValidationOperatorId);
            const safeObservationId = Number(jbtStandardObservationId);
            const safeExpectedValue = String(expectedValue ?? '').trim();
            const safeExpectedValueMin = String(expectedValueMin ?? '').trim();
            const safeExpectedValueMax = String(expectedValueMax ?? '').trim();
            const safeTelemetryFreshnessMinutes = Number(telemetryFreshnessMinutes);
            const safeIsRequired = isRequired === true || isRequired === 1 || isRequired === '1' || isRequired === 'true' || isRequired === 'True';
            const safeRequiresNotes = requiresNoteOnFailure === true || requiresNoteOnFailure === 1 || requiresNoteOnFailure === '1' || requiresNoteOnFailure === 'true' || requiresNoteOnFailure === 'True';
            const safeUserId = Number(userId);

            if (!Number.isFinite(safeTemplateId) || safeTemplateId <= 0) {
                subscriber.error(new Error('A valid PM template id is required.'));
                return;
            }
            if (!Number.isFinite(safeSequenceNumber) || safeSequenceNumber < 1) {
                subscriber.error(new Error('A valid sequence number is required.'));
                return;
            }
            if (!safeTaskName.length) {
                subscriber.error(new Error('A task name is required.'));
                return;
            }
            if (!Number.isFinite(safeValidationTypeId) || safeValidationTypeId <= 0) {
                subscriber.error(new Error('A valid validation type id is required.'));
                return;
            }
            if (!Number.isFinite(safeUserId) || safeUserId <= 0) {
                subscriber.error(new Error('A valid current user id is required.'));
                return;
            }

            const statement =
                `API.PMAddTemplateTask ` +
                `@PMTemplateId = ${safeTemplateId}, ` +
                `@SequenceNumber = ${safeSequenceNumber}, ` +
                `@TaskName = N'${safeTaskName.replace(/'/g, "''")}', ` +
                `@TaskDescription = ${safeTaskDescription.length > 0 ? `N'${safeTaskDescription.replace(/'/g, "''")}'` : 'NULL'}, ` +
                `@PMValidationTypeId = ${safeValidationTypeId}, ` +
                `@PMValidationOperatorId = ${Number.isFinite(safeValidationOperatorId) && safeValidationOperatorId > 0 ? safeValidationOperatorId : 'NULL'}, ` +
                `@JBTStandardObservationId = ${Number.isFinite(safeObservationId) && safeObservationId > 0 ? safeObservationId : 'NULL'}, ` +
                `@ExpectedValue = ${safeExpectedValue.length > 0 ? `N'${safeExpectedValue.replace(/'/g, "''")}'` : 'NULL'}, ` +
                `@ExpectedValueMin = ${safeExpectedValueMin.length > 0 ? `N'${safeExpectedValueMin.replace(/'/g, "''")}'` : 'NULL'}, ` +
                `@ExpectedValueMax = ${safeExpectedValueMax.length > 0 ? `N'${safeExpectedValueMax.replace(/'/g, "''")}'` : 'NULL'}, ` +
                `@TelemetryFreshnessMinutes = ${Number.isFinite(safeTelemetryFreshnessMinutes) && safeTelemetryFreshnessMinutes > 0 ? safeTelemetryFreshnessMinutes : 'NULL'}, ` +
                `@IsRequired = ${safeIsRequired ? 1 : 0}, ` +
                `@RequiresNoteOnFailure = ${safeRequiresNotes ? 1 : 0}, ` +
                `@UserId = ${safeUserId}`;
            console.log('[data.service] AddPMTemplateTask call:', statement);

            this.SQLActionAsPromise(statement)
                .then((data: any) => {
                    console.log('[data.service] AddPMTemplateTask raw response:', Array.isArray(data) ? { rowCount: data.length } : data);
                    subscriber.next(data ?? []);
                    subscriber.complete();
                })
                .catch((err: any) => subscriber.error(err));
        });
        return data$;
    }

    UpdatePMTemplateTask(
        pmTemplateTaskId: any,
        sequenceNumber: any,
        taskName: any,
        taskDescription: any,
        pmValidationTypeId: any,
        pmValidationOperatorId: any,
        jbtStandardObservationId: any,
        expectedValue: any,
        expectedValueMin: any,
        expectedValueMax: any,
        telemetryFreshnessMinutes: any,
        isRequired: any,
        requiresNoteOnFailure: any,
        userId: any
    ) {
        var data$: any = new Observable((subscriber) => {
            const safeTaskId = Number(pmTemplateTaskId);
            const safeSequenceNumber = Number(sequenceNumber);
            const safeTaskName = String(taskName ?? '').trim();
            const safeTaskDescription = String(taskDescription ?? '').trim();
            const safeValidationTypeId = Number(pmValidationTypeId);
            const safeValidationOperatorId = Number(pmValidationOperatorId);
            const safeObservationId = Number(jbtStandardObservationId);
            const safeExpectedValue = String(expectedValue ?? '').trim();
            const safeExpectedValueMin = String(expectedValueMin ?? '').trim();
            const safeExpectedValueMax = String(expectedValueMax ?? '').trim();
            const safeTelemetryFreshnessMinutes = Number(telemetryFreshnessMinutes);
            const safeIsRequired = isRequired === true || isRequired === 1 || isRequired === '1' || isRequired === 'true' || isRequired === 'True';
            const safeRequiresNotes = requiresNoteOnFailure === true || requiresNoteOnFailure === 1 || requiresNoteOnFailure === '1' || requiresNoteOnFailure === 'true' || requiresNoteOnFailure === 'True';
            const safeUserId = Number(userId);

            if (!Number.isFinite(safeTaskId) || safeTaskId <= 0) {
                subscriber.error(new Error('A valid PM template task id is required.'));
                return;
            }
            if (!Number.isFinite(safeSequenceNumber) || safeSequenceNumber < 1) {
                subscriber.error(new Error('A valid sequence number is required.'));
                return;
            }
            if (!safeTaskName.length) {
                subscriber.error(new Error('A task name is required.'));
                return;
            }
            if (!Number.isFinite(safeValidationTypeId) || safeValidationTypeId <= 0) {
                subscriber.error(new Error('A valid validation type id is required.'));
                return;
            }
            if (!Number.isFinite(safeUserId) || safeUserId <= 0) {
                subscriber.error(new Error('A valid current user id is required.'));
                return;
            }

            const statement =
                `API.PMUpdateTemplateTask ` +
                `@PMTemplateTaskId = ${safeTaskId}, ` +
                `@SequenceNumber = ${safeSequenceNumber}, ` +
                `@TaskName = N'${safeTaskName.replace(/'/g, "''")}', ` +
                `@TaskDescription = ${safeTaskDescription.length > 0 ? `N'${safeTaskDescription.replace(/'/g, "''")}'` : 'NULL'}, ` +
                `@PMValidationTypeId = ${safeValidationTypeId}, ` +
                `@PMValidationOperatorId = ${Number.isFinite(safeValidationOperatorId) && safeValidationOperatorId > 0 ? safeValidationOperatorId : 'NULL'}, ` +
                `@JBTStandardObservationId = ${Number.isFinite(safeObservationId) && safeObservationId > 0 ? safeObservationId : 'NULL'}, ` +
                `@ExpectedValue = ${safeExpectedValue.length > 0 ? `N'${safeExpectedValue.replace(/'/g, "''")}'` : 'NULL'}, ` +
                `@ExpectedValueMin = ${safeExpectedValueMin.length > 0 ? `N'${safeExpectedValueMin.replace(/'/g, "''")}'` : 'NULL'}, ` +
                `@ExpectedValueMax = ${safeExpectedValueMax.length > 0 ? `N'${safeExpectedValueMax.replace(/'/g, "''")}'` : 'NULL'}, ` +
                `@TelemetryFreshnessMinutes = ${Number.isFinite(safeTelemetryFreshnessMinutes) && safeTelemetryFreshnessMinutes > 0 ? safeTelemetryFreshnessMinutes : 'NULL'}, ` +
                `@IsRequired = ${safeIsRequired ? 1 : 0}, ` +
                `@RequiresNoteOnFailure = ${safeRequiresNotes ? 1 : 0}, ` +
                `@UserId = ${safeUserId}`;
            console.log('[data.service] UpdatePMTemplateTask call:', statement);

            this.SQLActionAsPromise(statement)
                .then((data: any) => {
                    console.log('[data.service] UpdatePMTemplateTask raw response:', Array.isArray(data) ? { rowCount: data.length } : data);
                    subscriber.next(data ?? []);
                    subscriber.complete();
                })
                .catch((err: any) => subscriber.error(err));
        });
        return data$;
    }

    DeactivatePMTemplateTask(pmTemplateTaskId: any, userId: any) {
        var data$: any = new Observable((subscriber) => {
            const safeTaskId = Number(pmTemplateTaskId);
            const safeUserId = Number(userId);
            if (!Number.isFinite(safeTaskId) || safeTaskId <= 0) {
                subscriber.error(new Error('A valid PM template task id is required.'));
                return;
            }
            if (!Number.isFinite(safeUserId) || safeUserId <= 0) {
                subscriber.error(new Error('A valid current user id is required.'));
                return;
            }

            const statement = `API.PMDeactivateTemplateTask @PMTemplateTaskId = ${safeTaskId}, @UserId = ${safeUserId}`;
            console.log('[data.service] DeactivatePMTemplateTask call:', statement);
            this.SQLActionAsPromise(statement)
                .then((data: any) => {
                    console.log('[data.service] DeactivatePMTemplateTask raw response:', Array.isArray(data) ? { rowCount: data.length } : data);
                    subscriber.next(data ?? []);
                    subscriber.complete();
                })
                .catch((err: any) => subscriber.error(err));
        });
        return data$;
    }

    ReorderPMTemplateTask(pmTemplateTaskId: any, newSequenceNumber: any, userId: any) {
        var data$: any = new Observable((subscriber) => {
            const safeTaskId = Number(pmTemplateTaskId);
            const safeSequenceNumber = Number(newSequenceNumber);
            const safeUserId = Number(userId);
            if (!Number.isFinite(safeTaskId) || safeTaskId <= 0) {
                subscriber.error(new Error('A valid PM template task id is required.'));
                return;
            }
            if (!Number.isFinite(safeSequenceNumber) || safeSequenceNumber < 1) {
                subscriber.error(new Error('A valid sequence number is required.'));
                return;
            }
            if (!Number.isFinite(safeUserId) || safeUserId <= 0) {
                subscriber.error(new Error('A valid current user id is required.'));
                return;
            }

            const statement = `API.PMReorderTemplateTask @PMTemplateTaskId = ${safeTaskId}, @NewSequenceNumber = ${safeSequenceNumber}, @UserId = ${safeUserId}`;
            console.log('[data.service] ReorderPMTemplateTask call:', statement);
            this.SQLActionAsPromise(statement)
                .then((data: any) => {
                    console.log('[data.service] ReorderPMTemplateTask raw response:', Array.isArray(data) ? { rowCount: data.length } : data);
                    subscriber.next(data ?? []);
                    subscriber.complete();
                })
                .catch((err: any) => subscriber.error(err));
        });
        return data$;
    }

    CreatePMTemplate(templateName: any, templateFrequencyId: any, assetTypeId: any, templateVersion: any, userId: any, organizationId: any) {
        var data$: any = new Observable((subscriber) => {
            const safeTemplateName = String(templateName ?? '').trim();
            const safeFrequencyId = Number(templateFrequencyId);
            const safeAssetTypeId = Number(assetTypeId);
            const safeTemplateVersion = Number(templateVersion);
            const safeUserId = Number(userId);
            const safeOrganizationId = Number(organizationId);

            if (!safeTemplateName.length) {
                subscriber.error(new Error('A template name is required.'));
                return;
            }

            if (!Number.isFinite(safeFrequencyId) || safeFrequencyId <= 0) {
                subscriber.error(new Error('A frequency is required.'));
                return;
            }

            if (!Number.isFinite(safeTemplateVersion) || safeTemplateVersion < 1) {
                subscriber.error(new Error('A valid template version is required.'));
                return;
            }

            if (!Number.isFinite(safeUserId) || safeUserId <= 0) {
                subscriber.error(new Error('A valid current user id is required.'));
                return;
            }

            if (!Number.isFinite(safeOrganizationId) || safeOrganizationId <= 0) {
                subscriber.error(new Error('A valid organization id is required.'));
                return;
            }

            const escapedTemplateName = safeTemplateName.replace(/'/g, "''");
            const statement =
                `API.PMCreateTemplate ` +
                `@TemplateName = N'${escapedTemplateName}', ` +
                `@TemplateFrequencyId = ${safeFrequencyId}, ` +
                `@AssetTypeId = ${Number.isFinite(safeAssetTypeId) && safeAssetTypeId > 0 ? safeAssetTypeId : 'NULL'}, ` +
                `@TemplateVersion = ${Math.floor(safeTemplateVersion)}, ` +
                `@UserId = ${safeUserId}, ` +
                `@OrganizationId = ${safeOrganizationId}`;
            console.log('[data.service] CreatePMTemplate call:', statement);

            this.SQLActionAsPromise(statement)
                .then((data: any) => {
                    console.log('[data.service] CreatePMTemplate raw response:', Array.isArray(data) ? { rowCount: data.length } : data);
                    subscriber.next(data ?? []);
                    subscriber.complete();
                })
                .catch((err: any) => subscriber.error(err));
        });
        return data$;
    }

    SubmitPMExecution(pmExecutionId: any, userId?: any) {
        var data$: any = new Observable((subscriber) => {
            const safeExecutionId = Number(pmExecutionId);
            const safeUserId = Number(userId);
            if (!Number.isFinite(safeExecutionId) || safeExecutionId <= 0) {
                subscriber.error(new Error('A valid PM execution id is required.'));
                return;
            }
            if (!Number.isFinite(safeUserId) || safeUserId <= 0) {
                subscriber.error(new Error('A valid current user id is required.'));
                return;
            }

            const statement = `API.PMSubmitExecution @PMExecutionId=${safeExecutionId}, @UserId=${safeUserId}`;
            console.log('[data.service] SubmitPMExecution call:', statement);
            this.SQLActionAsPromise(statement)
                .then((data: any) => {
                    console.log('[data.service] SubmitPMExecution raw response:', Array.isArray(data) ? { rowCount: data.length } : data);
                    subscriber.next(data ?? []);
                    subscriber.complete();
                })
                .catch((err: any) => subscriber.error(err));
        });
        return data$;
    }

    GetPMActiveExecutions(organizationId: any) {
        var data$: any = new Observable((subscriber) => {
            const safeOrganizationId = Number(organizationId);
            if (!Number.isFinite(safeOrganizationId) || safeOrganizationId <= 0) {
                subscriber.error(new Error('A valid organization id is required.'));
                return;
            }

            const statement = `API.PMGetActiveExecutions @OrganizationId = ${safeOrganizationId}`;
            console.log('[data.service] GetPMActiveExecutions call:', statement);
            this.SQLActionAsPromise(statement)
                .then((data: any) => {
                    console.log('[data.service] GetPMActiveExecutions raw response:', Array.isArray(data) ? { rowCount: data.length } : data);
                    subscriber.next(data ?? []);
                    subscriber.complete();
                })
                .catch((err: any) => subscriber.error(err));
        });
        return data$;
    }

    GetPMRecentResults(organizationId: any) {
        var data$: any = new Observable((subscriber) => {
            const safeOrganizationId = Number(organizationId);
            if (!Number.isFinite(safeOrganizationId) || safeOrganizationId <= 0) {
                subscriber.error(new Error('A valid organization id is required.'));
                return;
            }

            const statement = `API.PMGetRecentResults @OrganizationId = ${safeOrganizationId}`;
            console.log('[data.service] GetPMRecentResults call:', statement);
            this.SQLActionAsPromise(statement)
                .then((data: any) => {
                    console.log('[data.service] GetPMRecentResults raw response:', Array.isArray(data) ? { rowCount: data.length } : data);
                    subscriber.next(data ?? []);
                    subscriber.complete();
                })
                .catch((err: any) => subscriber.error(err));
        });
        return data$;
    }

    DeletePMExecution(pmExecutionId: any) {
        var data$: any = new Observable((subscriber) => {
            const safeExecutionId = Number(pmExecutionId);
            if (!Number.isFinite(safeExecutionId) || safeExecutionId <= 0) {
                subscriber.error(new Error('A valid PM execution id is required.'));
                return;
            }

            const statement = `API.PMDeleteExecution @PMExecutionId=${safeExecutionId}`;
            this.SQLActionAsPromise(statement)
                .then((data: any) => {
                    subscriber.next(data ?? []);
                    subscriber.complete();
                })
                .catch((err: any) => subscriber.error(err));
        });
        return data$;
    }

    SavePMAudit(pmExecutionId: any, auditStatusCode: string | null, auditorName: string, auditComments?: string, taskAcknowledgments?: any[], userId?: any) {
        var data$: any = new Observable((subscriber) => {
            const safeExecutionId = Number(pmExecutionId);
            const safeAuditStatusCode = auditStatusCode == null ? null : String(auditStatusCode ?? '').trim().toUpperCase();
            const safeAuditorName = String(auditorName ?? '').trim();
            const safeAuditComments = String(auditComments ?? '').trim();
            const safeTaskAcknowledgments = Array.isArray(taskAcknowledgments) ? taskAcknowledgments : [];
            const safeUserId = Number(userId);

            if (!Number.isFinite(safeExecutionId) || safeExecutionId <= 0) {
                subscriber.error(new Error('A valid PM execution id is required.'));
                return;
            }

            if (!Number.isFinite(safeUserId) || safeUserId <= 0) {
                subscriber.error(new Error('A valid current user id is required.'));
                return;
            }

            if (!safeAuditorName.length) {
                subscriber.error(new Error('Reviewer name is required.'));
                return;
            }

            if (safeAuditStatusCode !== null && safeAuditStatusCode !== 'ACCEPTED' && safeAuditStatusCode !== 'NEEDS_CORRECTION') {
                subscriber.error(new Error('Audit status must be ACCEPTED or NEEDS_CORRECTION.'));
                return;
            }

            if (safeAuditStatusCode === 'NEEDS_CORRECTION' && !safeAuditComments.length) {
                subscriber.error(new Error('Comments are required for Needs Correction.'));
                return;
            }

            const escapedAuditorName = safeAuditorName.replace(/'/g, "''");
            const escapedAuditComments = safeAuditComments.replace(/'/g, "''");
            const taskAcknowledgmentStatements = safeTaskAcknowledgments
                .map((item: any) => {
                    const taskResultId = Number(item?.pmExecutionTaskResultId ?? 0);
                    if (!Number.isFinite(taskResultId) || taskResultId <= 0) {
                        return '';
                    }

                    const isAcknowledged = item?.isAcknowledged === true ? 1 : 0;
                    const reviewerComments = String(item?.reviewerComments ?? '').trim().replace(/'/g, "''");
                    return `INSERT INTO @TaskAcknowledgments (PMExecutionTaskResultId, IsAcknowledged, ReviewerComments) VALUES (${taskResultId}, ${isAcknowledged}, ${reviewerComments.length > 0 ? `N'${reviewerComments}'` : 'NULL'});`;
                })
                .filter((statement: string) => statement.length > 0)
                .join(' ');

            const statement =
                `DECLARE @TaskAcknowledgments PMAuditTaskAckTVP; ` +
                `${taskAcknowledgmentStatements.length > 0 ? `${taskAcknowledgmentStatements} ` : ''}` +
                `API.PMSaveAudit ` +
                `@PMExecutionId=${safeExecutionId}, ` +
                `@AuditStatusCode=${safeAuditStatusCode == null ? 'NULL' : `'${safeAuditStatusCode}'`}, ` +
                `@AuditorName='${escapedAuditorName}', ` +
                `@AuditComments='${escapedAuditComments}', ` +
                `@TaskAcknowledgments=@TaskAcknowledgments, ` +
                `@UserId=${safeUserId}`;
            console.log('[data.service] SavePMAudit call:', statement);

            this.httpHeaders = new HttpHeaders({
                "Content-Type": "text/plain",
                Authorization: this.currentUserJSONObject?.AuthenticationToken,
            });
            this.httpHeaders.Authorization = this.currentUserJSONObject?.AuthenticationToken;

            const httpOptions = {
                headers: this.httpHeaders,
                observe: "response" as "response",
                responseType: "text" as "text",
            };

            this.http
                .post(this.multiDataAPIUrl, statement, httpOptions)
                .toPromise()
                .then((response: any) => {
                    const data = response?.body;
                    console.log('[data.service] SavePMAudit raw response:', Array.isArray(data) ? { rowCount: data.length } : data);
                    subscriber.next(data ?? []);
                    subscriber.complete();
                })
                .catch((err: any) => subscriber.error(err));
        });
        return data$;
    }

    GetPMAuditDetail(pmExecutionId: any) {
        var data$: any = new Observable((subscriber) => {
            const safeExecutionId = Number(pmExecutionId);
            if (!Number.isFinite(safeExecutionId) || safeExecutionId <= 0) {
                subscriber.error(new Error('A valid PM execution id is required.'));
                return;
            }

            const statement = `API.PMGetAuditDetail @PMExecutionId = ${safeExecutionId}`;
            console.log('[data.service] GetPMAuditDetail call:', statement);
            this.SQLActionAsPromise(statement)
                .then((data: any) => {
                    console.log('[data.service] GetPMAuditDetail raw response:', Array.isArray(data) ? { rowCount: data.length } : data);
                    subscriber.next(data ?? []);
                    subscriber.complete();
                })
                .catch((err: any) => subscriber.error(err));
        });
        return data$;
    }



    GetObservationAggregatedHighChartValuesWithEpochTime(liveTagIds: any, startEpoch: any, endEpoch: any) {
        const ids = Array.isArray(liveTagIds) ? liveTagIds.slice() : [];
        if (!ids.length || startEpoch == null || endEpoch == null) {
            return of([]);
        }
        const duration = endEpoch - startEpoch;
        const bucketMs = 60 * 1000;
        const bucketedEnd = Math.floor(endEpoch / bucketMs) * bucketMs;
        const bucketedStart = Number.isFinite(duration) ? bucketedEnd - duration : startEpoch;
        ids.sort((a, b) => a - b);
    const key = `${ids.join(',')}|${bucketedStart}|${bucketedEnd}`;
        const now = Date.now();
        const cached = this.sparklineCache.get(key);
        if (cached && cached.expires > now) {
            return cached.obs$;
        }
        if (cached) {
            this.sparklineCache.delete(key);
        }

    const statement = `API.GetObservationAggregatedHighChartValuesWithEpochTime @tagIdsAsString = '${ids.join(',')}', @startDateTimeEpoch = '${bucketedStart}', @endDateTimeEpoch = '${bucketedEnd}'`;
        const obs$ = new Observable((subscriber) => {
            this.SQLActionAsPromise(statement)
                .then((data: any) => {
                    subscriber.next(data);
                    subscriber.complete();
                })
                .catch((err: any) => subscriber.error(err));
        }).pipe(
            shareReplay({ bufferSize: 1, refCount: false }),
            catchError((err) => {
                this.sparklineCache.delete(key);
                return throwError(() => err);
            })
        );

        this.sparklineCache.set(key, { expires: now + this.sparklineCacheTtlMs, obs$ });
        if (this.sparklineCache.size > this.sparklineCacheMax) {
            const firstKey = this.sparklineCache.keys().next().value;
            if (firstKey) this.sparklineCache.delete(firstKey);
        }
        return obs$;
    }

    getPbbHistoricalCache(key: string): { loadedAtMs: number; rows: Array<{ TagId?: number; TagValues?: string }> } | undefined {
        const cached = this.pbbHistoricalCache.get(key);
        if (!cached) return undefined;
        return {
            loadedAtMs: cached.loadedAtMs,
            rows: cached.rows.map((row) => ({ ...row })),
        };
    }

    setPbbHistoricalCache(key: string, rows: Array<{ TagId?: number; TagValues?: string }>, loadedAtMs = Date.now()): void {
        this.pbbHistoricalCache.set(key, {
            loadedAtMs,
            rows: rows.map((row) => ({ ...row })),
        });

        if (this.pbbHistoricalCache.size > this.pbbHistoricalCacheMax) {
            const firstKey = this.pbbHistoricalCache.keys().next().value;
            if (firstKey) this.pbbHistoricalCache.delete(firstKey);
        }
    }



    determineInlineHighestAlarmLevelForAsset(asset: IAsset): string {
        const tags: ITag[] = asset.Id != undefined ? this.tagCacheService.getAlarmTagsForAsset(asset.Id)() : [];
        if (!tags || tags.length === 0) {
            return "Normal"; // No tags, return Normal
    } else if (tags.some(tag => tag.IsCritical || tag.Severity === 'Critical')) {
      return "Critical"
    } else if (tags.some(tag => tag.IsAlarm || tag.Severity === 'Alarm')) {
      return "Alarm"
        } else {
      return "Normal"
        }

    }

    private toBool(v: any): boolean | undefined {
        if (v === undefined || v === null) return undefined;
    if (v === true || v === 'true' || v === 1 || v === '1') return true;
    if (v === false || v === 'false' || v === 0 || v === '0') return false;
        return !!v;
    }

    private formatAssetCurrentStatusFromTagRecords(asset: IAsset, patch?: any) {
        if (!asset?.Id) return;

        // 1) Get current tags for asset
        const tagsSig = this.tagCacheService.getTagsForAsset(asset.Id);
        const tags = tagsSig?.();
        if (!tags?.length) return;

        // 2) One-pass StandardId -> latest tag map
        const stdMap = new Map<number, ITag>();
        for (const t of tags) {
            if (t.AssetId !== asset.Id) continue;
            const sid = t.JBTStandardObservationId;
            if (sid == null) continue;
            const prev = stdMap.get(sid);
            const prevMs = prev?.LastObs?.DateInMilliseconds ?? -Infinity;
            const curMs = t.LastObs?.DateInMilliseconds ?? -Infinity;
            if (!prev || curMs >= prevMs) stdMap.set(sid, t);
        }
        const read = (sid: number) => stdMap.get(sid)?.LastObs?.Value;

        // 3) Build InAlarm / InCriticalAlarm from ACTIVE alarms (excluding 12324/12349)
        // Option 1: leverage your service's active alarm index (already excludes 12324/12349)
        const activeAlarmSig = this.tagCacheService.getAlarmTagsForAsset(asset.Id);
        const activeAlarms = activeAlarmSig?.() ?? [];

        let normalAlarmCount = 0;
        let criticalAlarmCount = 0;
        for (const t of activeAlarms) {
            // these are pre-filtered to active; just count types
            if (t.IsCritical) criticalAlarmCount++;
            else if (t.IsAlarm) normalAlarmCount++;
        }
        const inAlarm = normalAlarmCount > 0;
        const inCriticalAlarm = criticalAlarmCount > 0;

        // (If you’d rather not use the index, fallback scan:
        // const isActive = (t: ITag) =>
        //   (t.IsAlarm || t.IsCritical) &&
        //   t.LastObs?.Value === t.ValueWhenActive &&
        //   t.JBTStandardObservationId !== 12324 &&
        //   t.JBTStandardObservationId !== 12349;
        // let normalAlarmCount = 0, criticalAlarmCount = 0;
        // for (const t of tags) { if (t.AssetId!==asset.Id || !isActive(t)) continue;
        //   if (t.IsCritical) criticalAlarmCount++; else if (t.IsAlarm) normalAlarmCount++; }
        // const inAlarm = normalAlarmCount>0, inCriticalAlarm = criticalAlarmCount>0;
        //)

        // 4) Build the rest of CurrentState from stdMap
        const newState: IAssetState = {
            TagNamePrefix: asset.TagNamePrefix,
            Docked: this.toBool(read(12245)),
            MaintenanceMode: this.toBool(read(54283)),
            OutOfService: this.toBool(read(54284)),
            InMotion: this.toBool(read(54271)),
            On: this.toBool(read(12374)),
            AmbientTemp: read(1835),
            DischargeTemp: read(2736),
      InAlarm: inAlarm,           // ← derived from active set
      InCriticalAlarm: inCriticalAlarm,   // ← derived from active set
            Latitude: read(54260),
            Longitude: read(54261),
            BridgeMode: this.toBool(read(15220)),
            NotMeetingPerformance: this.toBool(read(79720)),
            NotMeetingBridgeCoolPerformance: this.toBool(read(79798)),
            NotMeetingHeatPerformance: this.toBool(read(79830)),
            ModeOfOperation: read(15219),
            VoltsInAvg: read(2244),
            VoltsOutAvg: read(1935),
            AmpsInAvg: read(15032),
            AmpsOutAvg: read(1942),
        };

        // 5) Single assignment
        asset.CurrentState = { ...(asset.CurrentState ?? {}), ...newState };
        // if (patch != undefined) {
        //      console.log("patch: %O", patch);
        //      console.log("affected asset's Current State: %O", asset.CurrentState);
        // }
    }


    determineIfSiteHasGateSystemAssets(siteId: number) {
        //determine if site has GateSystem assets
        var sitesObject = this.cache.sitesObject[siteId];
        let gateAssets = sitesObject.Assets.filter((asset: any) => {
      return (asset.AssetType.AssetTypeGroupId == 1);
        });
        if (gateAssets.length > 0) {
            return true;
        } else {
            return false;
        }
    }

    determineIfSiteHasGSEAssets(siteId: number) {
        //determine if site has GSE assets
        var sitesObject = this.cache.sitesObject[siteId];
        let gseAssets = sitesObject.Assets.filter((asset: any) => {
      return (asset.AssetType.AssetTypeGroupId == 2);
        });
        if (gseAssets.length > 0) {
            return true;
        } else {
            return false;
        }
    }

    determineIfOrganizationHasGateSystemAssets() {
    return this.cache.assets.firstOrDefault((asset: IAsset) => { return asset.AssetType?.AssetTypeGroupId == 1 }) != undefined ? true : false;
    }

    determineIfOrganizationHasGSEAssets() {
    return this.cache.assets.firstOrDefault((asset: IAsset) => { return asset.AssetType?.AssetTypeGroupId == 2 }) != undefined ? true : false;
    }

    /**
     * Returns the list of sites that have assets matching the specified Asset Type Group ID.
     * If groupId is 1 (Gate Systems), only sites with at least one Gate System asset are returned.
     * If groupId is 2 (GSE), only sites with at least one GSE asset are returned.
     * If groupId is null, undefined, or not 1/2 (All Assets), all cached organization sites are returned.
     */
    public getSitesForAssetTypeGroup(groupId?: number | null): ISite[] {
      const cachedSites = this.cache?.sites;
      const cachedSiteList: ISite[] = cachedSites
        ? (Array.isArray(cachedSites) ? cachedSites : (cachedSites as any).toArray?.() ?? Array.from(cachedSites))
        : [];
      const allSites: ISite[] =
        cachedSiteList.length > 0
          ? cachedSiteList
          : (Global?.User?.currentUser?.SelectedOrganizationSites ?? []);

      if (!allSites || allSites.length === 0) return [];

      if (groupId !== 1 && groupId !== 2) {
        return allSites;
      }

      const cachedAssets = this.cache?.assets;
      const assets: any[] = cachedAssets
        ? (Array.isArray(cachedAssets) ? cachedAssets : (cachedAssets as any).toArray?.() ?? Array.from(cachedAssets))
        : [];

      const matchingSiteIds = new Set<number>();

      for (const asset of assets) {
        const assetGroupId =
          asset.AssetType?.AssetTypeGroup?.Id ??
          asset.AssetType?.AssetTypeGroupId ??
          asset.AssetTypeGroupId ??
          (asset.AssetTypeId ? this.cache?.assetTypesObject?.[asset.AssetTypeId]?.AssetTypeGroupId : null);

        if (Number(assetGroupId) === groupId) {
          const siteId = asset.SiteId ?? asset.Site?.Id;
          if (siteId != null) {
            matchingSiteIds.add(Number(siteId));
          }
        }
      }

      return allSites.filter((site: ISite) => {
        if (matchingSiteIds.has(Number(site.Id))) {
          return true;
        }
        if (Array.isArray(site.Assets) && site.Assets.length > 0) {
          return site.Assets.some((asset: any) => {
            const assetGroupId =
              asset.AssetType?.AssetTypeGroup?.Id ??
              asset.AssetType?.AssetTypeGroupId ??
              asset.AssetTypeGroupId ??
              (asset.AssetTypeId ? this.cache?.assetTypesObject?.[asset.AssetTypeId]?.AssetTypeGroupId : null);
            return Number(assetGroupId) === groupId;
          });
        }
        const siteFromObject = this.cache?.sitesObject?.[site?.Id];
        if (Array.isArray(siteFromObject?.Assets) && siteFromObject.Assets.length > 0) {
          return siteFromObject.Assets.some((asset: any) => {
            const assetGroupId =
              asset.AssetType?.AssetTypeGroup?.Id ??
              asset.AssetType?.AssetTypeGroupId ??
              asset.AssetTypeGroupId ??
              (asset.AssetTypeId ? this.cache?.assetTypesObject?.[asset.AssetTypeId]?.AssetTypeGroupId : null);
            return Number(assetGroupId) === groupId;
          });
        }
        return false;
      });
    }

    determineIfFlightHasEnabledFlightSchedule(siteId: number) {
        // determine if site has enabled Flight Schedule
        return !!this.cache.sitesObject?.[siteId]?.EnableFlightData;
    }

    public determineIfOrganizationHasCameraFeeds(): boolean {
    return this.cache.cameras.firstOrDefault((camera: ICamera) => { return camera }) != undefined ? true : false; //--Cameras have already been filtered for the selected organization. --Kirk T. Sherer, September 26, 2025.
    }

  updateUseProductionDataOnTest(
    useProductionDataOnTest: boolean,
    updateFromSettings?: boolean
  ) {
        if (updateFromSettings) {
      this.SQLActionAsPromise(
        "API.UserSettings_UpdateRecordByIdAndFieldName @iOPSUserId=" +
        Global.User.currentUser.Id +
        ", @FieldName='UseProductionDataOnTest', @FieldValue='" +
        (useProductionDataOnTest == true ? 1 : 0) +
        "'"
      ).then((data: any) => {
        Global.User.DebugMode &&
          console.log(
            "UseProductionDataOnTest updated in user settings. user record: %O",
            data
          );
            });
        }
    }

    async showRawData(rawData: any[], gridConfig: any, title: string) {
        const { KendoGridModalComponent } = await import('../components/kendo-grid-modal/kendo-grid-modal.component');
        const modal = await this.modalCtrl.create({
            component: KendoGridModalComponent,
            componentProps: {
                gridConfig: gridConfig,
                data: rawData,
        title: title
            },
            breakpoints: [0, 0.5, 1],
            initialBreakpoint: 1,
      cssClass: 'fullscreen-modal' // 👈 apply the custom class

        });

        await modal.present();

        const { data: result, role } = await modal.onDidDismiss();
    if (role === 'select' && result?.selection) {
            // handle selected row from the grid
            // e.g., this.navigateToAsset(result.selection.dataItem.AssetId);
        }
    }

    /**
     * Safe stringify helper to avoid crashing on circular structures.
     */
    private safeStringify(obj: any): string {
        try {
            return JSON.stringify(obj);
        } catch {
      return '[unserializable error object]';
        }
    }

}
